import React, { useEffect, useMemo, useState } from "react";
import QRCode from "react-qr-code";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool
} from "amazon-cognito-identity-js";
import { stage2ReviewStatus, stage2Translations } from "./i18n/stage2";

const logoSrc = `${process.env.PUBLIC_URL}/banxito-logo.png`;

const config = {
  apiBaseUrl:
    process.env.REACT_APP_BANXITO_API_BASE_URL ??
    "https://02z5eytx7i.execute-api.us-east-1.amazonaws.com",
  userPoolId: process.env.REACT_APP_BANXITO_USER_POOL_ID ?? "us-east-1_rDaPhaYTl",
  userPoolClientId:
    process.env.REACT_APP_BANXITO_USER_POOL_CLIENT_ID ?? "4qp34crrai3hjo3ureo4ibb03d",
  googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? ""
};

const storageKeys = {
  idToken: "banxitoRecipient.idToken",
  email: "banxitoRecipient.email",
  fullName: "banxitoRecipient.fullName",
  pendingBindCode: "banxitoRecipient.pendingBindCode",
  pendingSignupProfile: "banxitoRecipient.pendingSignupProfile",
  activeQrSession: "banxitoRecipient.activeQrSession",
  language: "banxitoRecipient.language"
};

type AppLanguage = "en" | "es" | "fr" | "fil" | "ar" | "hi" | "vi" | "pa" | "sw";

const languageOptions: { value: AppLanguage; label: string }[] = [
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
  { value: "fr", label: "Français" },
  { value: "fil", label: "Filipino" },
  { value: "ar", label: "العربية" },
  { value: "hi", label: "हिन्दी" },
  { value: "vi", label: "Tiếng Việt" },
  { value: "pa", label: "ਪੰਜਾਬੀ" },
  { value: "sw", label: "Kiswahili" }
];

const rtlLanguages = new Set<AppLanguage>(["ar"]);

const languageDisplayLabels: Record<AppLanguage, string> = {
  en: "English",
  es: "Español",
  fr: "Français",
  fil: "Filipino",
  ar: "العربية",
  hi: "हिन्दी",
  vi: "Tiếng Việt",
  pa: "ਪੰਜਾਬੀ",
  sw: "Kiswahili"
};

const reportedI18nFallbacks = new Set<string>();

function translateRecipientString(language: AppLanguage, text: string) {
  const translated = recipientTranslations[language]?.[text];
  const debugI18n =
    typeof window !== "undefined" &&
    window.localStorage.getItem("banxitoRecipient.debugI18n") === "true";

  if (
    language !== "en" &&
    (!translated || translated === text) &&
    debugI18n
  ) {
    const reportKey = `${language}:${text}`;
    if (!reportedI18nFallbacks.has(reportKey)) {
      reportedI18nFallbacks.add(reportKey);
      console.warn(`[Recipient i18n] ${language} fallback: ${text}`);
    }
  }
  return translated ?? text;
}

const recipientTranslations: Record<AppLanguage, Record<string, string>> = {
  en: {},
  es: {
    "Sign out": "Cerrar sesión",
    "Language": "Idioma",
    "Banxito Recipient": "Banxito Familiar",
    "Restoring your Banxito access": "Restaurando tu acceso a Banxito",
    "Checking your saved session and loading your sponsored account.": "Estamos revisando tu sesión guardada y cargando tu cuenta patrocinada.",
    "Recipient home": "Inicio",
    "Your sponsored support is ready.": "Tu apoyo patrocinado está listo.",
    "View the service allowances your sponsor purchased for you, see which care categories are available, and follow recent care activity in one place.": "Ve lo que tu familia financió para ti, qué categorías están disponibles y la actividad reciente en un solo lugar.",
    "Available now": "Disponible ahora",
    "Allowances": "Saldos",
    "Recent activity": "Actividad reciente",
    "Status": "Estado",
    "See allowances": "Ver saldos",
    "View activity": "Ver actividad",
    "Recipient account": "Cuenta del familiar",
    "Email": "Correo",
    "Connected": "Conectado",
    "Not provided": "No proporcionado",
    "Awaiting setup": "Esperando configuración",
    "Visit QR": "QR de visita",
    "Use a Banxito QR at the clinic": "Usa un QR de Banxito en la clínica",
    "Choose an available service category and show the QR or manual code so the provider can submit a service claim.": "Elige una categoría con saldo y muestra el QR o código manual para que el proveedor solicite pago.",
    "Open QR": "Abrir QR",
    "Available": "Disponible",
    "Preparing QR...": "Preparando QR...",
    "Generate QR and code": "Generar QR y código",
    "No service allowance available for QR": "No hay saldo disponible para QR",
    "Once your sponsor purchases a category allowance, you can generate a QR or manual code for approved providers.": "Cuando tu patrocinador financie una categoría, podrás generar un QR o código manual para proveedores aprobados.",
    "Quick view": "Vista rápida",
    "Category allowances": "Saldos por categoría",
    "Refreshing allowances...": "Actualizando saldos...",
    "What you can use right now.": "Lo que puedes usar ahora.",
    "No category allowances available": "Aún no hay saldos financiados",
    "Your sponsor has not purchased a category allowance yet, or the allowance has not been assigned.": "Tu patrocinador aún no ha financiado una categoría o el dinero no se ha asignado.",
    "Show your Banxito QR or code": "Muestra tu QR o código Banxito",
    "Choose an available category. The provider scans the QR or enters the code, then Banxito approves the exact service and fee.": "Elige una categoría con saldo. El proveedor escanea el QR o ingresa el código, y Banxito aprueba el servicio y monto exactos.",
    "Choose a category to generate a QR": "Elige una categoría para generar un QR",
    "The QR does not issue payment by itself. It lets the provider submit a service claim for the selected Banxito category.": "El QR no paga por sí solo. Permite que el proveedor solicite pago desde la categoría seleccionada.",
    "How it works at the front desk": "Cómo funciona en recepción",
    "Ready": "Listo",
    "Manual fallback code": "Código manual alternativo",
    "What to do at the clinic": "Qué hacer en la clínica",
    "Refresh QR": "Actualizar QR",
    "My credits": "Mis saldos",
    "Your category service allowances": "Tus saldos financiados por categoría",
    "Each allowance is controlled by service category and approved provider rules.": "Cada saldo se controla por categoría de servicio y reglas de proveedores aprobados.",
    "No service allowances yet": "Aún no hay saldos financiados",
    "Once your sponsor purchases and assigns support, your category allowances will appear here.": "Cuando tu patrocinador financie y asigne apoyo, tus saldos aparecerán aquí.",
    "Activity": "Actividad",
    "Care and service history": "Historial de atención y servicios",
    "Recent provider activity and completed service events.": "Actividad reciente de proveedores y servicios completados.",
    "No service activity yet": "Aún no hay actividad de servicios",
    "When a provider completes care or submits service proof, it will appear here.": "Cuando un proveedor complete atención o suba comprobante, aparecerá aquí.",
    "Help": "Ayuda",
    "How to use Banxito": "Cómo usar Banxito",
    "A simple guide for recipients.": "Una guía sencilla para familiares.",
    "Me": "Yo",
    "Recipient profile": "Perfil del familiar",
    "Basic account details for this Banxito recipient account.": "Datos básicos de esta cuenta Banxito.",
    "Connect your account": "Conecta tu cuenta",
    "Enter the setup code shared by your sponsor. Banxito will connect you to the right sponsored service access.": "Ingresa el código que compartió tu patrocinador. Banxito te conectará con la cuenta correcta.",
    "Setup code": "Código de configuración",
    "Connect account": "Conectar cuenta",
    "Home": "Inicio"
    ,
    "Active": "Activa",
    "Approved": "Aprobada",
    "Completed": "Completada",
    "Submitted": "Enviada",
    "Pending": "Pendiente",
    "Requested": "Solicitada",
    "Scanned": "Escaneada",
    "Expired": "Vencida",
    "Replaced": "Reemplazada",
    "Waiting for setup": "Esperando configuración",
    "Mexico": "México",
    "India": "India",
    "Philippines": "Filipinas",
    "Ghana": "Ghana",
    "Kenya": "Kenia",
    "Spanish (Mexico)": "Español (México)",
    "English (Mexico)": "Inglés (México)",
    "English (India)": "Inglés (India)",
    "Hindi (India)": "Hindi (India)",
    "English (Philippines)": "Inglés (Filipinas)",
    "Filipino (Philippines)": "Filipino (Filipinas)",
    "English (Ghana)": "Inglés (Ghana)",
    "English (Kenya)": "Inglés (Kenia)",
    "Swahili (Kenya)": "Suajili (Kenia)",
    "QR is active": "QR activo",
    "available snapshot": "saldo disponible al generar",
    "Available:": "Disponible:",
    "Reserved:": "Reservado:",
    "Status:": "Estado:",
    "No service category available": "No hay categoría financiada disponible",
    "Your sponsor must purchase a category allowance before you can generate a provider QR.": "Tu patrocinador debe financiar una categoría antes de que puedas generar un QR para proveedores.",
    "1. Show the QR or manual code from an available service category.": "1. Muestra el QR o el código manual de una categoría con saldo.",
    "2. The provider enters the service and fee.": "2. El proveedor ingresa el servicio y el costo.",
    "3. Banxito approves or denies before card information is issued.": "3. Banxito aprueba o rechaza antes de emitir la información de pago.",
    "Available snapshot": "Saldo disponible al generar",
    "Expires": "Vence",
    "Recipient": "Familiar",
    "Member ID": "ID de miembro",
    "Category": "Categoría",
    "If the provider is using a desktop computer or the camera has trouble scanning, they can enter this code manually.": "Si el proveedor usa una computadora de escritorio o la cámara no puede escanear, puede ingresar este código manualmente.",
    "Ask the front desk to scan the QR or enter the code. They still need to enter the service and fee, and Banxito must approve it before one-time payment details are issued.": "Pide en recepción que escaneen el QR o ingresen el código. Aún deben ingresar el servicio y el costo, y Banxito debe aprobar antes de emitir la información de pago.",
    "Refreshing...": "Actualizando...",
    "Open receipt": "Abrir recibo",
    "Full name": "Nombre completo",
    "Phone": "Teléfono",
    "Country": "País",
    "Banxito accepts lowercase or uppercase input. Your code will be checked automatically.": "Banxito acepta letras minúsculas o mayúsculas. Tu código se revisará automáticamente."
  },
  fr: {
    "Sign out": "Se déconnecter",
    "Language": "Langue",
    "Banxito Recipient": "Banxito Famille",
    "Recipient home": "Accueil",
    "Your sponsored support is ready.": "Votre soutien financé est prêt.",
    "Available now": "Disponible",
    "Allowances": "Soldes",
    "Recent activity": "Activité récente",
    "Status": "Statut",
    "See allowances": "Voir les soldes",
    "View activity": "Voir l'activité",
    "Recipient account": "Compte bénéficiaire",
    "Email": "E-mail",
    "Connected": "Connecté",
    "Not provided": "Non fourni",
    "Awaiting setup": "Configuration en attente",
    "Visit QR": "QR visite",
    "Open QR": "Ouvrir le QR",
    "Available": "Disponible",
    "Preparing QR...": "Préparation du QR...",
    "Generate QR and code": "Générer QR et code",
    "Quick view": "Vue rapide",
    "Category allowances": "Soldes par catégorie",
    "Refreshing allowances...": "Actualisation des soldes...",
    "Show your Banxito QR or code": "Montrez votre QR ou code Banxito",
    "Ready": "Prêt",
    "Manual fallback code": "Code manuel de secours",
    "What to do at the clinic": "Que faire à la clinique",
    "Refresh QR": "Actualiser le QR",
    "My credits": "Mes crédits",
    "Your category service allowances": "Vos soldes financés par catégorie",
    "Activity": "Activité",
    "Care and service history": "Historique des soins et services",
    "Help": "Aide",
    "How to use Banxito": "Comment utiliser Banxito",
    "Me": "Moi",
    "Recipient profile": "Profil bénéficiaire",
    "Connect your account": "Connecter votre compte",
    "Setup code": "Code de configuration",
    "Connect account": "Connecter le compte",
    "Home": "Accueil"
  },
  fil: {
    "Sign out": "Mag-sign out",
    "Language": "Wika",
    "Banxito Recipient": "Banxito Recipient",
    "Recipient home": "Home",
    "Your sponsored support is ready.": "Handa na ang suportang ipinadala para sa iyo.",
    "Available now": "Available ngayon",
    "Allowances": "Mga wallet",
    "Recent activity": "Bagong activity",
    "Status": "Status",
    "See allowances": "Tingnan ang balances",
    "View activity": "Tingnan ang activity",
    "Recipient account": "Recipient account",
    "Email": "Email",
    "Connected": "Connected",
    "Not provided": "Hindi nilagay",
    "Awaiting setup": "Naghihintay ng setup",
    "Visit QR": "Visit QR",
    "Open QR": "Buksan ang QR",
    "Available": "Available",
    "Preparing QR...": "Inihahanda ang QR...",
    "Generate QR and code": "Gumawa ng QR at code",
    "Quick view": "Quick view",
    "Category allowances": "Category allowances",
    "Show your Banxito QR or code": "Ipakita ang Banxito QR o code",
    "Ready": "Ready",
    "Manual fallback code": "Manual backup code",
    "My credits": "Aking credits",
    "Activity": "Activity",
    "Care and service history": "Kasaysayan ng care at serbisyo",
    "Help": "Tulong",
    "Me": "Ako",
    "Connect your account": "I-connect ang account",
    "Setup code": "Setup code",
    "Connect account": "I-connect ang account",
    "Home": "Home"
  },
  ar: {
    "Sign out": "تسجيل الخروج",
    "Language": "اللغة",
    "Banxito Recipient": "بانكسيتو للمستفيد",
    "Recipient home": "الرئيسية",
    "Your sponsored support is ready.": "الدعم المرسل لك جاهز.",
    "Available now": "المتاح الآن",
    "Allowances": "الأرصدة",
    "Recent activity": "النشاط الأخير",
    "Status": "الحالة",
    "See allowances": "عرض الأرصدة",
    "View activity": "عرض النشاط",
    "Recipient account": "حساب المستفيد",
    "Email": "البريد الإلكتروني",
    "Connected": "متصل",
    "Not provided": "غير متوفر",
    "Awaiting setup": "بانتظار الإعداد",
    "Visit QR": "رمز الزيارة",
    "Open QR": "فتح الرمز",
    "Available": "متاح",
    "Preparing QR...": "جاري تجهيز الرمز...",
    "Generate QR and code": "إنشاء رمز وكود",
    "Quick view": "نظرة سريعة",
    "Category allowances": "أرصدة الفئات",
    "Show your Banxito QR or code": "اعرض رمز أو كود بانكسيتو",
    "Ready": "جاهز",
    "Manual fallback code": "كود يدوي بديل",
    "My credits": "رصيدي",
    "Activity": "النشاط",
    "Care and service history": "سجل الرعاية والخدمات",
    "Help": "المساعدة",
    "Me": "أنا",
    "Connect your account": "اربط حسابك",
    "Setup code": "كود الإعداد",
    "Connect account": "ربط الحساب",
    "Home": "الرئيسية"
  },
  hi: {
    "Sign out": "साइन आउट",
    "Language": "भाषा",
    "Banxito Recipient": "Banxito प्राप्तकर्ता",
    "Recipient home": "होम",
    "Your sponsored support is ready.": "आपका प्रायोजित सहयोग तैयार है।",
    "Available now": "अभी उपलब्ध",
    "Allowances": "वॉलेट",
    "Recent activity": "हाल की गतिविधि",
    "Status": "स्थिति",
    "See allowances": "बैलेंस देखें",
    "View activity": "गतिविधि देखें",
    "Recipient account": "प्राप्तकर्ता खाता",
    "Email": "ईमेल",
    "Connected": "जुड़ा हुआ",
    "Not provided": "नहीं दिया गया",
    "Awaiting setup": "सेटअप की प्रतीक्षा",
    "Visit QR": "विजिट QR",
    "Open QR": "QR खोलें",
    "Available": "उपलब्ध",
    "Preparing QR...": "QR तैयार हो रहा है...",
    "Generate QR and code": "QR और कोड बनाएं",
    "Quick view": "त्वरित दृश्य",
    "Category allowances": "श्रेणी बैलेंस",
    "Show your Banxito QR or code": "अपना Banxito QR या कोड दिखाएं",
    "Ready": "तैयार",
    "Manual fallback code": "मैनुअल बैकअप कोड",
    "My credits": "मेरे क्रेडिट",
    "Activity": "गतिविधि",
    "Care and service history": "देखभाल और सेवा इतिहास",
    "Help": "मदद",
    "Me": "मैं",
    "Connect your account": "अपना खाता जोड़ें",
    "Setup code": "सेटअप कोड",
    "Connect account": "खाता जोड़ें",
    "Home": "होम"
  },
  vi: {},
  pa: {},
  sw: {}
};

const recipientSupplementalTranslations: Partial<Record<AppLanguage, Record<string, string>>> = {
  fr: {
    "Restoring your Banxito access": "Restauration de votre accès Banxito",
    "Checking your saved session and loading your sponsored account.": "Vérification de votre session enregistrée et chargement de votre compte financé.",
    "View the service allowances your sponsor purchased for you, see which care categories are available, and follow recent care activity in one place.": "Voyez ce que votre famille a financé, quelles catégories sont disponibles et suivez l'activité de soins au même endroit.",
    "Use a Banxito QR at the clinic": "Utiliser un QR Banxito à la clinique",
    "Choose an available service category and show the QR or manual code so the provider can submit a service claim.": "Choisissez une catégorie financée et montrez le QR ou le code manuel pour que le fournisseur demande le paiement.",
    "No service allowance available for QR": "Aucun solde de catégorie disponible pour le QR",
    "Once your sponsor purchases a category allowance, you can generate a QR or manual code for approved providers.": "Une fois qu'un sponsor finance une catégorie, vous pouvez générer un QR ou un code manuel pour les fournisseurs approuvés.",
    "What you can use right now.": "Ce que vous pouvez utiliser maintenant.",
    "No category allowances available": "Aucun solde financé pour le moment",
    "Your sponsor has not purchased a category allowance yet, or the allowance has not been assigned.": "Votre sponsor n'a pas encore financé de portefeuille de catégorie, ou le financement n'a pas été alloué.",
    "Choose an available category. The provider scans the QR or enters the code, then Banxito approves the exact service and fee.": "Choisissez une catégorie financée. Le fournisseur scanne le QR ou saisit le code, puis Banxito approuve le service et le montant exacts.",
    "Choose a category to generate a QR": "Choisir une catégorie pour générer un QR",
    "The QR does not issue payment by itself. It lets the provider submit a service claim for the selected Banxito category.": "Le QR ne paie pas directement le fournisseur. Il permet au fournisseur de demander un paiement depuis la catégorie Banxito choisie.",
    "How it works at the front desk": "Comment ça marche à l'accueil",
    "1. Show the QR or manual code from an available service category.": "1. Montrez le QR ou le code manuel d'une catégorie financée.",
    "2. The provider enters the service and fee.": "2. Le fournisseur saisit le service et le tarif.",
    "3. Banxito approves or denies before card information is issued.": "3. Banxito approuve ou refuse avant d'émettre les informations de carte.",
    "No service category available": "Aucune catégorie financée disponible",
    "Your sponsor must purchase a category allowance before you can generate a provider QR.": "Votre sponsor doit financer une catégorie avant que vous puissiez générer un QR fournisseur.",
    "Available snapshot": "Solde disponible au moment du QR",
    "Expires": "Expire",
    "Recipient": "Bénéficiaire",
    "Member ID": "ID membre",
    "Category": "Catégorie",
    "available snapshot": "solde disponible au moment du QR",
    "QR is active": "QR actif",
    "Available:": "Disponible :",
    "Reserved:": "Réservé :",
    "Status:": "Statut :",
    "If the provider is using a desktop computer or the camera has trouble scanning, they can enter this code manually.": "Si le fournisseur utilise un ordinateur ou si la caméra ne scanne pas, il peut saisir ce code manuellement.",
    "Ask the front desk to scan the QR or enter the code. They still need to enter the service and fee, and Banxito must approve it before one-time payment details are issued.": "Demandez à l'accueil de scanner le QR ou de saisir le code. Ils doivent encore saisir le service et le tarif, et Banxito doit approuver avant d'émettre les détails de paiement.",
    "Each allowance is controlled by service category and approved provider rules.": "Chaque solde est contrôlé par la catégorie de service et les règles de fournisseurs approuvés.",
    "No service allowances yet": "Aucun portefeuille financé pour le moment",
    "Once your sponsor purchases and assigns support, your category allowances will appear here.": "Lorsque votre sponsor finance et alloue l'aide, vos soldes par catégorie apparaîtront ici.",
    "Recent provider activity and completed service events.": "Activité récente des fournisseurs et services terminés.",
    "No service activity yet": "Aucune activité de service pour le moment",
    "When a provider completes care or submits service proof, it will appear here.": "Quand un fournisseur termine un service ou envoie une preuve, cela apparaîtra ici.",
    "A simple guide for recipients.": "Un guide simple pour les bénéficiaires.",
    "Basic account details for this Banxito recipient account.": "Détails de base de ce compte bénéficiaire Banxito.",
    "Enter the setup code shared by your sponsor. Banxito will connect you to the right sponsored service access.": "Saisissez le code fourni par votre sponsor. Banxito vous connectera au bon compte financé.",
    "Active": "Actif",
    "Approved": "Approuvé",
    "Completed": "Terminé",
    "Submitted": "Envoyé",
    "Pending": "En attente",
    "Requested": "Demandé",
    "Scanned": "Scanné",
    "Expired": "Expiré",
    "Replaced": "Remplacé",
    "Waiting for setup": "Configuration en attente",
    "Open receipt": "Ouvrir le reçu",
    "Full name": "Nom complet",
    "Phone": "Téléphone",
    "Country": "Pays",
    "Banxito accepts lowercase or uppercase input. Your code will be checked automatically.": "Banxito accepte les minuscules ou majuscules. Votre code sera vérifié automatiquement.",
    "Mexico": "Mexique",
    "India": "Inde",
    "Philippines": "Philippines",
    "Ghana": "Ghana",
    "Kenya": "Kenya",
    "Spanish (Mexico)": "Espagnol (Mexique)",
    "English (Mexico)": "Anglais (Mexique)",
    "English (India)": "Anglais (Inde)",
    "Hindi (India)": "Hindi (Inde)",
    "English (Philippines)": "Anglais (Philippines)",
    "Filipino (Philippines)": "Filipino (Philippines)",
    "English (Ghana)": "Anglais (Ghana)",
    "English (Kenya)": "Anglais (Kenya)",
    "Swahili (Kenya)": "Swahili (Kenya)"
  },
  fil: {
    "Restoring your Banxito access": "Ibinabalik ang iyong Banxito access",
    "Checking your saved session and loading your sponsored account.": "Tinitingnan ang saved session at nilo-load ang sponsored account mo.",
    "View the service allowances your sponsor purchased for you, see which care categories are available, and follow recent care activity in one place.": "Tingnan kung ano ang pinondohan ng pamilya mo, aling categories ang available, at ang recent care activity sa isang lugar.",
    "Use a Banxito QR at the clinic": "Gamitin ang Banxito QR sa klinika",
    "Choose an available service category and show the QR or manual code so the provider can submit a service claim.": "Pumili ng funded category at ipakita ang QR o manual code para makapag-request ng payment ang provider.",
    "No service allowance available for QR": "Walang category balance para sa QR",
    "Once your sponsor purchases a category allowance, you can generate a QR or manual code for approved providers.": "Kapag pinondohan ng sponsor ang category balance, makakagawa ka ng QR o manual code para sa approved providers.",
    "What you can use right now.": "Ito ang magagamit mo ngayon.",
    "No category allowances available": "Wala pang funded balances",
    "Your sponsor has not purchased a category allowance yet, or the allowance has not been assigned.": "Hindi pa napopondohan ng sponsor ang category wallet, o hindi pa na-aallocate ang funding.",
    "Choose an available category. The provider scans the QR or enters the code, then Banxito approves the exact service and fee.": "Pumili ng funded category. I-scan ng provider ang QR o ilagay ang code, tapos aaprubahan ng Banxito ang eksaktong serbisyo at fee.",
    "Choose a category to generate a QR": "Pumili ng category para gumawa ng QR",
    "The QR does not issue payment by itself. It lets the provider submit a service claim for the selected Banxito category.": "Hindi direktang nagbabayad ang QR. Pinapayagan nito ang provider na humingi ng payment mula sa napiling Banxito category.",
    "How it works at the front desk": "Paano ito gumagana sa front desk",
    "1. Show the QR or manual code from an available service category.": "1. Ipakita ang QR o manual code mula sa funded category.",
    "2. The provider enters the service and fee.": "2. Ilalagay ng provider ang serbisyo at fee.",
    "3. Banxito approves or denies before card information is issued.": "3. Aaprubahan o ide-deny ng Banxito bago ibigay ang card information.",
    "No service category available": "Walang funded category na available",
    "Your sponsor must purchase a category allowance before you can generate a provider QR.": "Kailangang pondohan muna ng sponsor ang category wallet bago ka makagawa ng provider QR.",
    "Available snapshot": "Available balance snapshot",
    "Expires": "Mag-e-expire",
    "Recipient": "Recipient",
    "Member ID": "Member ID",
    "Category": "Category",
    "available snapshot": "available balance snapshot",
    "QR is active": "active ang QR",
    "Available:": "Available:",
    "Reserved:": "Reserved:",
    "Status:": "Status:",
    "If the provider is using a desktop computer or the camera has trouble scanning, they can enter this code manually.": "Kung desktop ang gamit ng provider o hindi ma-scan ng camera, puwede nilang ilagay ang code na ito manually.",
    "Ask the front desk to scan the QR or enter the code. They still need to enter the service and fee, and Banxito must approve it before one-time payment details are issued.": "Sabihin sa front desk na i-scan ang QR o ilagay ang code. Kailangan pa rin nilang ilagay ang serbisyo at fee, at kailangang ma-approve ng Banxito bago lumabas ang payment details.",
    "Each allowance is controlled by service category and approved provider rules.": "Bawat balance ay kontrolado ng service category at approved provider rules.",
    "No service allowances yet": "Wala pang funded wallets",
    "Once your sponsor purchases and assigns support, your category allowances will appear here.": "Kapag pinondohan at na-allocate na ng sponsor ang suporta, lalabas dito ang category balances mo.",
    "Recent provider activity and completed service events.": "Recent provider activity at completed service events.",
    "No service activity yet": "Wala pang service activity",
    "When a provider completes care or submits service proof, it will appear here.": "Kapag natapos ng provider ang care o nagsumite ng proof, lalabas iyon dito.",
    "A simple guide for recipients.": "Simpleng guide para sa recipients.",
    "Basic account details for this Banxito recipient account.": "Basic account details para sa Banxito recipient account na ito.",
    "Active": "Active",
    "Approved": "Approved",
    "Completed": "Completed",
    "Submitted": "Submitted",
    "Pending": "Pending",
    "Requested": "Requested",
    "Scanned": "Scanned",
    "Expired": "Expired",
    "Replaced": "Replaced",
    "Waiting for setup": "Naghihintay ng setup",
    "Open receipt": "Buksan ang receipt",
    "Full name": "Buong pangalan",
    "Phone": "Telepono",
    "Country": "Bansa",
    "Mexico": "Mexico",
    "India": "India",
    "Philippines": "Philippines",
    "Ghana": "Ghana",
    "Kenya": "Kenya",
    "Spanish (Mexico)": "Spanish (Mexico)",
    "English (Mexico)": "English (Mexico)",
    "English (India)": "English (India)",
    "Hindi (India)": "Hindi (India)",
    "English (Philippines)": "English (Philippines)",
    "Filipino (Philippines)": "Filipino (Philippines)"
  },
  ar: {
    "Restoring your Banxito access": "جارٍ استعادة وصولك إلى Banxito",
    "Checking your saved session and loading your sponsored account.": "جارٍ التحقق من جلستك المحفوظة وتحميل حسابك المدعوم.",
    "View the service allowances your sponsor purchased for you, see which care categories are available, and follow recent care activity in one place.": "اعرض ما مولته عائلتك لك، والفئات المتاحة، ونشاط الرعاية الأخير في مكان واحد.",
    "Use a Banxito QR at the clinic": "استخدم QR من Banxito في العيادة",
    "Choose an available service category and show the QR or manual code so the provider can submit a service claim.": "اختر فئة ممولة واعرض QR أو الرمز اليدوي ليطلب المزود الدفع.",
    "No service allowance available for QR": "لا يوجد رصيد فئة متاح للرمز",
    "Once your sponsor purchases a category allowance, you can generate a QR or manual code for approved providers.": "عندما يمول الراعي رصيد فئة، يمكنك إنشاء QR أو رمز يدوي للمزودين المعتمدين.",
    "What you can use right now.": "ما يمكنك استخدامه الآن.",
    "No category allowances available": "لا توجد أرصدة ممولة بعد",
    "Your sponsor has not purchased a category allowance yet, or the allowance has not been assigned.": "لم يمول الراعي محفظة فئة بعد، أو لم يتم تخصيص التمويل.",
    "Choose an available category. The provider scans the QR or enters the code, then Banxito approves the exact service and fee.": "اختر فئة ممولة. يمسح المزود QR أو يدخل الرمز، ثم توافق Banxito على الخدمة والرسوم المحددة.",
    "Choose a category to generate a QR": "اختر فئة لإنشاء QR",
    "The QR does not issue payment by itself. It lets the provider submit a service claim for the selected Banxito category.": "لا يدفع QR للمزود مباشرة. بل يسمح للمزود بطلب الدفع من فئة Banxito المحددة.",
    "How it works at the front desk": "كيف يعمل في مكتب الاستقبال",
    "1. Show the QR or manual code from an available service category.": "1. اعرض QR أو الرمز اليدوي من فئة ممولة.",
    "2. The provider enters the service and fee.": "2. يدخل المزود الخدمة والرسوم.",
    "3. Banxito approves or denies before card information is issued.": "3. توافق Banxito أو ترفض قبل إصدار معلومات البطاقة.",
    "No service category available": "لا توجد فئة ممولة متاحة",
    "Your sponsor must purchase a category allowance before you can generate a provider QR.": "يجب أن يمول الراعي محفظة فئة قبل إنشاء QR للمزود.",
    "Available snapshot": "لقطة الرصيد المتاح",
    "Expires": "ينتهي",
    "Recipient": "المستفيد",
    "Member ID": "رقم العضو",
    "Category": "الفئة",
    "available snapshot": "لقطة الرصيد المتاح",
    "QR is active": "QR نشط",
    "Available:": "متاح:",
    "Reserved:": "محجوز:",
    "Status:": "الحالة:",
    "If the provider is using a desktop computer or the camera has trouble scanning, they can enter this code manually.": "إذا كان المزود يستخدم حاسوباً أو تعذر المسح بالكاميرا، يمكنه إدخال هذا الرمز يدوياً.",
    "Ask the front desk to scan the QR or enter the code. They still need to enter the service and fee, and Banxito must approve it before one-time payment details are issued.": "اطلب من مكتب الاستقبال مسح QR أو إدخال الرمز. يجب عليهم إدخال الخدمة والرسوم، ويجب أن توافق Banxito قبل إصدار تفاصيل الدفع.",
    "Active": "نشط",
    "Approved": "موافق عليه",
    "Completed": "مكتمل",
    "Submitted": "مرسل",
    "Pending": "قيد الانتظار",
    "Requested": "مطلوب",
    "Scanned": "تم المسح",
    "Expired": "منتهي",
    "Replaced": "مستبدل",
    "Waiting for setup": "بانتظار الإعداد",
    "Open receipt": "فتح الإيصال",
    "Full name": "الاسم الكامل",
    "Phone": "الهاتف",
    "Country": "الدولة",
    "Mexico": "المكسيك",
    "India": "الهند",
    "Philippines": "الفلبين",
    "Ghana": "غانا",
    "Kenya": "كينيا"
  },
  hi: {
    "Restoring your Banxito access": "आपकी Banxito पहुंच बहाल हो रही है",
    "Checking your saved session and loading your sponsored account.": "आपका सहेजा गया सत्र जांचा जा रहा है और प्रायोजित खाता लोड हो रहा है।",
    "View the service allowances your sponsor purchased for you, see which care categories are available, and follow recent care activity in one place.": "देखें कि परिवार ने आपके लिए क्या फंड किया, कौन सी श्रेणियां उपलब्ध हैं, और हाल की देखभाल गतिविधि एक जगह देखें।",
    "Use a Banxito QR at the clinic": "क्लिनिक में Banxito QR उपयोग करें",
    "Choose an available service category and show the QR or manual code so the provider can submit a service claim.": "एक funded category चुनें और QR या manual code दिखाएं ताकि provider payment request कर सके।",
    "No service allowance available for QR": "QR के लिए कोई category balance उपलब्ध नहीं",
    "Once your sponsor purchases a category allowance, you can generate a QR or manual code for approved providers.": "जब sponsor category balance fund करेगा, आप approved providers के लिए QR या manual code बना सकेंगे।",
    "What you can use right now.": "जो आप अभी उपयोग कर सकते हैं।",
    "No category allowances available": "अभी कोई funded balance नहीं",
    "Your sponsor has not purchased a category allowance yet, or the allowance has not been assigned.": "आपके sponsor ने अभी category wallet fund नहीं किया है, या funding allocate नहीं हुई है।",
    "Choose an available category. The provider scans the QR or enters the code, then Banxito approves the exact service and fee.": "एक funded category चुनें। Provider QR scan करता है या code दर्ज करता है, फिर Banxito exact service और fee approve करता है।",
    "Choose a category to generate a QR": "QR बनाने के लिए category चुनें",
    "The QR does not issue payment by itself. It lets the provider submit a service claim for the selected Banxito category.": "QR खुद provider को payment नहीं करता। यह provider को selected Banxito category से payment request करने देता है।",
    "How it works at the front desk": "Front desk पर यह कैसे काम करता है",
    "1. Show the QR or manual code from an available service category.": "1. Purchased category का QR या manual code दिखाएं।",
    "2. The provider enters the service and fee.": "2. Provider service और fee दर्ज करता है।",
    "3. Banxito approves or denies before card information is issued.": "3. Card information issue होने से पहले Banxito approve या deny करता है।",
    "No service category available": "कोई funded category उपलब्ध नहीं",
    "Your sponsor must purchase a category allowance before you can generate a provider QR.": "Provider QR बनाने से पहले sponsor को category wallet fund करना होगा।",
    "Available snapshot": "Available balance snapshot",
    "Expires": "समाप्त होता है",
    "Recipient": "प्राप्तकर्ता",
    "Member ID": "Member ID",
    "Category": "श्रेणी",
    "available snapshot": "available balance snapshot",
    "QR is active": "QR सक्रिय है",
    "Available:": "उपलब्ध:",
    "Reserved:": "आरक्षित:",
    "Status:": "स्थिति:",
    "If the provider is using a desktop computer or the camera has trouble scanning, they can enter this code manually.": "अगर provider desktop उपयोग कर रहा है या camera scan नहीं कर पा रहा, वे यह code manually दर्ज कर सकते हैं।",
    "Ask the front desk to scan the QR or enter the code. They still need to enter the service and fee, and Banxito must approve it before one-time payment details are issued.": "Front desk से QR scan करने या code दर्ज करने को कहें। उन्हें service और fee दर्ज करनी होगी, और payment details issue होने से पहले Banxito को approve करना होगा।",
    "Active": "सक्रिय",
    "Approved": "स्वीकृत",
    "Completed": "पूर्ण",
    "Submitted": "भेजा गया",
    "Pending": "लंबित",
    "Requested": "अनुरोधित",
    "Scanned": "स्कैन किया गया",
    "Expired": "समाप्त",
    "Replaced": "बदला गया",
    "Waiting for setup": "सेटअप की प्रतीक्षा",
    "Open receipt": "रसीद खोलें",
    "Full name": "पूरा नाम",
    "Phone": "फ़ोन",
    "Country": "देश",
    "Mexico": "मेक्सिको",
    "India": "भारत",
    "Philippines": "फिलीपींस",
    "Ghana": "घाना",
    "Kenya": "केन्या"
  }
};

for (const [language, translations] of Object.entries(recipientSupplementalTranslations) as [
  AppLanguage,
  Record<string, string>
][]) {
  recipientTranslations[language] = {
    ...recipientTranslations[language],
    ...translations
  };
}

const recipientCleanTranslations: Partial<Record<AppLanguage, Record<string, string>>> = {
  es: {
    "Language": "Idioma",
    "Sign out": "Cerrar sesión",
    "Banxito Recipient": "Banxito Familiar",
    "Recipient home": "Inicio",
    "Your sponsored support is ready.": "Tu apoyo familiar está listo.",
    "View the service allowances your sponsor purchased for you, see which care categories are available, and follow recent care activity in one place.": "Consulta lo que tu familia financió, las categorías disponibles y tu actividad reciente en un solo lugar.",
    "Available now": "Disponible ahora",
    "Allowances": "Saldos",
    "Recent activity": "Actividad reciente",
    "Status": "Estado",
    "Active": "Activo",
    "See allowances": "Ver saldos",
    "View activity": "Ver actividad",
    "Recipient account": "Cuenta familiar",
    "Visit QR": "QR de visita",
    "Show your Banxito QR or code": "Muestra tu QR o código Banxito",
    "Choose an available category. The provider scans the QR or enters the code, then Banxito approves the exact service and fee.": "Elige una categoría con saldo. El proveedor escanea el QR o ingresa el código, y Banxito aprueba el servicio y el monto exactos.",
    "Choose a category to generate a QR": "Elige una categoría para generar un QR",
    "Generate QR and code": "Generar QR y código",
    "Manual fallback code": "Código manual alternativo",
    "What to do at the clinic": "Qué hacer en la clínica",
    "Category allowances": "Saldos por categoría",
    "No service allowances yet": "Aún no hay saldos financiados",
    "Home": "Inicio",
    "Help": "Ayuda",
    "Me": "Yo",
    "Health": "Salud",
    "Healthcare": "Salud",
    "Education": "Educación",
    "Essentials": "Esenciales",
    "Specialized Care": "Atención especializada",
    "Submitted": "Enviado",
    "Pending": "Pendiente",
    "Approved": "Aprobado",
    "Declined": "Rechazado",
    "Expired": "Vencido",
    "Mexico": "México",
    "India": "India",
    "Philippines": "Filipinas",
    "Ghana": "Ghana",
    "Kenya": "Kenia"
  },
  fr: {
    "Language": "Langue",
    "Sign out": "Se déconnecter",
    "Banxito Recipient": "Famille Banxito",
    "Recipient home": "Accueil",
    "Your sponsored support is ready.": "Votre soutien familial est prêt.",
    "View the service allowances your sponsor purchased for you, see which care categories are available, and follow recent care activity in one place.": "Consultez ce que votre famille a financé, les catégories disponibles et l'activité récente au même endroit.",
    "Available now": "Disponible maintenant",
    "Allowances": "Soldes",
    "Recent activity": "Activité récente",
    "Status": "Statut",
    "Active": "Actif",
    "See allowances": "Voir les soldes",
    "View activity": "Voir l'activité",
    "Recipient account": "Compte familial",
    "Visit QR": "QR de visite",
    "Show your Banxito QR or code": "Présentez votre QR ou code Banxito",
    "Choose an available category. The provider scans the QR or enters the code, then Banxito approves the exact service and fee.": "Choisissez une catégorie financée. Le prestataire scanne le QR ou saisit le code, puis Banxito approuve le service et le montant exacts.",
    "Choose a category to generate a QR": "Choisissez une catégorie pour générer un QR",
    "Generate QR and code": "Générer le QR et le code",
    "Manual fallback code": "Code manuel de secours",
    "What to do at the clinic": "Que faire à la clinique",
    "Category allowances": "Soldes par catégorie",
    "No service allowances yet": "Aucun solde financé pour le moment",
    "Home": "Accueil",
    "Help": "Aide",
    "Me": "Moi",
    "Health": "Santé",
    "Healthcare": "Santé",
    "Education": "Éducation",
    "Essentials": "Essentiels",
    "Specialized Care": "Soins spécialisés",
    "Submitted": "Envoyé",
    "Pending": "En attente",
    "Approved": "Approuvé",
    "Declined": "Refusé",
    "Expired": "Expiré",
    "Mexico": "Mexique",
    "India": "Inde",
    "Philippines": "Philippines",
    "Ghana": "Ghana",
    "Kenya": "Kenya"
  },
  fil: {
    "Language": "Wika",
    "Sign out": "Mag-sign out",
    "Banxito Recipient": "Pamilya Banxito",
    "Recipient home": "Home",
    "Your sponsored support is ready.": "Handa na ang suporta ng pamilya mo.",
    "View the service allowances your sponsor purchased for you, see which care categories are available, and follow recent care activity in one place.": "Tingnan ang pinondohan ng pamilya mo, ang available na category, at recent activity sa iisang lugar.",
    "Available now": "Available ngayon",
    "Allowances": "Mga balanse",
    "Recent activity": "Recent activity",
    "Status": "Status",
    "Active": "Active",
    "See allowances": "Tingnan ang balanse",
    "View activity": "Tingnan ang activity",
    "Recipient account": "Family account",
    "Visit QR": "Visit QR",
    "Show your Banxito QR or code": "Ipakita ang Banxito QR o code",
    "Choose an available category. The provider scans the QR or enters the code, then Banxito approves the exact service and fee.": "Pumili ng funded category. I-scan ng provider ang QR o ilagay ang code, tapos aaprubahan ng Banxito ang eksaktong serbisyo at bayad.",
    "Choose a category to generate a QR": "Pumili ng category para gumawa ng QR",
    "Generate QR and code": "Gumawa ng QR at code",
    "Manual fallback code": "Manual backup code",
    "What to do at the clinic": "Gagawin sa clinic",
    "Category allowances": "Mga balanse kada category",
    "No service allowances yet": "Wala pang funded balance",
    "Home": "Home",
    "Help": "Tulong",
    "Me": "Ako",
    "Health": "Kalusugan",
    "Healthcare": "Kalusugan",
    "Education": "Edukasyon",
    "Essentials": "Pangunahing kailangan",
    "Specialized Care": "Specialized care",
    "Submitted": "Naipasa",
    "Pending": "Pending",
    "Approved": "Approved",
    "Declined": "Declined",
    "Expired": "Expired",
    "Mexico": "Mexico",
    "India": "India",
    "Philippines": "Pilipinas",
    "Ghana": "Ghana",
    "Kenya": "Kenya"
  },
  ar: {
    "Language": "اللغة",
    "Sign out": "تسجيل الخروج",
    "Banxito Recipient": "عائلة Banxito",
    "Recipient home": "الرئيسية",
    "Your sponsored support is ready.": "الدعم الممول لك جاهز.",
    "View the service allowances your sponsor purchased for you, see which care categories are available, and follow recent care activity in one place.": "اعرف ما مولته عائلتك، والفئات المتاحة، والنشاط الأخير في مكان واحد.",
    "Available now": "المتاح الآن",
    "Allowances": "الأرصدة",
    "Recent activity": "النشاط الأخير",
    "Status": "الحالة",
    "Active": "نشط",
    "See allowances": "عرض الأرصدة",
    "View activity": "عرض النشاط",
    "Recipient account": "حساب العائلة",
    "Visit QR": "رمز الزيارة",
    "Show your Banxito QR or code": "اعرض رمز Banxito أو الكود",
    "Choose an available category. The provider scans the QR or enters the code, then Banxito approves the exact service and fee.": "اختر فئة ممولة. يمسح مقدم الخدمة الرمز أو يدخل الكود، ثم توافق Banxito على الخدمة والمبلغ.",
    "Choose a category to generate a QR": "اختر فئة لإنشاء رمز",
    "Generate QR and code": "إنشاء الرمز والكود",
    "Manual fallback code": "كود بديل يدوي",
    "What to do at the clinic": "ما يجب فعله في العيادة",
    "Category allowances": "أرصدة الفئات",
    "No service allowances yet": "لا توجد أرصدة ممولة بعد",
    "Home": "الرئيسية",
    "Help": "مساعدة",
    "Me": "أنا",
    "Health": "الصحة",
    "Healthcare": "الصحة",
    "Education": "التعليم",
    "Essentials": "الأساسيات",
    "Specialized Care": "رعاية متخصصة",
    "Submitted": "مُرسل",
    "Pending": "قيد الانتظار",
    "Approved": "موافق عليه",
    "Declined": "مرفوض",
    "Expired": "منتهي",
    "Mexico": "المكسيك",
    "India": "الهند",
    "Philippines": "الفلبين",
    "Ghana": "غانا",
    "Kenya": "كينيا"
  },
  hi: {
    "Language": "भाषा",
    "Sign out": "साइन आउट",
    "Banxito Recipient": "Banxito परिवार",
    "Recipient home": "होम",
    "Your sponsored support is ready.": "आपका पारिवारिक सहयोग तैयार है।",
    "View the service allowances your sponsor purchased for you, see which care categories are available, and follow recent care activity in one place.": "आपके परिवार ने क्या फंड किया, कौन सी श्रेणियां उपलब्ध हैं, और हाल की गतिविधि एक जगह देखें।",
    "Available now": "अभी उपलब्ध",
    "Allowances": "बैलेंस",
    "Recent activity": "हाल की गतिविधि",
    "Status": "स्थिति",
    "Active": "सक्रिय",
    "See allowances": "बैलेंस देखें",
    "View activity": "गतिविधि देखें",
    "Recipient account": "परिवार खाता",
    "Visit QR": "विज़िट QR",
    "Show your Banxito QR or code": "अपना Banxito QR या कोड दिखाएं",
    "Choose an available category. The provider scans the QR or enters the code, then Banxito approves the exact service and fee.": "फंडेड श्रेणी चुनें। प्रदाता QR स्कैन करता है या कोड डालता है, फिर Banxito सेवा और शुल्क को मंज़ूरी देता है।",
    "Choose a category to generate a QR": "QR बनाने के लिए श्रेणी चुनें",
    "Generate QR and code": "QR और कोड बनाएं",
    "Manual fallback code": "मैनुअल बैकअप कोड",
    "What to do at the clinic": "क्लिनिक में क्या करें",
    "Category allowances": "श्रेणी बैलेंस",
    "No service allowances yet": "अभी कोई फंडेड बैलेंस नहीं",
    "Home": "होम",
    "Help": "सहायता",
    "Me": "मैं",
    "Health": "स्वास्थ्य",
    "Healthcare": "स्वास्थ्य",
    "Education": "शिक्षा",
    "Essentials": "ज़रूरी चीज़ें",
    "Specialized Care": "विशेष देखभाल",
    "Submitted": "भेजा गया",
    "Pending": "लंबित",
    "Approved": "मंज़ूर",
    "Declined": "अस्वीकृत",
    "Expired": "समाप्त",
    "Mexico": "मेक्सिको",
    "India": "भारत",
    "Philippines": "फिलीपींस",
    "Ghana": "घाना",
    "Kenya": "केन्या"
  }
};

for (const [language, translations] of Object.entries(recipientCleanTranslations) as [
  AppLanguage,
  Record<string, string>
][]) {
  recipientTranslations[language] = {
    ...recipientTranslations[language],
    ...translations
  };
}

const recipientAdditionalLanguageTranslations: Partial<Record<AppLanguage, Record<string, string>>> = {
  vi: {
    "Sign out": "Đăng xuất",
    "Language": "Ngôn ngữ",
    "Banxito Recipient": "Gia đình Banxito",
    "Restoring your Banxito access": "Đang khôi phục quyền truy cập Banxito",
    "Checking your saved session and loading your sponsored account.": "Đang kiểm tra phiên đã lưu và tải tài khoản được hỗ trợ của bạn.",
    "Recipient home": "Trang chủ người nhận",
    "Your sponsored support is ready.": "Khoản hỗ trợ của bạn đã sẵn sàng.",
    "View the service allowances your sponsor purchased for you, see which care categories are available, and follow recent care activity in one place.": "Xem gia đình đã hỗ trợ gì cho bạn, danh mục nào còn tiền, và theo dõi hoạt động chăm sóc ở một nơi.",
    "Available now": "Hiện có",
    "Allowances": "Ví",
    "Recent activity": "Hoạt động gần đây",
    "Status": "Trạng thái",
    "See allowances": "Xem số dư",
    "View activity": "Xem hoạt động",
    "Recipient account": "Tài khoản người nhận",
    "Email": "Email",
    "Connected": "Đã kết nối",
    "Not provided": "Chưa cung cấp",
    "Awaiting setup": "Đang chờ thiết lập",
    "Visit QR": "QR cho lần khám",
    "Use a Banxito QR at the clinic": "Dùng QR Banxito tại phòng khám",
    "Choose an available service category and show the QR or manual code so the provider can submit a service claim.": "Chọn danh mục còn tiền và đưa QR hoặc mã thủ công để nhà cung cấp yêu cầu thanh toán.",
    "Open QR": "Mở QR",
    "Available": "Có sẵn",
    "Preparing QR...": "Đang chuẩn bị QR...",
    "Generate QR and code": "Tạo QR và mã",
    "No service allowance available for QR": "Không có số dư danh mục để tạo QR",
    "Once your sponsor purchases a category allowance, you can generate a QR or manual code for approved providers.": "Khi người tài trợ nạp tiền cho danh mục, bạn có thể tạo QR hoặc mã thủ công cho nhà cung cấp đã duyệt.",
    "Category allowances": "Số dư theo danh mục",
    "What you can use right now.": "Số tiền bạn có thể dùng ngay.",
    "No service allowances yet": "Chưa có số dư được nạp",
    "Show your Banxito QR or code": "Đưa QR hoặc mã Banxito",
    "Choose an available category. The provider scans the QR or enters the code, then Banxito approves the exact service and fee.": "Chọn danh mục còn tiền. Nhà cung cấp quét QR hoặc nhập mã, rồi Banxito duyệt dịch vụ và phí chính xác.",
    "Choose a category to generate a QR": "Chọn danh mục để tạo QR",
    "The QR does not issue payment by itself. It lets the provider submit a service claim for the selected Banxito category.": "QR không tự thanh toán. Nó cho phép nhà cung cấp yêu cầu thanh toán từ danh mục Banxito đã chọn.",
    "How it works at the front desk": "Cách dùng tại quầy tiếp nhận",
    "The provider confirms the service and amount before requesting payment.": "Nhà cung cấp xác nhận dịch vụ và số tiền trước khi yêu cầu thanh toán.",
    "Manual code": "Mã thủ công",
    "QR expires": "QR hết hạn",
    "Activity history": "Lịch sử hoạt động",
    "Help": "Trợ giúp",
    "Home": "Trang chủ",
    "Me": "Tôi",
    "Health": "Sức khỏe",
    "Healthcare": "Y tế",
    "Education": "Giáo dục",
    "Essentials": "Thiết yếu",
    "Specialized Care": "Chăm sóc đặc biệt",
    "Active": "Đang hoạt động",
    "Pending": "Đang chờ",
    "Approved": "Đã duyệt",
    "Declined": "Bị từ chối",
    "Completed": "Hoàn tất",
    "Mexico": "Mexico",
    "India": "Ấn Độ",
    "Philippines": "Philippines",
    "Ghana": "Ghana",
    "Kenya": "Kenya",
    "Swahili (Kenya)": "Tiếng Swahili (Kenya)"
  },
  pa: {
    "Sign out": "ਸਾਈਨ ਆਉਟ",
    "Language": "ਭਾਸ਼ਾ",
    "Banxito Recipient": "Banxito ਪਰਿਵਾਰ",
    "Restoring your Banxito access": "ਤੁਹਾਡੀ Banxito ਪਹੁੰਚ ਮੁੜ ਖੋਲ੍ਹੀ ਜਾ ਰਹੀ ਹੈ",
    "Checking your saved session and loading your sponsored account.": "ਤੁਹਾਡਾ ਸੇਵ ਸੈਸ਼ਨ ਜਾਂਚਿਆ ਜਾ ਰਿਹਾ ਹੈ ਅਤੇ ਸਹਾਇਤਾ ਖਾਤਾ ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ।",
    "Recipient home": "ਪ੍ਰਾਪਤਕਰਤਾ ਘਰ",
    "Your sponsored support is ready.": "ਤੁਹਾਡੀ ਸਹਾਇਤਾ ਤਿਆਰ ਹੈ।",
    "View the service allowances your sponsor purchased for you, see which care categories are available, and follow recent care activity in one place.": "ਵੇਖੋ ਤੁਹਾਡੇ ਪਰਿਵਾਰ ਨੇ ਕੀ ਫੰਡ ਕੀਤਾ, ਕਿਹੜੀਆਂ ਸ਼੍ਰੇਣੀਆਂ ਉਪਲਬਧ ਹਨ, ਅਤੇ ਦੇਖਭਾਲ ਦੀ ਸਰਗਰਮੀ ਇਕ ਥਾਂ ਵੇਖੋ।",
    "Available now": "ਹੁਣ ਉਪਲਬਧ",
    "Allowances": "ਵਾਲਿਟ",
    "Recent activity": "ਤਾਜ਼ਾ ਸਰਗਰਮੀ",
    "Status": "ਹਾਲਤ",
    "See allowances": "ਬਕਾਇਆ ਵੇਖੋ",
    "View activity": "ਸਰਗਰਮੀ ਵੇਖੋ",
    "Recipient account": "ਪ੍ਰਾਪਤਕਰਤਾ ਖਾਤਾ",
    "Email": "ਈਮੇਲ",
    "Connected": "ਜੁੜਿਆ",
    "Not provided": "ਨਹੀਂ ਦਿੱਤਾ",
    "Awaiting setup": "ਸੈੱਟਅਪ ਦੀ ਉਡੀਕ",
    "Visit QR": "ਵਿਜ਼ਿਟ QR",
    "Use a Banxito QR at the clinic": "ਕਲੀਨਿਕ ਵਿੱਚ Banxito QR ਵਰਤੋ",
    "Choose an available service category and show the QR or manual code so the provider can submit a service claim.": "ਫੰਡ ਕੀਤੀ ਸ਼੍ਰੇਣੀ ਚੁਣੋ ਅਤੇ QR ਜਾਂ ਹੱਥੋਂ ਕੋਡ ਦਿਖਾਓ ਤਾਂ ਜੋ ਪ੍ਰਦਾਤਾ ਭੁਗਤਾਨ ਦੀ ਬੇਨਤੀ ਕਰ ਸਕੇ।",
    "Open QR": "QR ਖੋਲ੍ਹੋ",
    "Available": "ਉਪਲਬਧ",
    "Preparing QR...": "QR ਤਿਆਰ ਹੋ ਰਿਹਾ ਹੈ...",
    "Generate QR and code": "QR ਅਤੇ ਕੋਡ ਬਣਾਓ",
    "No service allowance available for QR": "QR ਲਈ ਕੋਈ ਸ਼੍ਰੇਣੀ ਬਕਾਇਆ ਨਹੀਂ",
    "Category allowances": "ਸ਼੍ਰੇਣੀ ਬਕਾਇਆ",
    "What you can use right now.": "ਜੋ ਤੁਸੀਂ ਹੁਣ ਵਰਤ ਸਕਦੇ ਹੋ।",
    "No service allowances yet": "ਹਾਲੇ ਕੋਈ ਫੰਡ ਬਕਾਇਆ ਨਹੀਂ",
    "Show your Banxito QR or code": "ਆਪਣਾ Banxito QR ਜਾਂ ਕੋਡ ਦਿਖਾਓ",
    "How it works at the front desk": "ਫਰੰਟ ਡੈਸਕ ਤੇ ਇਹ ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ",
    "Manual code": "ਹੱਥੋਂ ਕੋਡ",
    "QR expires": "QR ਦੀ ਮਿਆਦ ਮੁੱਕਦੀ ਹੈ",
    "Activity history": "ਸਰਗਰਮੀ ਇਤਿਹਾਸ",
    "Help": "ਮਦਦ",
    "Home": "ਘਰ",
    "Me": "ਮੈਂ",
    "Health": "ਸਿਹਤ",
    "Healthcare": "ਸਿਹਤ ਸੇਵਾ",
    "Education": "ਸਿੱਖਿਆ",
    "Essentials": "ਜ਼ਰੂਰੀ ਚੀਜ਼ਾਂ",
    "Specialized Care": "ਖਾਸ ਦੇਖਭਾਲ",
    "Active": "ਸਰਗਰਮ",
    "Pending": "ਬਕਾਇਆ",
    "Approved": "ਮਨਜ਼ੂਰ",
    "Declined": "ਰੱਦ",
    "Completed": "ਪੂਰਾ",
    "Mexico": "ਮੈਕਸੀਕੋ",
    "India": "ਭਾਰਤ",
    "Philippines": "ਫਿਲੀਪੀਨਜ਼",
    "Ghana": "ਘਾਨਾ",
    "Kenya": "ਕੀਨੀਆ",
    "Swahili (Kenya)": "ਸਵਾਹਿਲੀ (ਕੀਨੀਆ)"
  },
  sw: {
    "Sign out": "Ondoka",
    "Language": "Lugha",
    "Banxito Recipient": "Familia ya Banxito",
    "Restoring your Banxito access": "Tunarejesha ufikiaji wako wa Banxito",
    "Checking your saved session and loading your sponsored account.": "Tunakagua kipindi chako kilichohifadhiwa na kupakia akaunti yako ya msaada.",
    "Recipient home": "Nyumbani kwa mpokeaji",
    "Your sponsored support is ready.": "Msaada wako uko tayari.",
    "View the service allowances your sponsor purchased for you, see which care categories are available, and follow recent care activity in one place.": "Tazama familia yako ilichofadhili, aina zilizo na fedha, na shughuli za huduma mahali pamoja.",
    "Available now": "Inapatikana sasa",
    "Allowances": "Pochi",
    "Recent activity": "Shughuli za karibuni",
    "Status": "Hali",
    "See allowances": "Tazama salio",
    "View activity": "Tazama shughuli",
    "Recipient account": "Akaunti ya mpokeaji",
    "Email": "Barua pepe",
    "Connected": "Imeunganishwa",
    "Not provided": "Haijatolewa",
    "Awaiting setup": "Inasubiri usanidi",
    "Visit QR": "QR ya ziara",
    "Use a Banxito QR at the clinic": "Tumia QR ya Banxito kliniki",
    "Choose an available service category and show the QR or manual code so the provider can submit a service claim.": "Chagua aina yenye fedha na uonyeshe QR au msimbo ili mtoa huduma aombe malipo.",
    "Open QR": "Fungua QR",
    "Available": "Inapatikana",
    "Preparing QR...": "QR inaandaliwa...",
    "Generate QR and code": "Tengeneza QR na msimbo",
    "No service allowance available for QR": "Hakuna salio la aina kwa QR",
    "Once your sponsor purchases a category allowance, you can generate a QR or manual code for approved providers.": "Mfadhili akiweka fedha kwenye aina, unaweza kutengeneza QR au msimbo kwa watoa huduma walioidhinishwa.",
    "Category allowances": "Salio kwa aina",
    "What you can use right now.": "Unachoweza kutumia sasa.",
    "No service allowances yet": "Bado hakuna salio lililofadhiliwa",
    "Show your Banxito QR or code": "Onyesha QR au msimbo wako wa Banxito",
    "Choose an available category. The provider scans the QR or enters the code, then Banxito approves the exact service and fee.": "Chagua aina yenye fedha. Mtoa huduma huchanganua QR au kuingiza msimbo, kisha Banxito huidhinisha huduma na ada kamili.",
    "Choose a category to generate a QR": "Chagua aina ili kutengeneza QR",
    "The QR does not issue payment by itself. It lets the provider submit a service claim for the selected Banxito category.": "QR hailipi yenyewe. Inaruhusu mtoa huduma kuomba malipo kutoka aina uliyochagua.",
    "How it works at the front desk": "Jinsi inavyofanya kazi mapokezi",
    "The provider confirms the service and amount before requesting payment.": "Mtoa huduma huthibitisha huduma na kiasi kabla ya kuomba malipo.",
    "Manual code": "Msimbo wa kuandika",
    "QR expires": "QR inaisha",
    "Activity history": "Historia ya shughuli",
    "Help": "Msaada",
    "Home": "Mwanzo",
    "Me": "Mimi",
    "Health": "Afya",
    "Healthcare": "Huduma ya afya",
    "Education": "Elimu",
    "Essentials": "Mahitaji muhimu",
    "Specialized Care": "Huduma maalum",
    "Active": "Inatumika",
    "Pending": "Inasubiri",
    "Approved": "Imeidhinishwa",
    "Declined": "Imekataliwa",
    "Completed": "Imekamilika",
    "Mexico": "Meksiko",
    "India": "India",
    "Philippines": "Ufilipino",
    "Ghana": "Ghana",
    "Kenya": "Kenya",
    "Swahili (Kenya)": "Kiswahili (Kenya)"
  }
};

for (const [language, translations] of Object.entries(recipientAdditionalLanguageTranslations) as [
  AppLanguage,
  Record<string, string>
][]) {
  recipientTranslations[language] = {
    ...recipientTranslations[language],
    ...translations
  };
}

for (const [language, translations] of Object.entries(stage2Translations) as [
  AppLanguage,
  Record<string, string>
][]) {
  recipientTranslations[language] = {
    ...recipientTranslations[language],
    ...translations
  };
}

const recipientPolishTranslations: Partial<Record<AppLanguage, Record<string, string>>> = {
  es: {
    "Get your Banxito account ready": "Prepara tu cuenta Banxito",
    "Sign in to view your sponsored service allowances or create your recipient account using the setup code from your sponsor.": "Inicia sesión para ver tus saldos patrocinados o crea tu cuenta de beneficiario con el código de configuración de tu patrocinador.",
    "Sign in": "Iniciar sesión",
    "Create account": "Crear cuenta",
    "Email or mobile phone": "Correo electrónico o teléfono móvil",
    "We’ll text a verification code to this mobile number.": "Enviaremos un código de verificación por mensaje de texto a este número móvil.",
    "We’ll email a verification code to this address.": "Enviaremos un código de verificación a este correo.",
    "Enter a valid mobile number during signup.": "Ingresa un número móvil válido durante el registro.",
    "Enter a valid email address during signup.": "Ingresa un correo electrónico válido durante el registro.",
    "Verification code": "Código de verificación",
    "Open your text messages and enter the code Banxito sent to this mobile number.": "Abre tus mensajes de texto e ingresa el código que Banxito envió a este número móvil.",
    "Open your inbox and enter the code Banxito emailed to you.": "Abre tu correo e ingresa el código que Banxito te envió.",
    "Tap Show if you want to verify what you typed.": "Toca Mostrar si quieres verificar lo que escribiste.",
    "Working...": "Procesando...",
    "Care Passport": "Pasaporte de cuidado",
    "Your Banxito Care Passport is loading.": "Tu Pasaporte de cuidado Banxito se está cargando.",
    "{name}, your Banxito Care Passport is ready.": "{name}, tu Pasaporte de cuidado Banxito está listo.",
    "One simple place to see what care you can use, what to say at the front desk, and how to show your QR or code.": "Un lugar sencillo para ver qué apoyo puedes usar, qué decir en recepción y cómo mostrar tu QR o código.",
    "Show my QR or code": "Mostrar mi QR o código",
    "Open Visit QR": "Abrir QR de visita",
    "Refresh passport": "Actualizar pasaporte",
    "Available care": "Apoyo disponible",
    "What you can use": "Lo que puedes usar",
    "Plain-language service allowances by category. Banxito still checks provider approval and the exact service before fulfillment.": "Saldos claros por categoría. Banxito todavía verifica que el proveedor esté aprobado y el servicio exacto antes del pago.",
    "Building your Care Passport": "Preparando tu Pasaporte de cuidado",
    "Care Passport not loaded yet": "El Pasaporte de cuidado aún no se cargó",
    "Banxito is preparing a simple view of your available care categories.": "Banxito está preparando una vista sencilla de tus categorías con saldo.",
    "At the front desk": "En recepción",
    "What to say": "Qué decir",
    "Use this simple script so the clinic or provider knows exactly what to do.": "Usa esta frase sencilla para que la clínica o el proveedor sepa exactamente qué hacer.",
    "Say this": "Di esto",
    "Tell the front desk: I'm using Banxito. Please scan my QR or enter my code before you charge me.": "Dile a recepción: Estoy usando Banxito. Por favor escanee mi QR o ingrese mi código antes de cobrarme.",
    "QR instructions": "Instrucciones del QR",
    "Open Visit QR, choose an available category, and show the QR or manual code after the provider confirms the service and amount.": "Abre QR de visita, elige una categoría con saldo y muestra el QR o código manual después de que el proveedor confirme el servicio y el monto.",
    "Backup options": "Opciones de respaldo",
    "If scanning does not work": "Si el escaneo no funciona",
    "Banxito always gives the provider a low-tech option.": "Banxito siempre le da al proveedor una opción sencilla.",
    "Ask the provider to type the manual code shown under your QR.": "Pide al proveedor que escriba el código manual que aparece debajo de tu QR.",
    "If the code expired, generate a fresh Visit QR when you are ready.": "Si el código venció, genera un nuevo QR de visita cuando estés listo.",
    "Helpful reminders": "Recordatorios útiles",
    "What needs attention": "Lo que necesita atención",
    "Friendly notes based on your available support.": "Notas útiles según el apoyo que tienes disponible.",
    "AI-assisted guidance for clarity. Allowances and approvals come from Banxito records.": "Guía asistida por IA para mayor claridad. Los saldos y aprobaciones vienen de los registros de Banxito.",
    "No active category allowance yet": "Aún no hay saldo activo por categoría",
    "When your sponsor purchases a care category allowance, it will appear here.": "Cuando tu patrocinador financie una categoría de cuidado, aparecerá aquí.",
    "Health balance is getting low": "El saldo de salud está bajando",
    "Recent care activity": "Actividad reciente de cuidado",
    "{provider} requested {amount} for {category}.": "{provider} solicitó {amount} para {category}.",
    "You have {amount} in {category} service allowance.": "Tienes {amount} para {category}.",
    "Use this with approved Banxito {category} providers.": "Úsalo con proveedores Banxito aprobados para {category}.",
    "home": "hogar",
    "health": "salud",
    "education": "educación",
    "essentials": "artículos esenciales",
    "low": "bajo",
    "medium": "medio",
    "high": "alto",
    "1. Use your setup code": "1. Usa tu código de configuración",
    "Your sponsor shares a setup code with you. Enter it once to connect your Banxito account.": "Tu patrocinador comparte un código de configuración contigo. Ingrésalo una sola vez para conectar tu cuenta Banxito.",
    "2. Check your allowances": "2. Revisa tus saldos",
    "See what service categories are available to you, such as healthcare, school, or essentials.": "Ve qué categorías tienen saldo para ti, como salud, escuela o artículos esenciales.",
    "3. Visit approved providers": "3. Visita proveedores aprobados",
    "Banxito support is meant to be used with approved providers and tracked service delivery.": "El apoyo de Banxito debe usarse con proveedores aprobados y con servicios registrados.",
    "Password": "Contraseña",
    "Confirm password": "Confirmar contraseña",
    "Forgot password?": "¿Olvidaste tu contraseña?",
    "Reset your password": "Restablecer contraseña",
    "Account email or mobile phone": "Correo o teléfono móvil de la cuenta",
    "Banxito will send a password reset code to the email address or mobile number on this account.": "Banxito enviará un código para restablecer la contraseña al correo o teléfono móvil de esta cuenta.",
    "We texted a password reset code to this mobile number.": "Enviamos un código por texto a este número móvil.",
    "We emailed a password reset code to this address.": "Enviamos un código de restablecimiento a este correo.",
    "Use the account contact you entered above.": "Usa el contacto de la cuenta que ingresaste arriba.",
    "Reset code from text message": "Código recibido por mensaje de texto",
    "Reset code from email": "Código recibido por correo",
    "New password": "Nueva contraseña",
    "Confirm new password": "Confirmar nueva contraseña",
    "Update password": "Actualizar contraseña",
    "Updating...": "Actualizando...",
    "Show": "Mostrar",
    "Hide": "Ocultar",
    "At least 8 characters": "Al menos 8 caracteres",
    "1 uppercase letter": "1 letra mayúscula",
    "1 lowercase letter": "1 letra minúscula",
    "1 number": "1 número",
    "Add": "Falta"
  },
  fr: {
    "Care Passport": "Passeport de soins",
    "Your Banxito Care Passport is loading.": "Votre Passeport de soins Banxito se charge.",
    "{name}, your Banxito Care Passport is ready.": "{name}, votre Passeport de soins Banxito est prêt.",
    "One simple place to see what care you can use, what to say at the front desk, and how to show your QR or code.": "Un seul endroit pour voir l'aide disponible, quoi dire à l'accueil et comment montrer votre QR ou code.",
    "Show my QR or code": "Afficher mon QR ou code",
    "Open Visit QR": "Ouvrir le QR de visite",
    "Refresh passport": "Actualiser le passeport",
    "Available care": "Aide disponible",
    "What you can use": "Ce que vous pouvez utiliser",
    "Plain-language service allowances by category. Banxito still checks provider approval and the exact service before fulfillment.": "Soldes simples par catégorie. Banxito vérifie toujours le fournisseur approuvé et le service exact avant le paiement.",
    "Building your Care Passport": "Préparation de votre Passeport de soins",
    "Care Passport not loaded yet": "Passeport de soins pas encore chargé",
    "Banxito is preparing a simple view of your available care categories.": "Banxito prépare une vue simple de vos catégories financées.",
    "At the front desk": "À l'accueil",
    "What to say": "Que dire",
    "Use this simple script so the clinic or provider knows exactly what to do.": "Utilisez cette phrase simple pour que la clinique ou le fournisseur sache quoi faire.",
    "Say this": "Dites ceci",
    "Tell the front desk: I'm using Banxito. Please scan my QR or enter my code before you charge me.": "Dites à l'accueil : J'utilise Banxito. Veuillez scanner mon QR ou saisir mon code avant de me facturer.",
    "QR instructions": "Instructions QR",
    "Open Visit QR, choose an available category, and show the QR or manual code after the provider confirms the service and amount.": "Ouvrez le QR de visite, choisissez une catégorie financée, puis montrez le QR ou le code manuel après confirmation du service et du montant.",
    "Backup options": "Options de secours",
    "If scanning does not work": "Si le scan ne fonctionne pas",
    "Banxito always gives the provider a low-tech option.": "Banxito donne toujours au fournisseur une option simple.",
    "Ask the provider to type the manual code shown under your QR.": "Demandez au fournisseur de saisir le code manuel affiché sous votre QR.",
    "If the code expired, generate a fresh Visit QR when you are ready.": "Si le code a expiré, générez un nouveau QR de visite quand vous êtes prêt.",
    "Helpful reminders": "Rappels utiles",
    "What needs attention": "Ce qui nécessite votre attention",
    "Friendly notes based on your available support.": "Notes utiles selon l'aide disponible.",
    "You have {amount} in {category} service allowance.": "Vous avez {amount} pour {category}.",
    "Use this with approved Banxito {category} providers.": "Utilisez-le avec des fournisseurs Banxito approuvés pour {category}.",
    "1. Use your setup code": "1. Utilisez votre code de configuration",
    "Your sponsor shares a setup code with you. Enter it once to connect your Banxito account.": "Votre sponsor partage un code de configuration avec vous. Saisissez-le une fois pour connecter votre compte Banxito.",
    "2. Check your allowances": "2. Vérifiez vos soldes",
    "See what service categories are available to you, such as healthcare, school, or essentials.": "Voyez quelles catégories sont financées pour vous, comme la santé, l'école ou les essentiels.",
    "3. Visit approved providers": "3. Consultez des fournisseurs approuvés",
    "Banxito support is meant to be used with approved providers and tracked service delivery.": "L'aide Banxito doit être utilisée avec des fournisseurs approuvés et des services suivis.",
    "Password": "Mot de passe",
    "Confirm password": "Confirmer le mot de passe",
    "Forgot password?": "Mot de passe oublié ?",
    "Reset your password": "Réinitialiser votre mot de passe",
    "Account email or mobile phone": "E-mail ou téléphone mobile du compte",
    "Banxito will send a password reset code to the email address or mobile number on this account.": "Banxito enverra un code de réinitialisation à l'e-mail ou au téléphone mobile de ce compte.",
    "We texted a password reset code to this mobile number.": "Nous avons envoyé un code par SMS à ce numéro.",
    "We emailed a password reset code to this address.": "Nous avons envoyé un code de réinitialisation à cette adresse e-mail.",
    "Use the account contact you entered above.": "Utilisez le contact du compte saisi ci-dessus.",
    "Reset code from text message": "Code reçu par SMS",
    "Reset code from email": "Code reçu par e-mail",
    "New password": "Nouveau mot de passe",
    "Confirm new password": "Confirmer le nouveau mot de passe",
    "Update password": "Mettre à jour le mot de passe",
    "Updating...": "Mise à jour...",
    "Show": "Afficher",
    "Hide": "Masquer",
    "At least 8 characters": "Au moins 8 caractères",
    "1 uppercase letter": "1 lettre majuscule",
    "1 lowercase letter": "1 lettre minuscule",
    "1 number": "1 chiffre",
    "Add": "Ajouter"
  },
  fil: {
    "Care Passport": "Care Passport",
    "Your Banxito Care Passport is loading.": "Naglo-load ang iyong Banxito Care Passport.",
    "{name}, your Banxito Care Passport is ready.": "{name}, handa na ang iyong Banxito Care Passport.",
    "One simple place to see what care you can use, what to say at the front desk, and how to show your QR or code.": "Isang simpleng lugar para makita kung anong suporta ang puwede mong gamitin, ano ang sasabihin sa front desk, at paano ipapakita ang QR o code.",
    "Show my QR or code": "Ipakita ang QR o code ko",
    "Open Visit QR": "Buksan ang Visit QR",
    "Refresh passport": "I-refresh ang passport",
    "Available care": "Available na suporta",
    "What you can use": "Ano ang puwede mong gamitin",
    "Plain-language service allowances by category. Banxito still checks provider approval and the exact service before fulfillment.": "Malinaw na balances ayon sa category. Tinitingnan pa rin ng Banxito kung approved ang provider at ang eksaktong serbisyo bago magbayad.",
    "Building your Care Passport": "Inihahanda ang iyong Care Passport",
    "Care Passport not loaded yet": "Hindi pa na-load ang Care Passport",
    "Banxito is preparing a simple view of your available care categories.": "Inihahanda ng Banxito ang simpleng view ng funded care categories mo.",
    "At the front desk": "Sa front desk",
    "What to say": "Ano ang sasabihin",
    "Use this simple script so the clinic or provider knows exactly what to do.": "Gamitin ang simpleng linyang ito para alam ng clinic o provider ang gagawin.",
    "Say this": "Sabihin ito",
    "Tell the front desk: I'm using Banxito. Please scan my QR or enter my code before you charge me.": "Sabihin sa front desk: Gumagamit ako ng Banxito. Pakiscan ang QR ko o ilagay ang code ko bago ako singilin.",
    "QR instructions": "Mga tagubilin sa QR",
    "Open Visit QR, choose an available category, and show the QR or manual code after the provider confirms the service and amount.": "Buksan ang Visit QR, pumili ng funded category, at ipakita ang QR o manual code pagkatapos kumpirmahin ng provider ang serbisyo at halaga.",
    "Backup options": "Backup options",
    "If scanning does not work": "Kung hindi gumana ang scan",
    "Banxito always gives the provider a low-tech option.": "Laging may simpleng backup option ang Banxito para sa provider.",
    "Ask the provider to type the manual code shown under your QR.": "Pakiusap sa provider na i-type ang manual code na nasa ilalim ng QR mo.",
    "If the code expired, generate a fresh Visit QR when you are ready.": "Kung expired ang code, gumawa ng bagong Visit QR kapag handa ka na.",
    "Helpful reminders": "Mga paalala",
    "What needs attention": "Mga kailangang pansinin",
    "Friendly notes based on your available support.": "Mga paalala batay sa available mong suporta.",
    "You have {amount} in {category} service allowance.": "Mayroon kang {amount} para sa {category}.",
    "Use this with approved Banxito {category} providers.": "Gamitin ito sa approved Banxito providers para sa {category}.",
    "1. Use your setup code": "1. Gamitin ang setup code mo",
    "Your sponsor shares a setup code with you. Enter it once to connect your Banxito account.": "Ibabahagi sa iyo ng sponsor mo ang setup code. Ilagay ito isang beses para ma-connect ang Banxito account mo.",
    "2. Check your allowances": "2. Tingnan ang balances mo",
    "See what service categories are available to you, such as healthcare, school, or essentials.": "Tingnan kung anong categories ang funded para sa iyo, gaya ng healthcare, school, o essentials.",
    "3. Visit approved providers": "3. Pumunta sa approved providers",
    "Banxito support is meant to be used with approved providers and tracked service delivery.": "Ang Banxito support ay para gamitin sa approved providers at may record ng serbisyong naibigay.",
    "Password": "Password",
    "Confirm password": "Kumpirmahin ang password",
    "Forgot password?": "Nakalimutan ang password?",
    "Reset your password": "I-reset ang password mo",
    "Account email or mobile phone": "Email o mobile phone ng account",
    "New password": "Bagong password",
    "Confirm new password": "Kumpirmahin ang bagong password",
    "Update password": "I-update ang password",
    "Updating...": "Ina-update...",
    "Show": "Ipakita",
    "Hide": "Itago",
    "At least 8 characters": "Hindi bababa sa 8 characters",
    "1 uppercase letter": "1 malaking letra",
    "1 lowercase letter": "1 maliit na letra",
    "1 number": "1 numero",
    "Add": "Idagdag"
  },
  ar: {
    "Care Passport": "جواز الرعاية",
    "Your Banxito Care Passport is loading.": "يتم تحميل جواز الرعاية من Banxito.",
    "{name}, your Banxito Care Passport is ready.": "{name}، جواز الرعاية من Banxito جاهز.",
    "One simple place to see what care you can use, what to say at the front desk, and how to show your QR or code.": "مكان واحد بسيط لمعرفة الدعم المتاح لك، وما تقوله في مكتب الاستقبال، وكيف تعرض رمز QR أو الكود.",
    "Show my QR or code": "عرض رمز QR أو الكود",
    "Open Visit QR": "فتح رمز الزيارة",
    "Refresh passport": "تحديث جواز الرعاية",
    "Available care": "الدعم المتاح",
    "What you can use": "ما يمكنك استخدامه",
    "Plain-language service allowances by category. Banxito still checks provider approval and the exact service before fulfillment.": "أرصدة واضحة حسب الفئة. لا يزال Banxito يتحقق من اعتماد المزود والخدمة المحددة قبل الدفع.",
    "Building your Care Passport": "جارٍ تجهيز جواز الرعاية",
    "Care Passport not loaded yet": "لم يتم تحميل جواز الرعاية بعد",
    "Banxito is preparing a simple view of your available care categories.": "يقوم Banxito بإعداد عرض بسيط لفئات الرعاية الممولة لديك.",
    "At the front desk": "في مكتب الاستقبال",
    "What to say": "ماذا تقول",
    "Use this simple script so the clinic or provider knows exactly what to do.": "استخدم هذه العبارة البسيطة حتى تعرف العيادة أو المزود ما يجب فعله.",
    "Say this": "قل هذا",
    "Tell the front desk: I'm using Banxito. Please scan my QR or enter my code before you charge me.": "قل لمكتب الاستقبال: أستخدم Banxito. يرجى مسح رمز QR أو إدخال الكود قبل تحصيل الرسوم.",
    "QR instructions": "تعليمات رمز QR",
    "Open Visit QR, choose an available category, and show the QR or manual code after the provider confirms the service and amount.": "افتح رمز الزيارة، اختر فئة ممولة، ثم اعرض رمز QR أو الكود اليدوي بعد أن يؤكد المزود الخدمة والمبلغ.",
    "Backup options": "خيارات بديلة",
    "If scanning does not work": "إذا لم يعمل المسح",
    "Banxito always gives the provider a low-tech option.": "يوفر Banxito دائمًا خيارًا بسيطًا للمزود.",
    "Ask the provider to type the manual code shown under your QR.": "اطلب من المزود كتابة الكود اليدوي الظاهر أسفل رمز QR.",
    "If the code expired, generate a fresh Visit QR when you are ready.": "إذا انتهت صلاحية الكود، أنشئ رمز زيارة جديدًا عندما تكون جاهزًا.",
    "Helpful reminders": "تذكيرات مفيدة",
    "What needs attention": "ما يحتاج إلى انتباه",
    "Friendly notes based on your available support.": "ملاحظات مفيدة بناءً على الدعم المتاح لديك.",
    "You have {amount} in {category} service allowance.": "لديك {amount} لفئة {category}.",
    "Use this with approved Banxito {category} providers.": "استخدمه مع مزودي Banxito المعتمدين لفئة {category}.",
    "1. Use your setup code": "1. استخدم كود الإعداد",
    "Your sponsor shares a setup code with you. Enter it once to connect your Banxito account.": "يرسل لك الراعي كود إعداد. أدخله مرة واحدة لربط حسابك في Banxito.",
    "2. Check your allowances": "2. تحقق من أرصدتك",
    "See what service categories are available to you, such as healthcare, school, or essentials.": "اعرف الفئات الممولة لك، مثل الصحة أو المدرسة أو الأساسيات.",
    "3. Visit approved providers": "3. زر مزودين معتمدين",
    "Banxito support is meant to be used with approved providers and tracked service delivery.": "دعم Banxito مخصص للاستخدام مع مزودين معتمدين وخدمات مسجلة.",
    "Password": "كلمة المرور",
    "Confirm password": "تأكيد كلمة المرور",
    "Forgot password?": "هل نسيت كلمة المرور؟",
    "Reset your password": "إعادة تعيين كلمة المرور",
    "Account email or mobile phone": "البريد الإلكتروني أو الهاتف المحمول للحساب",
    "New password": "كلمة مرور جديدة",
    "Confirm new password": "تأكيد كلمة المرور الجديدة",
    "Update password": "تحديث كلمة المرور",
    "Updating...": "جارٍ التحديث...",
    "Show": "إظهار",
    "Hide": "إخفاء",
    "At least 8 characters": "8 أحرف على الأقل",
    "1 uppercase letter": "حرف كبير واحد",
    "1 lowercase letter": "حرف صغير واحد",
    "1 number": "رقم واحد",
    "Add": "أضف"
  },
  hi: {
    "Care Passport": "देखभाल पासपोर्ट",
    "Your Banxito Care Passport is loading.": "आपका Banxito देखभाल पासपोर्ट लोड हो रहा है।",
    "{name}, your Banxito Care Passport is ready.": "{name}, आपका Banxito देखभाल पासपोर्ट तैयार है।",
    "One simple place to see what care you can use, what to say at the front desk, and how to show your QR or code.": "एक सरल जगह जहाँ आप उपलब्ध सहायता, फ्रंट डेस्क पर क्या कहना है, और अपना QR या कोड कैसे दिखाना है देख सकते हैं।",
    "Show my QR or code": "मेरा QR या कोड दिखाएँ",
    "Open Visit QR": "विज़िट QR खोलें",
    "Refresh passport": "पासपोर्ट रीफ्रेश करें",
    "Available care": "उपलब्ध सहायता",
    "What you can use": "आप क्या उपयोग कर सकते हैं",
    "Plain-language service allowances by category. Banxito still checks provider approval and the exact service before fulfillment.": "श्रेणी के अनुसार सरल भाषा में बैलेंस। भुगतान से पहले Banxito प्रदाता की स्वीकृति और सेवा की जाँच करता है।",
    "Building your Care Passport": "आपका देखभाल पासपोर्ट तैयार हो रहा है",
    "Care Passport not loaded yet": "देखभाल पासपोर्ट अभी लोड नहीं हुआ",
    "Banxito is preparing a simple view of your available care categories.": "Banxito आपकी वित्तपोषित देखभाल श्रेणियों का सरल दृश्य तैयार कर रहा है।",
    "At the front desk": "फ्रंट डेस्क पर",
    "What to say": "क्या कहना है",
    "Use this simple script so the clinic or provider knows exactly what to do.": "इस सरल वाक्य का उपयोग करें ताकि क्लिनिक या प्रदाता समझ सके कि क्या करना है।",
    "Say this": "यह कहें",
    "Tell the front desk: I'm using Banxito. Please scan my QR or enter my code before you charge me.": "फ्रंट डेस्क से कहें: मैं Banxito का उपयोग कर रहा/रही हूँ। कृपया शुल्क लेने से पहले मेरा QR स्कैन करें या कोड दर्ज करें।",
    "QR instructions": "QR निर्देश",
    "Open Visit QR, choose an available category, and show the QR or manual code after the provider confirms the service and amount.": "विज़िट QR खोलें, वित्तपोषित श्रेणी चुनें, और प्रदाता द्वारा सेवा व राशि की पुष्टि के बाद QR या मैनुअल कोड दिखाएँ।",
    "Backup options": "बैकअप विकल्प",
    "If scanning does not work": "यदि स्कैन काम नहीं करता",
    "Banxito always gives the provider a low-tech option.": "Banxito प्रदाता को हमेशा एक सरल बैकअप विकल्प देता है।",
    "Ask the provider to type the manual code shown under your QR.": "प्रदाता से कहें कि वे आपके QR के नीचे दिखा मैनुअल कोड टाइप करें।",
    "If the code expired, generate a fresh Visit QR when you are ready.": "यदि कोड समाप्त हो गया है, तो तैयार होने पर नया विज़िट QR बनाएं।",
    "Helpful reminders": "उपयोगी अनुस्मारक",
    "What needs attention": "जिस पर ध्यान चाहिए",
    "Friendly notes based on your available support.": "आपकी उपलब्ध सहायता के आधार पर उपयोगी नोट्स।",
    "You have {amount} in {category} service allowance.": "आपके पास {category} के लिए {amount} है।",
    "Use this with approved Banxito {category} providers.": "इसे स्वीकृत Banxito {category} प्रदाताओं के साथ उपयोग करें।",
    "1. Use your setup code": "1. अपना सेटअप कोड उपयोग करें",
    "Your sponsor shares a setup code with you. Enter it once to connect your Banxito account.": "आपका प्रायोजक आपके साथ सेटअप कोड साझा करता है। अपना Banxito खाता जोड़ने के लिए इसे एक बार दर्ज करें।",
    "2. Check your allowances": "2. अपने बैलेंस देखें",
    "See what service categories are available to you, such as healthcare, school, or essentials.": "देखें कि आपके लिए कौन-सी श्रेणियाँ वित्तपोषित हैं, जैसे स्वास्थ्य, स्कूल या आवश्यक चीजें।",
    "3. Visit approved providers": "3. स्वीकृत प्रदाताओं के पास जाएँ",
    "Banxito support is meant to be used with approved providers and tracked service delivery.": "Banxito सहायता स्वीकृत प्रदाताओं और दर्ज सेवाओं के साथ उपयोग के लिए है।",
    "Password": "पासवर्ड",
    "Confirm password": "पासवर्ड की पुष्टि करें",
    "Forgot password?": "पासवर्ड भूल गए?",
    "Reset your password": "अपना पासवर्ड रीसेट करें",
    "Account email or mobile phone": "खाते का ईमेल या मोबाइल फोन",
    "New password": "नया पासवर्ड",
    "Confirm new password": "नए पासवर्ड की पुष्टि करें",
    "Update password": "पासवर्ड अपडेट करें",
    "Updating...": "अपडेट हो रहा है...",
    "Show": "दिखाएँ",
    "Hide": "छिपाएँ",
    "At least 8 characters": "कम से कम 8 अक्षर",
    "1 uppercase letter": "1 बड़ा अक्षर",
    "1 lowercase letter": "1 छोटा अक्षर",
    "1 number": "1 संख्या",
    "Add": "जोड़ें"
  },
  vi: {
    "Care Passport": "Hồ sơ chăm sóc",
    "Your Banxito Care Passport is loading.": "Hồ sơ chăm sóc Banxito của bạn đang tải.",
    "{name}, your Banxito Care Passport is ready.": "{name}, Hồ sơ chăm sóc Banxito của bạn đã sẵn sàng.",
    "One simple place to see what care you can use, what to say at the front desk, and how to show your QR or code.": "Một nơi đơn giản để xem bạn có thể dùng hỗ trợ nào, nói gì tại quầy lễ tân, và cách hiển thị mã QR hoặc mã chữ.",
    "Show my QR or code": "Hiển thị QR hoặc mã của tôi",
    "Open Visit QR": "Mở QR thăm khám",
    "Refresh passport": "Làm mới hồ sơ",
    "Available care": "Hỗ trợ khả dụng",
    "What you can use": "Bạn có thể dùng gì",
    "Plain-language service allowances by category. Banxito still checks provider approval and the exact service before fulfillment.": "Số dư rõ ràng theo từng danh mục. Banxito vẫn kiểm tra nhà cung cấp đã được duyệt và dịch vụ cụ thể trước khi thanh toán.",
    "Building your Care Passport": "Đang chuẩn bị Hồ sơ chăm sóc",
    "Care Passport not loaded yet": "Hồ sơ chăm sóc chưa tải",
    "Banxito is preparing a simple view of your available care categories.": "Banxito đang chuẩn bị chế độ xem đơn giản cho các danh mục đã được tài trợ của bạn.",
    "At the front desk": "Tại quầy lễ tân",
    "What to say": "Nên nói gì",
    "Use this simple script so the clinic or provider knows exactly what to do.": "Dùng câu nói đơn giản này để phòng khám hoặc nhà cung cấp biết chính xác cần làm gì.",
    "Say this": "Hãy nói",
    "Tell the front desk: I'm using Banxito. Please scan my QR or enter my code before you charge me.": "Nói với quầy lễ tân: Tôi dùng Banxito. Vui lòng quét QR hoặc nhập mã của tôi trước khi tính phí.",
    "QR instructions": "Hướng dẫn QR",
    "Open Visit QR, choose an available category, and show the QR or manual code after the provider confirms the service and amount.": "Mở QR thăm khám, chọn danh mục có tiền, rồi hiển thị QR hoặc mã thủ công sau khi nhà cung cấp xác nhận dịch vụ và số tiền.",
    "Backup options": "Tùy chọn dự phòng",
    "If scanning does not work": "Nếu quét không được",
    "Banxito always gives the provider a low-tech option.": "Banxito luôn cung cấp cho nhà cung cấp một lựa chọn đơn giản.",
    "Ask the provider to type the manual code shown under your QR.": "Yêu cầu nhà cung cấp nhập mã thủ công hiển thị dưới QR của bạn.",
    "If the code expired, generate a fresh Visit QR when you are ready.": "Nếu mã hết hạn, hãy tạo QR thăm khám mới khi bạn sẵn sàng.",
    "Helpful reminders": "Nhắc nhở hữu ích",
    "What needs attention": "Điều cần chú ý",
    "Friendly notes based on your available support.": "Ghi chú hữu ích dựa trên hỗ trợ hiện có của bạn.",
    "You have {amount} in {category} service allowance.": "Bạn có {amount} cho {category}.",
    "Use this with approved Banxito {category} providers.": "Dùng với các nhà cung cấp Banxito đã được duyệt cho {category}.",
    "1. Use your setup code": "1. Dùng mã thiết lập của bạn",
    "Your sponsor shares a setup code with you. Enter it once to connect your Banxito account.": "Người bảo trợ chia sẻ mã thiết lập với bạn. Nhập một lần để kết nối tài khoản Banxito.",
    "2. Check your allowances": "2. Kiểm tra số dư",
    "See what service categories are available to you, such as healthcare, school, or essentials.": "Xem các danh mục đã được tài trợ cho bạn, như y tế, trường học hoặc nhu yếu phẩm.",
    "3. Visit approved providers": "3. Đến nhà cung cấp đã được duyệt",
    "Banxito support is meant to be used with approved providers and tracked service delivery.": "Hỗ trợ Banxito được dùng với nhà cung cấp đã được duyệt và dịch vụ được ghi nhận.",
    "Password": "Mật khẩu",
    "Confirm password": "Xác nhận mật khẩu",
    "Forgot password?": "Quên mật khẩu?",
    "Reset your password": "Đặt lại mật khẩu",
    "Account email or mobile phone": "Email hoặc số điện thoại tài khoản",
    "New password": "Mật khẩu mới",
    "Confirm new password": "Xác nhận mật khẩu mới",
    "Update password": "Cập nhật mật khẩu",
    "Updating...": "Đang cập nhật...",
    "Show": "Hiện",
    "Hide": "Ẩn",
    "At least 8 characters": "Ít nhất 8 ký tự",
    "1 uppercase letter": "1 chữ hoa",
    "1 lowercase letter": "1 chữ thường",
    "1 number": "1 số",
    "Add": "Thêm"
  },
  pa: {
    "Care Passport": "ਕੇਅਰ ਪਾਸਪੋਰਟ",
    "Your Banxito Care Passport is loading.": "ਤੁਹਾਡਾ Banxito ਕੇਅਰ ਪਾਸਪੋਰਟ ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ।",
    "{name}, your Banxito Care Passport is ready.": "{name}, ਤੁਹਾਡਾ Banxito ਕੇਅਰ ਪਾਸਪੋਰਟ ਤਿਆਰ ਹੈ।",
    "One simple place to see what care you can use, what to say at the front desk, and how to show your QR or code.": "ਇੱਕ ਸਧਾਰਨ ਥਾਂ ਜਿੱਥੇ ਤੁਸੀਂ ਵੇਖ ਸਕਦੇ ਹੋ ਕਿ ਕਿਹੜੀ ਸਹਾਇਤਾ ਵਰਤ ਸਕਦੇ ਹੋ, ਫਰੰਟ ਡੈਸਕ 'ਤੇ ਕੀ ਕਹਿਣਾ ਹੈ, ਅਤੇ ਆਪਣਾ QR ਜਾਂ ਕੋਡ ਕਿਵੇਂ ਦਿਖਾਉਣਾ ਹੈ।",
    "Show my QR or code": "ਮੇਰਾ QR ਜਾਂ ਕੋਡ ਦਿਖਾਓ",
    "Open Visit QR": "ਵਿਜ਼ਿਟ QR ਖੋਲ੍ਹੋ",
    "Refresh passport": "ਪਾਸਪੋਰਟ ਤਾਜ਼ਾ ਕਰੋ",
    "Available care": "ਉਪਲਬਧ ਸਹਾਇਤਾ",
    "What you can use": "ਤੁਸੀਂ ਕੀ ਵਰਤ ਸਕਦੇ ਹੋ",
    "Plain-language service allowances by category. Banxito still checks provider approval and the exact service before fulfillment.": "ਸ਼੍ਰੇਣੀ ਅਨੁਸਾਰ ਸਧਾਰਨ ਬਕਾਇਆ। ਭੁਗਤਾਨ ਤੋਂ ਪਹਿਲਾਂ Banxito ਪ੍ਰਦਾਤਾ ਦੀ ਮਨਜ਼ੂਰੀ ਅਤੇ ਸੇਵਾ ਦੀ ਜਾਂਚ ਕਰਦਾ ਹੈ।",
    "Building your Care Passport": "ਤੁਹਾਡਾ ਕੇਅਰ ਪਾਸਪੋਰਟ ਤਿਆਰ ਕੀਤਾ ਜਾ ਰਿਹਾ ਹੈ",
    "Care Passport not loaded yet": "ਕੇਅਰ ਪਾਸਪੋਰਟ ਹਾਲੇ ਲੋਡ ਨਹੀਂ ਹੋਇਆ",
    "Banxito is preparing a simple view of your available care categories.": "Banxito ਤੁਹਾਡੀਆਂ ਫੰਡ ਕੀਤੀਆਂ ਕੇਅਰ ਸ਼੍ਰੇਣੀਆਂ ਦੀ ਸਧਾਰਨ ਝਲਕ ਤਿਆਰ ਕਰ ਰਿਹਾ ਹੈ।",
    "At the front desk": "ਫਰੰਟ ਡੈਸਕ 'ਤੇ",
    "What to say": "ਕੀ ਕਹਿਣਾ ਹੈ",
    "Use this simple script so the clinic or provider knows exactly what to do.": "ਇਹ ਸਧਾਰਨ ਵਾਕ ਵਰਤੋ ਤਾਂ ਕਿ ਕਲੀਨਿਕ ਜਾਂ ਪ੍ਰਦਾਤਾ ਜਾਣ ਸਕੇ ਕਿ ਕੀ ਕਰਨਾ ਹੈ।",
    "Say this": "ਇਹ ਕਹੋ",
    "Tell the front desk: I'm using Banxito. Please scan my QR or enter my code before you charge me.": "ਫਰੰਟ ਡੈਸਕ ਨੂੰ ਕਹੋ: ਮੈਂ Banxito ਵਰਤ ਰਿਹਾ/ਰਹੀ ਹਾਂ। ਮੈਨੂੰ ਚਾਰਜ ਕਰਨ ਤੋਂ ਪਹਿਲਾਂ ਕਿਰਪਾ ਕਰਕੇ ਮੇਰਾ QR ਸਕੈਨ ਕਰੋ ਜਾਂ ਮੇਰਾ ਕੋਡ ਦਰਜ ਕਰੋ।",
    "QR instructions": "QR ਹਦਾਇਤਾਂ",
    "Backup options": "ਬੈਕਅੱਪ ਚੋਣਾਂ",
    "If scanning does not work": "ਜੇ ਸਕੈਨ ਨਹੀਂ ਹੁੰਦਾ",
    "Banxito always gives the provider a low-tech option.": "Banxito ਪ੍ਰਦਾਤਾ ਨੂੰ ਹਮੇਸ਼ਾ ਇੱਕ ਸਧਾਰਨ ਚੋਣ ਦਿੰਦਾ ਹੈ।",
    "Ask the provider to type the manual code shown under your QR.": "ਪ੍ਰਦਾਤਾ ਨੂੰ ਕਹੋ ਕਿ ਉਹ ਤੁਹਾਡੇ QR ਹੇਠਾਂ ਦਿੱਤਾ ਮੈਨੂਅਲ ਕੋਡ ਟਾਈਪ ਕਰੇ।",
    "If the code expired, generate a fresh Visit QR when you are ready.": "ਜੇ ਕੋਡ ਮਿਆਦ ਪੁੱਗ ਗਿਆ ਹੈ, ਤਿਆਰ ਹੋਣ 'ਤੇ ਨਵਾਂ ਵਿਜ਼ਿਟ QR ਬਣਾਓ।",
    "Helpful reminders": "ਮਦਦਗਾਰ ਯਾਦ ਦਿਹਾਣੀਆਂ",
    "What needs attention": "ਜਿਸ 'ਤੇ ਧਿਆਨ ਚਾਹੀਦਾ ਹੈ",
    "You have {amount} in {category} service allowance.": "ਤੁਹਾਡੇ ਕੋਲ {category} ਲਈ {amount} ਹੈ।",
    "Use this with approved Banxito {category} providers.": "ਇਸਨੂੰ ਮਨਜ਼ੂਰਸ਼ੁਦਾ Banxito {category} ਪ੍ਰਦਾਤਾਵਾਂ ਨਾਲ ਵਰਤੋ।",
    "1. Use your setup code": "1. ਆਪਣਾ ਸੈਟਅੱਪ ਕੋਡ ਵਰਤੋ",
    "Your sponsor shares a setup code with you. Enter it once to connect your Banxito account.": "ਤੁਹਾਡਾ ਸਪਾਂਸਰ ਤੁਹਾਡੇ ਨਾਲ ਸੈਟਅੱਪ ਕੋਡ ਸਾਂਝਾ ਕਰਦਾ ਹੈ। ਆਪਣਾ Banxito ਖਾਤਾ ਜੋੜਨ ਲਈ ਇਸਨੂੰ ਇੱਕ ਵਾਰ ਦਰਜ ਕਰੋ।",
    "2. Check your allowances": "2. ਆਪਣੇ ਬਕਾਏ ਵੇਖੋ",
    "See what service categories are available to you, such as healthcare, school, or essentials.": "ਵੇਖੋ ਤੁਹਾਡੇ ਲਈ ਕਿਹੜੀਆਂ ਸ਼੍ਰੇਣੀਆਂ ਫੰਡ ਕੀਤੀਆਂ ਗਈਆਂ ਹਨ, ਜਿਵੇਂ ਸਿਹਤ, ਸਕੂਲ ਜਾਂ ਜ਼ਰੂਰੀ ਚੀਜ਼ਾਂ।",
    "3. Visit approved providers": "3. ਮਨਜ਼ੂਰਸ਼ੁਦਾ ਪ੍ਰਦਾਤਾਵਾਂ ਕੋਲ ਜਾਓ",
    "Banxito support is meant to be used with approved providers and tracked service delivery.": "Banxito ਸਹਾਇਤਾ ਮਨਜ਼ੂਰਸ਼ੁਦਾ ਪ੍ਰਦਾਤਾਵਾਂ ਅਤੇ ਦਰਜ ਕੀਤੀਆਂ ਸੇਵਾਵਾਂ ਨਾਲ ਵਰਤਣ ਲਈ ਹੈ।",
    "Password": "ਪਾਸਵਰਡ",
    "Confirm password": "ਪਾਸਵਰਡ ਪੁਸ਼ਟੀ ਕਰੋ",
    "Forgot password?": "ਪਾਸਵਰਡ ਭੁੱਲ ਗਏ?",
    "Reset your password": "ਆਪਣਾ ਪਾਸਵਰਡ ਰੀਸੈਟ ਕਰੋ",
    "Account email or mobile phone": "ਖਾਤੇ ਦਾ ਈਮੇਲ ਜਾਂ ਮੋਬਾਈਲ ਫੋਨ",
    "New password": "ਨਵਾਂ ਪਾਸਵਰਡ",
    "Confirm new password": "ਨਵੇਂ ਪਾਸਵਰਡ ਦੀ ਪੁਸ਼ਟੀ ਕਰੋ",
    "Update password": "ਪਾਸਵਰਡ ਅਪਡੇਟ ਕਰੋ",
    "Updating...": "ਅਪਡੇਟ ਹੋ ਰਿਹਾ ਹੈ...",
    "Show": "ਦਿਖਾਓ",
    "Hide": "ਲੁਕਾਓ",
    "At least 8 characters": "ਘੱਟੋ-ਘੱਟ 8 ਅੱਖਰ",
    "1 uppercase letter": "1 ਵੱਡਾ ਅੱਖਰ",
    "1 lowercase letter": "1 ਛੋਟਾ ਅੱਖਰ",
    "1 number": "1 ਨੰਬਰ",
    "Add": "ਜੋੜੋ"
  },
  sw: {
    "Care Passport": "Pasipoti ya huduma",
    "Your Banxito Care Passport is loading.": "Pasipoti yako ya huduma ya Banxito inapakia.",
    "{name}, your Banxito Care Passport is ready.": "{name}, Pasipoti yako ya huduma ya Banxito iko tayari.",
    "One simple place to see what care you can use, what to say at the front desk, and how to show your QR or code.": "Sehemu moja rahisi ya kuona msaada unaoweza kutumia, cha kusema mapokezi, na jinsi ya kuonyesha QR au msimbo wako.",
    "Show my QR or code": "Onyesha QR au msimbo wangu",
    "Open Visit QR": "Fungua QR ya ziara",
    "Refresh passport": "Sasisha pasipoti",
    "Available care": "Msaada uliopo",
    "What you can use": "Unachoweza kutumia",
    "Plain-language service allowances by category. Banxito still checks provider approval and the exact service before fulfillment.": "Salio rahisi kwa kila aina. Banxito bado hukagua idhini ya mtoa huduma na huduma halisi kabla ya malipo.",
    "Building your Care Passport": "Tunaandaa Pasipoti yako ya huduma",
    "Care Passport not loaded yet": "Pasipoti ya huduma bado haijapakia",
    "Banxito is preparing a simple view of your available care categories.": "Banxito inaandaa mwonekano rahisi wa aina za huduma zilizofadhiliwa.",
    "At the front desk": "Mapokezi",
    "What to say": "Cha kusema",
    "Use this simple script so the clinic or provider knows exactly what to do.": "Tumia maneno haya rahisi ili kliniki au mtoa huduma ajue la kufanya.",
    "Say this": "Sema hivi",
    "Tell the front desk: I'm using Banxito. Please scan my QR or enter my code before you charge me.": "Mwambie mhudumu: Ninatumia Banxito. Tafadhali changanua QR yangu au ingiza msimbo wangu kabla ya kunitoza.",
    "QR instructions": "Maelekezo ya QR",
    "Open Visit QR, choose an available category, and show the QR or manual code after the provider confirms the service and amount.": "Fungua QR ya ziara, chagua aina yenye fedha, kisha onyesha QR au msimbo baada ya mtoa huduma kuthibitisha huduma na kiasi.",
    "Backup options": "Njia mbadala",
    "If scanning does not work": "Ikiwa kuchanganua hakufanyi kazi",
    "Banxito always gives the provider a low-tech option.": "Banxito humpa mtoa huduma njia rahisi mbadala kila wakati.",
    "Ask the provider to type the manual code shown under your QR.": "Mwombe mtoa huduma aandike msimbo unaoonekana chini ya QR yako.",
    "If the code expired, generate a fresh Visit QR when you are ready.": "Ikiwa msimbo umeisha muda, tengeneza QR mpya ya ziara ukiwa tayari.",
    "Helpful reminders": "Vikumbusho muhimu",
    "What needs attention": "Kinachohitaji kuangaliwa",
    "Friendly notes based on your available support.": "Vidokezo vya kirafiki kulingana na msaada uliopo.",
    "You have {amount} in {category} service allowance.": "Una {amount} kwa {category}.",
    "Use this with approved Banxito {category} providers.": "Tumia hii na watoa huduma wa Banxito walioidhinishwa kwa {category}.",
    "1. Use your setup code": "1. Tumia msimbo wako wa kuanzisha",
    "Your sponsor shares a setup code with you. Enter it once to connect your Banxito account.": "Mfadhili wako atakupa msimbo wa kuanzisha. Uweke mara moja ili kuunganisha akaunti yako ya Banxito.",
    "2. Check your allowances": "2. Angalia salio lako",
    "See what service categories are available to you, such as healthcare, school, or essentials.": "Ona aina zilizofadhiliwa kwa ajili yako, kama afya, shule au mahitaji muhimu.",
    "3. Visit approved providers": "3. Tembelea watoa huduma walioidhinishwa",
    "Banxito support is meant to be used with approved providers and tracked service delivery.": "Msaada wa Banxito unapaswa kutumiwa na watoa huduma walioidhinishwa na huduma zilizorekodiwa.",
    "Password": "Nenosiri",
    "Confirm password": "Thibitisha nenosiri",
    "Forgot password?": "Umesahau nenosiri?",
    "Reset your password": "Weka upya nenosiri",
    "Account email or mobile phone": "Barua pepe au simu ya akaunti",
    "New password": "Nenosiri jipya",
    "Confirm new password": "Thibitisha nenosiri jipya",
    "Update password": "Sasisha nenosiri",
    "Updating...": "Inasasisha...",
    "Show": "Onyesha",
    "Hide": "Ficha",
    "At least 8 characters": "Angalau herufi 8",
    "1 uppercase letter": "Herufi kubwa 1",
    "1 lowercase letter": "Herufi ndogo 1",
    "1 number": "Nambari 1",
    "Add": "Ongeza"
  }
};

for (const [language, translations] of Object.entries(recipientPolishTranslations) as [
  AppLanguage,
  Record<string, string>
][]) {
  recipientTranslations[language] = {
    ...recipientTranslations[language],
    ...translations
  };
}

const recipientServiceDiscoveryTranslations: Partial<Record<AppLanguage, Record<string, string>>> = {
  es: {
    "Services": "Servicios",
    "Find Services": "Buscar servicios",
    "Find approved care near you": "Encuentra atención aprobada cerca de ti",
    "Choose an available category and Banxito will show approved providers that can submit a service claim for it.": "Elige una categoría con saldo y Banxito mostrará proveedores aprobados que pueden solicitar pago.",
    "All available categories": "Todas las categorías con saldo",
    "Search approved providers": "Buscar proveedores aprobados",
    "Search by provider, city, or category": "Buscar por proveedor, ciudad o categoría",
    "Open now": "Abierto ahora",
    "Use my location": "Usar mi ubicación",
    "Using your location": "Usando tu ubicación",
    "Map view": "Vista de mapa",
    "List view": "Vista de lista",
    "Providers": "Proveedores",
    "Map-ready provider list": "Lista de proveedores lista para mapa",
    "Approved providers": "Proveedores aprobados",
    "Map view will use these approved provider locations. Choose a provider from the list below to start a visit QR.": "La vista de mapa usará estas ubicaciones de proveedores aprobados. Elige un proveedor de la lista para iniciar un QR de visita.",
    "Call, get directions, or start a Banxito visit QR for an approved provider.": "Llama, obtén indicaciones o inicia un QR de visita Banxito para un proveedor aprobado.",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.": "Banxito ya tiene ubicaciones de proveedores. Agrega tu clave de Google Maps para activar el mapa.",
    "Approved provider locations will appear on the map once latitude and longitude are available.": "Las ubicaciones de proveedores aprobados aparecerán en el mapa cuando haya latitud y longitud.",
    "Map of approved providers": "Mapa de proveedores aprobados",
    "Loading approved providers": "Cargando proveedores aprobados",
    "Checking providers that match your available categories.": "Buscando proveedores que coincidan con tus categorías con saldo.",
    "No approved providers found": "No se encontraron proveedores aprobados",
    "Try another category, search term, or turn off open-now filtering.": "Prueba otra categoría, búsqueda o desactiva el filtro de abierto ahora.",
    "Address coming soon": "Dirección próximamente",
    "Closed now": "Cerrado ahora",
    "Available for this category": "Disponible para esta categoría",
    "Allowance available": "Saldo disponible",
    "Distance": "Distancia",
    "Nearby": "Cerca",
    "km away": "km de distancia",
    "Provider is approved for": "Proveedor aprobado para",
    "This provider can submit a service claim for your selected category. Banxito still verifies the exact service and amount before any card details are issued.": "Este proveedor puede solicitar pago desde la categoría seleccionada. Banxito todavía aprueba el servicio y monto exactos antes de emitir datos de tarjeta.",
    "Call": "Llamar",
    "Directions": "Indicaciones",
    "Start visit": "Iniciar visita",
    "Starting...": "Iniciando...",
    "Your visit QR is ready. Show it at the front desk.": "Tu QR de visita está listo. Muéstralo en recepción.",
    "Location access is not available in this browser.": "El acceso a la ubicación no está disponible en este navegador.",
    "Banxito is using your location only for this provider search.": "Banxito usa tu ubicación solo para esta búsqueda de proveedores.",
    "Banxito could not access your location. You can still search by category or provider name.": "Banxito no pudo acceder a tu ubicación. Aún puedes buscar por categoría o nombre del proveedor."
  },
  fr: {
    "Services": "Services",
    "Find Services": "Trouver des services",
    "Find approved care near you": "Trouvez des services approuvÃ©s prÃ¨s de vous",
    "Choose an available category and Banxito will show approved providers that can submit a service claim for it.": "Choisissez une catÃ©gorie financÃ©e et Banxito affichera les prestataires approuvÃ©s qui peuvent demander le paiement.",
    "All available categories": "Toutes les catÃ©gories financÃ©es",
    "Search approved providers": "Rechercher des prestataires approuvÃ©s",
    "Search by provider, city, or category": "Rechercher par prestataire, ville ou catÃ©gorie",
    "Open now": "Ouvert maintenant",
    "Use my location": "Utiliser ma position",
    "Using your location": "Position utilisÃ©e",
    "Map view": "Vue carte",
    "List view": "Vue liste",
    "Providers": "Prestataires",
    "Map-ready provider list": "Liste de prestataires prÃªte pour la carte",
    "Approved providers": "Prestataires approuvÃ©s",
    "Map view will use these approved provider locations. Choose a provider from the list below to start a visit QR.": "La vue carte utilisera ces emplacements approuvÃ©s. Choisissez un prestataire dans la liste pour dÃ©marrer un QR de visite.",
    "Call, get directions, or start a Banxito visit QR for an approved provider.": "Appelez, obtenez l'itinÃ©raire ou dÃ©marrez un QR de visite Banxito pour un prestataire approuvÃ©.",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.": "Banxito dispose des emplacements des prestataires. Ajoutez votre clÃ© Google Maps pour activer la carte.",
    "Approved provider locations will appear on the map once latitude and longitude are available.": "Les emplacements approuvÃ©s apparaÃ®tront sur la carte lorsque latitude et longitude seront disponibles.",
    "Map of approved providers": "Carte des prestataires approuvÃ©s",
    "Loading approved providers": "Chargement des prestataires approuvÃ©s",
    "Checking providers that match your available categories.": "Recherche de prestataires correspondant Ã  vos catÃ©gories financÃ©es.",
    "No approved providers found": "Aucun prestataire approuvÃ© trouvÃ©",
    "Try another category, search term, or turn off open-now filtering.": "Essayez une autre catÃ©gorie, un autre terme ou dÃ©sactivez le filtre ouvert maintenant.",
    "Address coming soon": "Adresse bientÃ´t disponible",
    "Closed now": "FermÃ© maintenant",
    "Available for this category": "Disponible pour cette catÃ©gorie",
    "Allowance available": "Solde disponible",
    "Distance": "Distance",
    "Nearby": "Ã€ proximitÃ©",
    "km away": "km",
    "Provider is approved for": "Prestataire approuvÃ© pour",
    "This provider can submit a service claim for your selected category. Banxito still verifies the exact service and amount before any card details are issued.": "Ce prestataire peut demander un paiement depuis la catÃ©gorie sÃ©lectionnÃ©e. Banxito approuve toujours le service exact et le montant avant d'Ã©mettre les donnÃ©es de carte.",
    "Call": "Appeler",
    "Directions": "ItinÃ©raire",
    "Start visit": "DÃ©marrer la visite",
    "Starting...": "DÃ©marrage...",
    "Your visit QR is ready. Show it at the front desk.": "Votre QR de visite est prÃªt. Montrez-le Ã  l'accueil.",
    "Location access is not available in this browser.": "L'accÃ¨s Ã  la position n'est pas disponible dans ce navigateur.",
    "Banxito is using your location only for this provider search.": "Banxito utilise votre position uniquement pour cette recherche de prestataires.",
    "Banxito could not access your location. You can still search by category or provider name.": "Banxito n'a pas pu accÃ©der Ã  votre position. Vous pouvez toujours chercher par catÃ©gorie ou nom de prestataire."
  },
  fil: {
    "Services": "Mga serbisyo",
    "Find Services": "Maghanap ng serbisyo",
    "Find approved care near you": "Maghanap ng aprubadong serbisyo malapit sa iyo",
    "Choose an available category and Banxito will show approved providers that can submit a service claim for it.": "Pumili ng may pondong kategorya at ipapakita ng Banxito ang aprubadong provider na maaaring humiling ng bayad.",
    "All available categories": "Lahat ng may pondong kategorya",
    "Search approved providers": "Maghanap ng aprubadong provider",
    "Search by provider, city, or category": "Maghanap ayon sa provider, lungsod, o kategorya",
    "Open now": "Bukas ngayon",
    "Use my location": "Gamitin ang lokasyon ko",
    "Using your location": "Ginagamit ang lokasyon mo",
    "Map view": "Mapa",
    "List view": "Listahan",
    "Providers": "Mga provider",
    "Map-ready provider list": "Listahan ng provider para sa mapa",
    "Approved providers": "Aprubadong provider",
    "Map view will use these approved provider locations. Choose a provider from the list below to start a visit QR.": "Gagamitin ng mapa ang mga lokasyong ito ng aprubadong provider. Pumili ng provider sa listahan para magsimula ng visit QR.",
    "Call, get directions, or start a Banxito visit QR for an approved provider.": "Tumawag, kumuha ng direksyon, o magsimula ng Banxito visit QR para sa aprubadong provider.",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.": "Handa na ang lokasyon ng mga provider sa Banxito. Idagdag ang Google Maps key para paganahin ang mapa.",
    "Approved provider locations will appear on the map once latitude and longitude are available.": "Lalabas sa mapa ang lokasyon kapag may latitude at longitude na.",
    "Map of approved providers": "Mapa ng aprubadong provider",
    "Loading approved providers": "Naglo-load ng aprubadong provider",
    "Checking providers that match your available categories.": "Tinitingnan ang mga provider na tugma sa iyong may pondong kategorya.",
    "No approved providers found": "Walang nahanap na aprubadong provider",
    "Try another category, search term, or turn off open-now filtering.": "Subukan ang ibang kategorya, paghahanap, o alisin ang filter na bukas ngayon.",
    "Address coming soon": "Malapit nang idagdag ang address",
    "Closed now": "Sarado ngayon",
    "Available for this category": "Available para sa kategoryang ito",
    "Allowance available": "Available na balanse",
    "Distance": "Layo",
    "Nearby": "Malapit",
    "km away": "km ang layo",
    "Provider is approved for": "Aprubado ang provider para sa",
    "This provider can submit a service claim for your selected category. Banxito still verifies the exact service and amount before any card details are issued.": "Maaaring humiling ng bayad ang provider mula sa napiling kategorya. Inaaprubahan pa rin ng Banxito ang eksaktong serbisyo at halaga bago maglabas ng card details.",
    "Call": "Tumawag",
    "Directions": "Direksyon",
    "Start visit": "Simulan ang pagbisita",
    "Starting...": "Nagsisimula...",
    "Your visit QR is ready. Show it at the front desk.": "Handa na ang iyong visit QR. Ipakita ito sa front desk.",
    "Location access is not available in this browser.": "Hindi available ang lokasyon sa browser na ito.",
    "Banxito is using your location only for this provider search.": "Ginagamit lang ng Banxito ang lokasyon mo para sa paghahanap na ito.",
    "Banxito could not access your location. You can still search by category or provider name.": "Hindi ma-access ng Banxito ang lokasyon mo. Maaari ka pa ring maghanap ayon sa kategorya o pangalan ng provider."
  },
  ar: {
    "Services": "Ø§Ù„Ø®Ø¯Ù…Ø§Øª",
    "Find Services": "Ø§Ù„Ø¨Ø­Ø« Ø¹Ù† Ø®Ø¯Ù…Ø§Øª",
    "Find approved care near you": "Ø§Ø¹Ø«Ø± Ø¹Ù„Ù‰ Ø±Ø¹Ø§ÙŠØ© Ù…Ø¹ØªÙ…Ø¯Ø© Ù‚Ø±ÙŠØ¨Ø© Ù…Ù†Ùƒ",
    "Choose an available category and Banxito will show approved providers that can submit a service claim for it.": "Ø§Ø®ØªØ± ÙØ¦Ø© Ù…Ù…ÙˆÙ„Ø© ÙˆØ³ÙŠØ¹Ø±Ø¶ Banxito Ù…Ù‚Ø¯Ù…ÙŠ Ø§Ù„Ø®Ø¯Ù…Ø© Ø§Ù„Ù…Ø¹ØªÙ…Ø¯ÙŠÙ† Ø§Ù„Ø°ÙŠÙ† ÙŠÙ…ÙƒÙ†Ù‡Ù… Ø·Ù„Ø¨ Ø§Ù„Ø¯ÙØ¹.",
    "All available categories": "ÙƒÙ„ Ø§Ù„ÙØ¦Ø§Øª Ø§Ù„Ù…Ù…ÙˆÙ„Ø©",
    "Search approved providers": "Ø§Ø¨Ø­Ø« Ø¹Ù† Ù…Ù‚Ø¯Ù…ÙŠÙ† Ù…Ø¹ØªÙ…Ø¯ÙŠÙ†",
    "Search by provider, city, or category": "Ø§Ø¨Ø­Ø« Ø¨Ø§Ù„Ù…Ù‚Ø¯Ù… Ø£Ùˆ Ø§Ù„Ù…Ø¯ÙŠÙ†Ø© Ø£Ùˆ Ø§Ù„ÙØ¦Ø©",
    "Open now": "Ù…ÙØªÙˆØ­ Ø§Ù„Ø¢Ù†",
    "Use my location": "Ø§Ø³ØªØ®Ø¯Ù… Ù…ÙˆÙ‚Ø¹ÙŠ",
    "Using your location": "ÙŠØªÙ… Ø§Ø³ØªØ®Ø¯Ø§Ù… Ù…ÙˆÙ‚Ø¹Ùƒ",
    "Map view": "Ø¹Ø±Ø¶ Ø§Ù„Ø®Ø±ÙŠØ·Ø©",
    "List view": "Ø¹Ø±Ø¶ Ø§Ù„Ù‚Ø§Ø¦Ù…Ø©",
    "Providers": "Ù…Ù‚Ø¯Ù…Ùˆ Ø§Ù„Ø®Ø¯Ù…Ø©",
    "Map-ready provider list": "Ù‚Ø§Ø¦Ù…Ø© Ù…Ù‚Ø¯Ù…ÙŠÙ† Ø¬Ø§Ù‡Ø²Ø© Ù„Ù„Ø®Ø±ÙŠØ·Ø©",
    "Approved providers": "Ù…Ù‚Ø¯Ù…ÙˆÙ† Ù…Ø¹ØªÙ…Ø¯ÙˆÙ†",
    "Map view will use these approved provider locations. Choose a provider from the list below to start a visit QR.": "Ø³ØªØ³ØªØ®Ø¯Ù… Ø§Ù„Ø®Ø±ÙŠØ·Ø© Ù…ÙˆØ§Ù‚Ø¹ Ù‡Ø¤Ù„Ø§Ø¡ Ø§Ù„Ù…Ù‚Ø¯Ù…ÙŠÙ† Ø§Ù„Ù…Ø¹ØªÙ…Ø¯ÙŠÙ†. Ø§Ø®ØªØ± Ù…Ù‚Ø¯Ù…Ø§Ù‹ Ù„Ø¨Ø¯Ø¡ QR Ø§Ù„Ø²ÙŠØ§Ø±Ø©.",
    "Call, get directions, or start a Banxito visit QR for an approved provider.": "Ø§ØªØµÙ„ØŒ Ø§Ø­ØµÙ„ Ø¹Ù„Ù‰ Ø§Ù„Ø§ØªØ¬Ø§Ù‡Ø§ØªØŒ Ø£Ùˆ Ø§Ø¨Ø¯Ø£ QR Ø²ÙŠØ§Ø±Ø© Banxito Ù„Ù…Ù‚Ø¯Ù… Ù…Ø¹ØªÙ…Ø¯.",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.": "Ù„Ø¯Ù‰ Banxito Ø¨ÙŠØ§Ù†Ø§Øª Ù…ÙˆØ§Ù‚Ø¹ Ø§Ù„Ù…Ù‚Ø¯Ù…ÙŠÙ†. Ø£Ø¶Ù Ù…ÙØªØ§Ø­ Google Maps Ù„ØªÙØ¹ÙŠÙ„ Ø§Ù„Ø®Ø±ÙŠØ·Ø©.",
    "Approved provider locations will appear on the map once latitude and longitude are available.": "Ø³ØªØ¸Ù‡Ø± Ù…ÙˆØ§Ù‚Ø¹ Ø§Ù„Ù…Ù‚Ø¯Ù…ÙŠÙ† Ø¹Ù„Ù‰ Ø§Ù„Ø®Ø±ÙŠØ·Ø© Ø¹Ù†Ø¯ ØªÙˆÙØ± Ø§Ù„Ø¥Ø­Ø¯Ø§Ø«ÙŠØ§Øª.",
    "Map of approved providers": "Ø®Ø±ÙŠØ·Ø© Ø§Ù„Ù…Ù‚Ø¯Ù…ÙŠÙ† Ø§Ù„Ù…Ø¹ØªÙ…Ø¯ÙŠÙ†",
    "Loading approved providers": "Ø¬Ø§Ø±ÙŠ ØªØ­Ù…ÙŠÙ„ Ø§Ù„Ù…Ù‚Ø¯Ù…ÙŠÙ† Ø§Ù„Ù…Ø¹ØªÙ…Ø¯ÙŠÙ†",
    "Checking providers that match your available categories.": "ÙŠØªÙ… Ø§Ù„ØªØ­Ù‚Ù‚ Ù…Ù† Ø§Ù„Ù…Ù‚Ø¯Ù…ÙŠÙ† Ø§Ù„Ù…Ø·Ø§Ø¨Ù‚ÙŠÙ† Ù„ÙØ¦Ø§ØªÙƒ Ø§Ù„Ù…Ù…ÙˆÙ„Ø©.",
    "No approved providers found": "Ù„Ù… ÙŠØªÙ… Ø§Ù„Ø¹Ø«ÙˆØ± Ø¹Ù„Ù‰ Ù…Ù‚Ø¯Ù…ÙŠÙ† Ù…Ø¹ØªÙ…Ø¯ÙŠÙ†",
    "Try another category, search term, or turn off open-now filtering.": "Ø¬Ø±Ø¨ ÙØ¦Ø© Ø£Ùˆ Ø¨Ø­Ø«Ø§Ù‹ Ø¢Ø®Ø± Ø£Ùˆ Ø£ÙˆÙ‚Ù ÙÙ„ØªØ± Ø§Ù„Ù…ÙØªÙˆØ­ Ø§Ù„Ø¢Ù†.",
    "Address coming soon": "Ø³ÙŠØªÙ… Ø¥Ø¶Ø§ÙØ© Ø§Ù„Ø¹Ù†ÙˆØ§Ù† Ù‚Ø±ÙŠØ¨Ø§Ù‹",
    "Closed now": "Ù…ØºÙ„Ù‚ Ø§Ù„Ø¢Ù†",
    "Available for this category": "Ù…ØªØ§Ø­ Ù„Ù‡Ø°Ù‡ Ø§Ù„ÙØ¦Ø©",
    "Allowance available": "Ø§Ù„Ø±ØµÙŠØ¯ Ø§Ù„Ù…ØªØ§Ø­",
    "Distance": "Ø§Ù„Ù…Ø³Ø§ÙØ©",
    "Nearby": "Ù‚Ø±ÙŠØ¨",
    "km away": "ÙƒÙ…",
    "Provider is approved for": "Ø§Ù„Ù…Ù‚Ø¯Ù… Ù…Ø¹ØªÙ…Ø¯ Ù„Ù€",
    "This provider can submit a service claim for your selected category. Banxito still verifies the exact service and amount before any card details are issued.": "ÙŠÙ…ÙƒÙ† Ù„Ù‡Ø°Ø§ Ø§Ù„Ù…Ù‚Ø¯Ù… Ø·Ù„Ø¨ Ø§Ù„Ø¯ÙØ¹ Ù…Ù† Ø§Ù„ÙØ¦Ø© Ø§Ù„Ù…Ø­Ø¯Ø¯Ø©. Ù„Ø§ ÙŠØ²Ø§Ù„ Banxito ÙŠÙˆØ§ÙÙ‚ Ø¹Ù„Ù‰ Ø§Ù„Ø®Ø¯Ù…Ø© ÙˆØ§Ù„Ù…Ø¨Ù„Øº Ù‚Ø¨Ù„ Ø¥ØµØ¯Ø§Ø± Ø¨ÙŠØ§Ù†Ø§Øª Ø§Ù„Ø¨Ø·Ø§Ù‚Ø©.",
    "Call": "Ø§ØªØµÙ„",
    "Directions": "Ø§Ù„Ø§ØªØ¬Ø§Ù‡Ø§Øª",
    "Start visit": "Ø§Ø¨Ø¯Ø£ Ø§Ù„Ø²ÙŠØ§Ø±Ø©",
    "Starting...": "Ø¬Ø§Ø±ÙŠ Ø§Ù„Ø¨Ø¯Ø¡...",
    "Your visit QR is ready. Show it at the front desk.": "QR Ø§Ù„Ø²ÙŠØ§Ø±Ø© Ø¬Ø§Ù‡Ø². Ø£Ø¸Ù‡Ø±Ù‡ Ù„Ù…ÙƒØªØ¨ Ø§Ù„Ø§Ø³ØªÙ‚Ø¨Ø§Ù„.",
    "Location access is not available in this browser.": "Ø§Ù„ÙˆØµÙˆÙ„ Ø¥Ù„Ù‰ Ø§Ù„Ù…ÙˆÙ‚Ø¹ ØºÙŠØ± Ù…ØªØ§Ø­ ÙÙŠ Ù‡Ø°Ø§ Ø§Ù„Ù…ØªØµÙØ­.",
    "Banxito is using your location only for this provider search.": "ÙŠØ³ØªØ®Ø¯Ù… Banxito Ù…ÙˆÙ‚Ø¹Ùƒ ÙÙ‚Ø· Ù„Ù‡Ø°Ø§ Ø§Ù„Ø¨Ø­Ø«.",
    "Banxito could not access your location. You can still search by category or provider name.": "Ù„Ù… ÙŠØªÙ…ÙƒÙ† Banxito Ù…Ù† Ø§Ù„ÙˆØµÙˆÙ„ Ø¥Ù„Ù‰ Ù…ÙˆÙ‚Ø¹Ùƒ. Ù„Ø§ ÙŠØ²Ø§Ù„ Ø¨Ø¥Ù…ÙƒØ§Ù†Ùƒ Ø§Ù„Ø¨Ø­Ø« Ø¨Ø§Ù„ÙØ¦Ø© Ø£Ùˆ Ø§Ø³Ù… Ø§Ù„Ù…Ù‚Ø¯Ù…."
  },
  hi: {
    "Services": "à¤¸à¥‡à¤µà¤¾à¤à¤‚",
    "Find Services": "à¤¸à¥‡à¤µà¤¾à¤à¤‚ à¤–à¥‹à¤œà¥‡à¤‚",
    "Find approved care near you": "à¤…à¤ªà¤¨à¥‡ à¤ªà¤¾à¤¸ approved care à¤–à¥‹à¤œà¥‡à¤‚",
    "Choose an available category and Banxito will show approved providers that can submit a service claim for it.": "à¤à¤• funded category à¤šà¥à¤¨à¥‡à¤‚ à¤”à¤° Banxito approved providers à¤¦à¤¿à¤–à¤¾à¤à¤—à¤¾ à¤œà¥‹ payment request à¤•à¤° à¤¸à¤•à¤¤à¥‡ à¤¹à¥ˆà¤‚à¥¤",
    "All available categories": "à¤¸à¤­à¥€ funded categories",
    "Search approved providers": "approved providers à¤–à¥‹à¤œà¥‡à¤‚",
    "Search by provider, city, or category": "provider, city, à¤¯à¤¾ category à¤¸à¥‡ à¤–à¥‹à¤œà¥‡à¤‚",
    "Open now": "à¤…à¤­à¥€ à¤–à¥à¤²à¤¾ à¤¹à¥ˆ",
    "Use my location": "à¤®à¥‡à¤°à¥€ location à¤‰à¤ªà¤¯à¥‹à¤— à¤•à¤°à¥‡à¤‚",
    "Using your location": "à¤†à¤ªà¤•à¥€ location à¤‰à¤ªà¤¯à¥‹à¤— à¤¹à¥‹ à¤°à¤¹à¥€ à¤¹à¥ˆ",
    "Map view": "Map view",
    "List view": "List view",
    "Providers": "Providers",
    "Map-ready provider list": "Map-ready provider list",
    "Approved providers": "Approved providers",
    "Map view will use these approved provider locations. Choose a provider from the list below to start a visit QR.": "Map view à¤‡à¤¨ approved provider locations à¤•à¤¾ à¤‰à¤ªà¤¯à¥‹à¤— à¤•à¤°à¥‡à¤—à¤¾à¥¤ Visit QR à¤¶à¥à¤°à¥‚ à¤•à¤°à¤¨à¥‡ à¤•à¥‡ à¤²à¤¿à¤ list à¤¸à¥‡ provider à¤šà¥à¤¨à¥‡à¤‚à¥¤",
    "Call, get directions, or start a Banxito visit QR for an approved provider.": "Approved provider à¤•à¥‡ à¤²à¤¿à¤ call à¤•à¤°à¥‡à¤‚, directions à¤²à¥‡à¤‚, à¤¯à¤¾ Banxito visit QR à¤¶à¥à¤°à¥‚ à¤•à¤°à¥‡à¤‚à¥¤",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.": "Banxito à¤•à¥‡ à¤ªà¤¾à¤¸ provider location data à¤¤à¥ˆà¤¯à¤¾à¤° à¤¹à¥ˆà¥¤ Live map à¤šà¤¾à¤²à¥‚ à¤•à¤°à¤¨à¥‡ à¤•à¥‡ à¤²à¤¿à¤ Google Maps key à¤œà¥‹à¤¡à¤¼à¥‡à¤‚à¥¤",
    "Approved provider locations will appear on the map once latitude and longitude are available.": "Latitude à¤”à¤° longitude à¤‰à¤ªà¤²à¤¬à¥à¤§ à¤¹à¥‹à¤¨à¥‡ à¤ªà¤° approved provider locations map à¤ªà¤° à¤¦à¤¿à¤–à¥‡à¤‚à¤—à¥€à¥¤",
    "Map of approved providers": "Approved providers à¤•à¤¾ map",
    "Loading approved providers": "Approved providers à¤²à¥‹à¤¡ à¤¹à¥‹ à¤°à¤¹à¥‡ à¤¹à¥ˆà¤‚",
    "Checking providers that match your available categories.": "à¤†à¤ªà¤•à¥€ funded categories à¤¸à¥‡ à¤®à¥ˆà¤š à¤•à¤°à¤¨à¥‡ à¤µà¤¾à¤²à¥‡ providers à¤šà¥‡à¤• à¤¹à¥‹ à¤°à¤¹à¥‡ à¤¹à¥ˆà¤‚à¥¤",
    "No approved providers found": "à¤•à¥‹à¤ˆ approved provider à¤¨à¤¹à¥€à¤‚ à¤®à¤¿à¤²à¤¾",
    "Try another category, search term, or turn off open-now filtering.": "à¤¦à¥‚à¤¸à¤°à¥€ category à¤¯à¤¾ search term à¤†à¤œà¤¼à¤®à¤¾à¤à¤‚, à¤¯à¤¾ open-now filter à¤¬à¤‚à¤¦ à¤•à¤°à¥‡à¤‚à¥¤",
    "Address coming soon": "Address à¤œà¤²à¥à¤¦ à¤†à¤à¤—à¤¾",
    "Closed now": "à¤…à¤­à¥€ à¤¬à¤‚à¤¦ à¤¹à¥ˆ",
    "Available for this category": "à¤‡à¤¸ category à¤•à¥‡ à¤²à¤¿à¤ available",
    "Allowance available": "Available balance",
    "Distance": "à¤¦à¥‚à¤°à¥€",
    "Nearby": "à¤ªà¤¾à¤¸ à¤®à¥‡à¤‚",
    "km away": "km à¤¦à¥‚à¤°",
    "Provider is approved for": "Provider approved à¤¹à¥ˆ",
    "This provider can submit a service claim for your selected category. Banxito still verifies the exact service and amount before any card details are issued.": "à¤¯à¤¹ provider à¤†à¤ªà¤•à¥€ selected funded category à¤¸à¥‡ payment request à¤•à¤° à¤¸à¤•à¤¤à¤¾ à¤¹à¥ˆà¥¤ Card details issue à¤¹à¥‹à¤¨à¥‡ à¤¸à¥‡ à¤ªà¤¹à¤²à¥‡ Banxito exact service à¤”à¤° amount approve à¤•à¤°à¤¤à¤¾ à¤¹à¥ˆà¥¤",
    "Call": "à¤•à¥‰à¤²",
    "Directions": "Directions",
    "Start visit": "Visit à¤¶à¥à¤°à¥‚ à¤•à¤°à¥‡à¤‚",
    "Starting...": "à¤¶à¥à¤°à¥‚ à¤¹à¥‹ à¤°à¤¹à¤¾ à¤¹à¥ˆ...",
    "Your visit QR is ready. Show it at the front desk.": "à¤†à¤ªà¤•à¤¾ visit QR à¤¤à¥ˆà¤¯à¤¾à¤° à¤¹à¥ˆà¥¤ à¤‡à¤¸à¥‡ front desk à¤ªà¤° à¤¦à¤¿à¤–à¤¾à¤à¤‚à¥¤",
    "Location access is not available in this browser.": "à¤‡à¤¸ browser à¤®à¥‡à¤‚ location access available à¤¨à¤¹à¥€à¤‚ à¤¹à¥ˆà¥¤",
    "Banxito is using your location only for this provider search.": "Banxito à¤†à¤ªà¤•à¥€ location à¤•à¤¾ à¤‰à¤ªà¤¯à¥‹à¤— à¤•à¥‡à¤µà¤² provider search à¤•à¥‡ à¤²à¤¿à¤ à¤•à¤° à¤°à¤¹à¤¾ à¤¹à¥ˆà¥¤",
    "Banxito could not access your location. You can still search by category or provider name.": "Banxito à¤†à¤ªà¤•à¥€ location access à¤¨à¤¹à¥€à¤‚ à¤•à¤° à¤¸à¤•à¤¾à¥¤ à¤†à¤ª category à¤¯à¤¾ provider name à¤¸à¥‡ search à¤•à¤° à¤¸à¤•à¤¤à¥‡ à¤¹à¥ˆà¤‚à¥¤"
  },
  vi: {
    "Services": "Dá»‹ch vá»¥",
    "Find Services": "TÃ¬m dá»‹ch vá»¥",
    "Find approved care near you": "TÃ¬m nhÃ  cung cáº¥p Ä‘Ã£ duyá»‡t gáº§n báº¡n",
    "Choose an available category and Banxito will show approved providers that can submit a service claim for it.": "Chá»n danh má»¥c cÃ²n tiá»n vÃ  Banxito sáº½ hiá»ƒn thá»‹ nhÃ  cung cáº¥p Ä‘Ã£ duyá»‡t cÃ³ thá»ƒ yÃªu cáº§u thanh toÃ¡n.",
    "All available categories": "Táº¥t cáº£ danh má»¥c cÃ²n tiá»n",
    "Search approved providers": "TÃ¬m nhÃ  cung cáº¥p Ä‘Ã£ duyá»‡t",
    "Search by provider, city, or category": "TÃ¬m theo nhÃ  cung cáº¥p, thÃ nh phá»‘ hoáº·c danh má»¥c",
    "Open now": "Äang má»Ÿ cá»­a",
    "Use my location": "DÃ¹ng vá»‹ trÃ­ cá»§a tÃ´i",
    "Using your location": "Äang dÃ¹ng vá»‹ trÃ­ cá»§a báº¡n",
    "Map view": "Xem báº£n Ä‘á»“",
    "List view": "Xem danh sÃ¡ch",
    "Providers": "NhÃ  cung cáº¥p",
    "Map-ready provider list": "Danh sÃ¡ch nhÃ  cung cáº¥p sáºµn sÃ ng cho báº£n Ä‘á»“",
    "Approved providers": "NhÃ  cung cáº¥p Ä‘Ã£ duyá»‡t",
    "Map view will use these approved provider locations. Choose a provider from the list below to start a visit QR.": "Báº£n Ä‘á»“ sáº½ dÃ¹ng cÃ¡c vá»‹ trÃ­ nhÃ  cung cáº¥p Ä‘Ã£ duyá»‡t nÃ y. Chá»n má»™t nhÃ  cung cáº¥p Ä‘á»ƒ báº¯t Ä‘áº§u QR láº§n khÃ¡m.",
    "Call, get directions, or start a Banxito visit QR for an approved provider.": "Gá»i, xem chá»‰ Ä‘Æ°á»ng, hoáº·c báº¯t Ä‘áº§u QR Banxito cho nhÃ  cung cáº¥p Ä‘Ã£ duyá»‡t.",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.": "Banxito Ä‘Ã£ cÃ³ dá»¯ liá»‡u vá»‹ trÃ­ nhÃ  cung cáº¥p. ThÃªm Google Maps key Ä‘á»ƒ báº­t báº£n Ä‘á»“.",
    "Approved provider locations will appear on the map once latitude and longitude are available.": "Vá»‹ trÃ­ nhÃ  cung cáº¥p sáº½ hiá»ƒn thá»‹ khi cÃ³ vá»¹ Ä‘á»™ vÃ  kinh Ä‘á»™.",
    "Map of approved providers": "Báº£n Ä‘á»“ nhÃ  cung cáº¥p Ä‘Ã£ duyá»‡t",
    "Loading approved providers": "Äang táº£i nhÃ  cung cáº¥p Ä‘Ã£ duyá»‡t",
    "Checking providers that match your available categories.": "Äang tÃ¬m nhÃ  cung cáº¥p phÃ¹ há»£p vá»›i danh má»¥c cÃ²n tiá»n cá»§a báº¡n.",
    "No approved providers found": "KhÃ´ng tÃ¬m tháº¥y nhÃ  cung cáº¥p Ä‘Ã£ duyá»‡t",
    "Try another category, search term, or turn off open-now filtering.": "Thá»­ danh má»¥c khÃ¡c, tá»« khÃ³a khÃ¡c, hoáº·c táº¯t lá»c Ä‘ang má»Ÿ cá»­a.",
    "Address coming soon": "Sáº½ cÃ³ Ä‘á»‹a chá»‰ sau",
    "Closed now": "Hiá»‡n Ä‘Ã£ Ä‘Ã³ng cá»­a",
    "Available for this category": "CÃ³ thá»ƒ dÃ¹ng cho danh má»¥c nÃ y",
    "Allowance available": "Sá»‘ dÆ° cÃ³ thá»ƒ dÃ¹ng",
    "Distance": "Khoáº£ng cÃ¡ch",
    "Nearby": "Gáº§n Ä‘Ã¢y",
    "km away": "km",
    "Provider is approved for": "NhÃ  cung cáº¥p Ä‘Æ°á»£c duyá»‡t cho",
    "This provider can submit a service claim for your selected category. Banxito still verifies the exact service and amount before any card details are issued.": "NhÃ  cung cáº¥p nÃ y cÃ³ thá»ƒ yÃªu cáº§u thanh toÃ¡n tá»« danh má»¥c báº¡n chá»n. Banxito váº«n duyá»‡t dá»‹ch vá»¥ vÃ  sá»‘ tiá»n chÃ­nh xÃ¡c trÆ°á»›c khi cáº¥p thÃ´ng tin tháº».",
    "Call": "Gá»i",
    "Directions": "Chá»‰ Ä‘Æ°á»ng",
    "Start visit": "Báº¯t Ä‘áº§u láº§n khÃ¡m",
    "Starting...": "Äang báº¯t Ä‘áº§u...",
    "Your visit QR is ready. Show it at the front desk.": "QR láº§n khÃ¡m Ä‘Ã£ sáºµn sÃ ng. HÃ£y Ä‘Æ°a cho quáº§y tiáº¿p nháº­n.",
    "Location access is not available in this browser.": "TrÃ¬nh duyá»‡t nÃ y khÃ´ng há»— trá»£ truy cáº­p vá»‹ trÃ­.",
    "Banxito is using your location only for this provider search.": "Banxito chá»‰ dÃ¹ng vá»‹ trÃ­ cá»§a báº¡n cho láº§n tÃ¬m kiáº¿m nÃ y.",
    "Banxito could not access your location. You can still search by category or provider name.": "Banxito khÃ´ng thá»ƒ láº¥y vá»‹ trÃ­ cá»§a báº¡n. Báº¡n váº«n cÃ³ thá»ƒ tÃ¬m theo danh má»¥c hoáº·c tÃªn nhÃ  cung cáº¥p."
  },
  pa: {
    "Services": "à¨¸à©‡à¨µà¨¾à¨µà¨¾à¨‚",
    "Find Services": "à¨¸à©‡à¨µà¨¾à¨µà¨¾à¨‚ à¨²à©±à¨­à©‹",
    "Find approved care near you": "à¨†à¨ªà¨£à©‡ à¨¨à©‡à©œà©‡ à¨®à¨¨à¨œà¨¼à©‚à¨°à¨¶à©à¨¦à¨¾ à¨¦à©‡à¨–à¨­à¨¾à¨² à¨²à©±à¨­à©‹",
    "Choose an available category and Banxito will show approved providers that can submit a service claim for it.": "à¨«à©°à¨¡ à¨•à©€à¨¤à©€ à¨¸à¨¼à©à¨°à©‡à¨£à©€ à¨šà©à¨£à©‹ à¨…à¨¤à©‡ Banxito à¨®à¨¨à¨œà¨¼à©‚à¨°à¨¶à©à¨¦à¨¾ à¨ªà©à¨°à¨¦à¨¾à¨¤à¨¾ à¨¦à¨¿à¨–à¨¾à¨à¨—à¨¾ à¨œà©‹ à¨­à©à¨—à¨¤à¨¾à¨¨ à¨®à©°à¨— à¨¸à¨•à¨¦à©‡ à¨¹à¨¨à¥¤",
    "All available categories": "à¨¸à¨¾à¨°à©€à¨†à¨‚ à¨«à©°à¨¡ à¨¸à¨¼à©à¨°à©‡à¨£à©€à¨†à¨‚",
    "Search approved providers": "à¨®à¨¨à¨œà¨¼à©‚à¨°à¨¶à©à¨¦à¨¾ à¨ªà©à¨°à¨¦à¨¾à¨¤à¨¾ à¨²à©±à¨­à©‹",
    "Search by provider, city, or category": "à¨ªà©à¨°à¨¦à¨¾à¨¤à¨¾, à¨¸à¨¼à¨¹à¨¿à¨° à¨œà¨¾à¨‚ à¨¸à¨¼à©à¨°à©‡à¨£à©€ à¨¨à¨¾à¨² à¨²à©±à¨­à©‹",
    "Open now": "à¨¹à©à¨£ à¨–à©à©±à¨²à©à¨¹à¨¾",
    "Use my location": "à¨®à©‡à¨°à©€ à¨²à©‹à¨•à©‡à¨¸à¨¼à¨¨ à¨µà¨°à¨¤à©‹",
    "Using your location": "à¨¤à©à¨¹à¨¾à¨¡à©€ à¨²à©‹à¨•à©‡à¨¸à¨¼à¨¨ à¨µà¨°à¨¤à©€ à¨œà¨¾ à¨°à¨¹à©€ à¨¹à©ˆ",
    "Map view": "à¨¨à¨•à¨¸à¨¼à¨¾ à¨µà©‡à¨–à©‹",
    "List view": "à¨²à¨¿à¨¸à¨Ÿ à¨µà©‡à¨–à©‹",
    "Providers": "à¨ªà©à¨°à¨¦à¨¾à¨¤à¨¾",
    "Map-ready provider list": "à¨¨à¨•à¨¸à¨¼à©‡ à¨²à¨ˆ à¨ªà©à¨°à¨¦à¨¾à¨¤à¨¾ à¨²à¨¿à¨¸à¨Ÿ",
    "Approved providers": "à¨®à¨¨à¨œà¨¼à©‚à¨°à¨¶à©à¨¦à¨¾ à¨ªà©à¨°à¨¦à¨¾à¨¤à¨¾",
    "Map view will use these approved provider locations. Choose a provider from the list below to start a visit QR.": "à¨¨à¨•à¨¸à¨¼à¨¾ à¨‡à¨¨à©à¨¹à¨¾à¨‚ à¨®à¨¨à¨œà¨¼à©‚à¨°à¨¶à©à¨¦à¨¾ à¨¥à¨¾à¨µà¨¾à¨‚ à¨µà¨°à¨¤à©‡à¨—à¨¾à¥¤ à¨µà¨¿à¨œà¨¼à¨¿à¨Ÿ QR à¨¶à©à¨°à©‚ à¨•à¨°à¨¨ à¨²à¨ˆ à¨²à¨¿à¨¸à¨Ÿ à¨¤à©‹à¨‚ à¨ªà©à¨°à¨¦à¨¾à¨¤à¨¾ à¨šà©à¨£à©‹à¥¤",
    "Call, get directions, or start a Banxito visit QR for an approved provider.": "à¨•à¨¾à¨² à¨•à¨°à©‹, à¨¦à¨¿à¨¶à¨¾à¨µà¨¾à¨‚ à¨²à¨µà©‹, à¨œà¨¾à¨‚ à¨®à¨¨à¨œà¨¼à©‚à¨°à¨¶à©à¨¦à¨¾ à¨ªà©à¨°à¨¦à¨¾à¨¤à¨¾ à¨²à¨ˆ Banxito QR à¨¶à©à¨°à©‚ à¨•à¨°à©‹à¥¤",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.": "Banxito à¨•à©‹à¨² à¨ªà©à¨°à¨¦à¨¾à¨¤à¨¾ à¨²à©‹à¨•à©‡à¨¸à¨¼à¨¨ à¨¡à¨¾à¨Ÿà¨¾ à¨¤à¨¿à¨†à¨° à¨¹à©ˆà¥¤ à¨²à¨¾à¨ˆà¨µ à¨¨à¨•à¨¸à¨¼à¨¾ à¨šà¨¾à¨²à©‚ à¨•à¨°à¨¨ à¨²à¨ˆ Google Maps key à¨œà©‹à©œà©‹à¥¤",
    "Approved provider locations will appear on the map once latitude and longitude are available.": "à¨œà¨¦à©‹à¨‚ latitude à¨…à¨¤à©‡ longitude à¨‰à¨ªà¨²à¨¬à¨§ à¨¹à©‹à¨£à¨—à©‡, à¨¥à¨¾à¨µà¨¾à¨‚ à¨¨à¨•à¨¸à¨¼à©‡ à¨¤à©‡ à¨†à¨‰à¨£à¨—à©€à¨†à¨‚à¥¤",
    "Map of approved providers": "à¨®à¨¨à¨œà¨¼à©‚à¨°à¨¶à©à¨¦à¨¾ à¨ªà©à¨°à¨¦à¨¾à¨¤à¨¾à¨µà¨¾à¨‚ à¨¦à¨¾ à¨¨à¨•à¨¸à¨¼à¨¾",
    "Loading approved providers": "à¨®à¨¨à¨œà¨¼à©‚à¨°à¨¶à©à¨¦à¨¾ à¨ªà©à¨°à¨¦à¨¾à¨¤à¨¾ à¨²à©‹à¨¡ à¨¹à©‹ à¨°à¨¹à©‡ à¨¹à¨¨",
    "Checking providers that match your available categories.": "à¨¤à©à¨¹à¨¾à¨¡à©€à¨†à¨‚ à¨«à©°à¨¡ à¨¸à¨¼à©à¨°à©‡à¨£à©€à¨†à¨‚ à¨¨à¨¾à¨² à¨®à©ˆà¨š à¨ªà©à¨°à¨¦à¨¾à¨¤à¨¾ à¨šà©ˆà¨• à¨¹à©‹ à¨°à¨¹à©‡ à¨¹à¨¨à¥¤",
    "No approved providers found": "à¨•à©‹à¨ˆ à¨®à¨¨à¨œà¨¼à©‚à¨°à¨¶à©à¨¦à¨¾ à¨ªà©à¨°à¨¦à¨¾à¨¤à¨¾ à¨¨à¨¹à©€à¨‚ à¨²à©±à¨­à¨¿à¨†",
    "Try another category, search term, or turn off open-now filtering.": "à¨¹à©‹à¨° à¨¸à¨¼à©à¨°à©‡à¨£à©€, à¨–à©‹à¨œ à¨¶à¨¬à¨¦, à¨œà¨¾à¨‚ open-now filter à¨¬à©°à¨¦ à¨•à¨°à©‹à¥¤",
    "Address coming soon": "à¨ªà¨¤à¨¾ à¨œà¨²à¨¦ à¨†à¨µà©‡à¨—à¨¾",
    "Closed now": "à¨¹à©à¨£ à¨¬à¨‚à¨¦",
    "Available for this category": "à¨‡à¨¸ à¨¸à¨¼à©à¨°à©‡à¨£à©€ à¨²à¨ˆ à¨‰à¨ªà¨²à¨¬à¨§",
    "Allowance available": "à¨‰à¨ªà¨²à¨¬à¨§ à¨¬à¨•à¨¾à¨‡à¨†",
    "Distance": "à¨¦à©‚à¨°à©€",
    "Nearby": "à¨¨à©‡à©œà©‡",
    "km away": "km à¨¦à©‚à¨°",
    "Provider is approved for": "à¨ªà©à¨°à¨¦à¨¾à¨¤à¨¾ à¨®à¨¨à¨œà¨¼à©‚à¨° à¨¹à©ˆ",
    "This provider can submit a service claim for your selected category. Banxito still verifies the exact service and amount before any card details are issued.": "à¨‡à¨¹ à¨ªà©à¨°à¨¦à¨¾à¨¤à¨¾ à¨¤à©à¨¹à¨¾à¨¡à©€ à¨šà©à¨£à©€ à¨«à©°à¨¡ à¨¸à¨¼à©à¨°à©‡à¨£à©€ à¨¤à©‹à¨‚ à¨­à©à¨—à¨¤à¨¾à¨¨ à¨®à©°à¨— à¨¸à¨•à¨¦à¨¾ à¨¹à©ˆà¥¤ Banxito à¨•à¨¾à¨°à¨¡ à¨µà©‡à¨°à¨µà©‡ à¨¦à©‡à¨£ à¨¤à©‹à¨‚ à¨ªà¨¹à¨¿à¨²à¨¾à¨‚ à¨¸à©‡à¨µà¨¾ à¨…à¨¤à©‡ à¨°à¨•à¨® à¨®à¨¨à¨œà¨¼à©‚à¨° à¨•à¨°à¨¦à¨¾ à¨¹à©ˆà¥¤",
    "Call": "à¨•à¨¾à¨²",
    "Directions": "à¨¦à¨¿à¨¶à¨¾à¨µà¨¾à¨‚",
    "Start visit": "à¨µà¨¿à¨œà¨¼à¨¿à¨Ÿ à¨¶à©à¨°à©‚ à¨•à¨°à©‹",
    "Starting...": "à¨¶à©à¨°à©‚ à¨¹à©‹ à¨°à¨¿à¨¹à¨¾ à¨¹à©ˆ...",
    "Your visit QR is ready. Show it at the front desk.": "à¨¤à©à¨¹à¨¾à¨¡à¨¾ à¨µà¨¿à¨œà¨¼à¨¿à¨Ÿ QR à¨¤à¨¿à¨†à¨° à¨¹à©ˆà¥¤ à¨«à¨°à©°à¨Ÿ à¨¡à©ˆà¨¸à¨• à¨¤à©‡ à¨¦à¨¿à¨–à¨¾à¨“à¥¤",
    "Location access is not available in this browser.": "à¨‡à¨¸ browser à¨µà¨¿à©±à¨š location access à¨‰à¨ªà¨²à¨¬à¨§ à¨¨à¨¹à©€à¨‚à¥¤",
    "Banxito is using your location only for this provider search.": "Banxito à¨¤à©à¨¹à¨¾à¨¡à©€ location à¨¸à¨¿à¨°à¨« à¨‡à¨¸ provider search à¨²à¨ˆ à¨µà¨°à¨¤ à¨°à¨¿à¨¹à¨¾ à¨¹à©ˆà¥¤",
    "Banxito could not access your location. You can still search by category or provider name.": "Banxito à¨¤à©à¨¹à¨¾à¨¡à©€ location access à¨¨à¨¹à©€à¨‚ à¨•à¨° à¨¸à¨•à¨¿à¨†à¥¤ à¨¤à©à¨¸à©€à¨‚ à¨…à¨œà©‡ à¨µà©€ à¨¸à¨¼à©à¨°à©‡à¨£à©€ à¨œà¨¾à¨‚ à¨ªà©à¨°à¨¦à¨¾à¨¤à¨¾ à¨¨à¨¾à¨® à¨¨à¨¾à¨² à¨²à©±à¨­ à¨¸à¨•à¨¦à©‡ à¨¹à©‹à¥¤"
  },
  sw: {
    "Services": "Huduma",
    "Find Services": "Tafuta huduma",
    "Find approved care near you": "Tafuta huduma zilizoidhinishwa karibu nawe",
    "Choose an available category and Banxito will show approved providers that can submit a service claim for it.": "Chagua aina yenye fedha na Banxito itaonyesha watoa huduma walioidhinishwa wanaoweza kuomba malipo.",
    "All available categories": "Aina zote zenye fedha",
    "Search approved providers": "Tafuta watoa huduma walioidhinishwa",
    "Search by provider, city, or category": "Tafuta kwa mtoa huduma, jiji au aina",
    "Open now": "Wazi sasa",
    "Use my location": "Tumia eneo langu",
    "Using your location": "Tunatumia eneo lako",
    "Map view": "Mwonekano wa ramani",
    "List view": "Mwonekano wa orodha",
    "Providers": "Watoa huduma",
    "Map-ready provider list": "Orodha ya watoa huduma kwa ramani",
    "Approved providers": "Watoa huduma walioidhinishwa",
    "Map view will use these approved provider locations. Choose a provider from the list below to start a visit QR.": "Ramani itatumia maeneo haya ya watoa huduma walioidhinishwa. Chagua mtoa huduma kwenye orodha kuanza QR ya ziara.",
    "Call, get directions, or start a Banxito visit QR for an approved provider.": "Piga simu, pata maelekezo, au anza QR ya Banxito kwa mtoa huduma aliyeidhinishwa.",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.": "Banxito ina data ya maeneo ya watoa huduma. Ongeza Google Maps key ili kuwezesha ramani.",
    "Approved provider locations will appear on the map once latitude and longitude are available.": "Maeneo ya watoa huduma yataonekana kwenye ramani pale latitudo na longitudo zitakapopatikana.",
    "Map of approved providers": "Ramani ya watoa huduma walioidhinishwa",
    "Loading approved providers": "Inapakia watoa huduma walioidhinishwa",
    "Checking providers that match your available categories.": "Tunakagua watoa huduma wanaolingana na aina zako zenye fedha.",
    "No approved providers found": "Hakuna watoa huduma walioidhinishwa waliopatikana",
    "Try another category, search term, or turn off open-now filtering.": "Jaribu aina nyingine, neno lingine, au zima kichujio cha wazi sasa.",
    "Address coming soon": "Anwani itakuja hivi karibuni",
    "Closed now": "Imefungwa sasa",
    "Available for this category": "Inapatikana kwa aina hii",
    "Allowance available": "Salio linalopatikana",
    "Distance": "Umbali",
    "Nearby": "Karibu",
    "km away": "km mbali",
    "Provider is approved for": "Mtoa huduma ameidhinishwa kwa",
    "This provider can submit a service claim for your selected category. Banxito still verifies the exact service and amount before any card details are issued.": "Mtoa huduma huyu anaweza kuomba malipo kutoka aina uliyochagua. Banxito bado huidhinisha huduma na kiasi kabla ya kutoa maelezo ya kadi.",
    "Call": "Piga simu",
    "Directions": "Maelekezo",
    "Start visit": "Anza ziara",
    "Starting...": "Inaanza...",
    "Your visit QR is ready. Show it at the front desk.": "QR yako ya ziara iko tayari. Ionyeshe mapokezi.",
    "Location access is not available in this browser.": "Ufikiaji wa eneo haupatikani kwenye kivinjari hiki.",
    "Banxito is using your location only for this provider search.": "Banxito inatumia eneo lako kwa utafutaji huu pekee.",
    "Banxito could not access your location. You can still search by category or provider name.": "Banxito haikuweza kufikia eneo lako. Bado unaweza kutafuta kwa aina au jina la mtoa huduma."
  }
};

for (const [language, translations] of Object.entries(recipientServiceDiscoveryTranslations) as [
  AppLanguage,
  Record<string, string>
][]) {
  recipientTranslations[language] = {
    ...recipientTranslations[language],
    ...translations
  };
}

const recipientServiceDiscoveryFollowupTranslations: Partial<Record<AppLanguage, Record<string, string>>> = {
  es: {
    "Show all approved providers": "Mostrar todos los proveedores aprobados",
    "Tip: location search works best when you are near the recipient's service area.": "Consejo: la busqueda por ubicacion funciona mejor cuando estas cerca del area de servicio del beneficiario.",
    "Using location for this search. If you are not near the recipient's service area, show all providers instead.": "Usando ubicacion para esta busqueda. Si no estas cerca del area de servicio del beneficiario, muestra todos los proveedores.",
    "Showing all approved providers for your available categories.": "Mostrando todos los proveedores aprobados para tus categorias con saldo."
  },
  fr: {
    "Show all approved providers": "Afficher tous les prestataires approuves",
    "Tip: location search works best when you are near the recipient's service area.": "Astuce : la recherche par position fonctionne mieux quand vous etes pres de la zone de service du beneficiaire.",
    "Using location for this search. If you are not near the recipient's service area, show all providers instead.": "Position utilisee pour cette recherche. Si vous n'etes pas pres de la zone de service du beneficiaire, affichez tous les prestataires.",
    "Showing all approved providers for your available categories.": "Affichage de tous les prestataires approuves pour vos categories financees."
  },
  fil: {
    "Show all approved providers": "Ipakita ang lahat ng aprubadong provider",
    "Tip: location search works best when you are near the recipient's service area.": "Tip: Pinakamahusay ang location search kapag malapit ka sa service area ng recipient.",
    "Using location for this search. If you are not near the recipient's service area, show all providers instead.": "Ginagamit ang lokasyon para sa paghahanap na ito. Kung wala ka malapit sa service area ng recipient, ipakita ang lahat ng provider.",
    "Showing all approved providers for your available categories.": "Ipinapakita ang lahat ng aprubadong provider para sa iyong may pondong kategorya."
  },
  ar: {
    "Show all approved providers": "عرض كل مقدمي الخدمة المعتمدين",
    "Tip: location search works best when you are near the recipient's service area.": "نصيحة: يعمل البحث بالموقع بشكل أفضل عندما تكون قريبا من منطقة خدمة المستفيد.",
    "Using location for this search. If you are not near the recipient's service area, show all providers instead.": "يتم استخدام الموقع لهذا البحث. إذا لم تكن قريبا من منطقة خدمة المستفيد، اعرض كل مقدمي الخدمة.",
    "Showing all approved providers for your available categories.": "يتم عرض كل مقدمي الخدمة المعتمدين لفئاتك الممولة."
  },
  hi: {
    "Show all approved providers": "सभी स्वीकृत प्रदाता दिखाएं",
    "Tip: location search works best when you are near the recipient's service area.": "सुझाव: लोकेशन खोज तब सबसे अच्छी काम करती है जब आप प्राप्तकर्ता के सेवा क्षेत्र के पास हों.",
    "Using location for this search. If you are not near the recipient's service area, show all providers instead.": "इस खोज के लिए लोकेशन का उपयोग हो रहा है. अगर आप प्राप्तकर्ता के सेवा क्षेत्र के पास नहीं हैं, तो सभी प्रदाता दिखाएं.",
    "Showing all approved providers for your available categories.": "आपकी funded categories के लिए सभी स्वीकृत प्रदाता दिखाए जा रहे हैं."
  },
  vi: {
    "Show all approved providers": "Hien thi tat ca nha cung cap da duyet",
    "Tip: location search works best when you are near the recipient's service area.": "Meo: tim theo vi tri hieu qua nhat khi ban o gan khu vuc dich vu cua nguoi nhan.",
    "Using location for this search. If you are not near the recipient's service area, show all providers instead.": "Dang dung vi tri cho lan tim kiem nay. Neu ban khong o gan khu vuc dich vu cua nguoi nhan, hay hien thi tat ca nha cung cap.",
    "Showing all approved providers for your available categories.": "Dang hien thi tat ca nha cung cap da duyet cho cac danh muc da duoc tai tro cua ban."
  },
  pa: {
    "Show all approved providers": "ਸਾਰੇ ਮਨਜ਼ੂਰਸ਼ੁਦਾ ਪ੍ਰਦਾਤਾ ਦਿਖਾਓ",
    "Tip: location search works best when you are near the recipient's service area.": "ਸੁਝਾਅ: ਲੋਕੇਸ਼ਨ ਖੋਜ ਤਦੋਂ ਵਧੀਆ ਕੰਮ ਕਰਦੀ ਹੈ ਜਦੋਂ ਤੁਸੀਂ ਪ੍ਰਾਪਤਕਰਤਾ ਦੇ ਸੇਵਾ ਖੇਤਰ ਦੇ ਨੇੜੇ ਹੋ.",
    "Using location for this search. If you are not near the recipient's service area, show all providers instead.": "ਇਸ ਖੋਜ ਲਈ ਲੋਕੇਸ਼ਨ ਵਰਤੀ ਜਾ ਰਹੀ ਹੈ. ਜੇ ਤੁਸੀਂ ਪ੍ਰਾਪਤਕਰਤਾ ਦੇ ਸੇਵਾ ਖੇਤਰ ਨੇੜੇ ਨਹੀਂ ਹੋ, ਤਾਂ ਸਾਰੇ ਪ੍ਰਦਾਤਾ ਦਿਖਾਓ.",
    "Showing all approved providers for your available categories.": "ਤੁਹਾਡੀਆਂ funded categories ਲਈ ਸਾਰੇ ਮਨਜ਼ੂਰਸ਼ੁਦਾ ਪ੍ਰਦਾਤਾ ਦਿਖਾਏ ਜਾ ਰਹੇ ਹਨ."
  },
  sw: {
    "Show all approved providers": "Onyesha watoa huduma wote walioidhinishwa",
    "Tip: location search works best when you are near the recipient's service area.": "Kidokezo: utafutaji wa eneo hufanya kazi vizuri ukiwa karibu na eneo la huduma la mpokeaji.",
    "Using location for this search. If you are not near the recipient's service area, show all providers instead.": "Tunatumia eneo kwa utafutaji huu. Kama hauko karibu na eneo la huduma la mpokeaji, onyesha watoa huduma wote.",
    "Showing all approved providers for your available categories.": "Tunaonyesha watoa huduma wote walioidhinishwa kwa aina zako zenye fedha."
  }
};

for (const [language, translations] of Object.entries(recipientServiceDiscoveryFollowupTranslations) as [
  AppLanguage,
  Record<string, string>
][]) {
  recipientTranslations[language] = {
    ...recipientTranslations[language],
    ...translations
  };
}

const recipientProviderMapCopyTranslations: Partial<Record<AppLanguage, Record<string, string>>> = {
  en: {
    "Map-ready provider list": "Provider map",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.":
      "Live map view will appear here when it is ready. You can still choose an approved provider from the list.",
    "Approved provider locations will appear on the map once latitude and longitude are available.":
      "Approved provider locations will appear on the map when available. You can use the list today.",
    "Approved provider locations will appear here as soon as Banxito has map coordinates for them.":
      "Approved provider locations will appear here when available.",
    "You can still choose an approved provider from the list below. Live map view will be available soon.":
      "You can still choose an approved provider from the list below. Live map view will be available soon."
  },
  es: {
    "Map-ready provider list": "Mapa de proveedores",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.":
      "La vista de mapa aparecera aqui cuando este lista. Mientras tanto, puedes elegir un proveedor aprobado de la lista.",
    "Approved provider locations will appear on the map once latitude and longitude are available.":
      "Las ubicaciones de proveedores aprobados apareceran en el mapa cuando esten disponibles. Puedes usar la lista hoy.",
    "Approved provider locations will appear here as soon as Banxito has map coordinates for them.":
      "Las ubicaciones de proveedores aprobados apareceran aqui cuando esten disponibles.",
    "You can still choose an approved provider from the list below. Live map view will be available soon.":
      "Aun puedes elegir un proveedor aprobado de la lista. La vista de mapa estara disponible pronto."
  },
  fr: {
    "Map-ready provider list": "Carte des prestataires",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.":
      "La carte apparaitra ici quand elle sera prete. Vous pouvez deja choisir un prestataire approuve dans la liste.",
    "Approved provider locations will appear on the map once latitude and longitude are available.":
      "Les lieux des prestataires approuves apparaitront sur la carte quand ils seront disponibles. Vous pouvez utiliser la liste aujourd'hui.",
    "Approved provider locations will appear here as soon as Banxito has map coordinates for them.":
      "Les lieux des prestataires approuves apparaitront ici quand ils seront disponibles.",
    "You can still choose an approved provider from the list below. Live map view will be available soon.":
      "Vous pouvez toujours choisir un prestataire approuve dans la liste. La carte sera bientot disponible."
  },
  fil: {
    "Map-ready provider list": "Mapa ng provider",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.":
      "Lalabas dito ang live map kapag handa na. Maaari ka pa ring pumili ng aprubadong provider mula sa listahan.",
    "Approved provider locations will appear on the map once latitude and longitude are available.":
      "Lalabas sa mapa ang lokasyon ng mga aprubadong provider kapag available na. Maaari mong gamitin ang listahan ngayon.",
    "Approved provider locations will appear here as soon as Banxito has map coordinates for them.":
      "Lalabas dito ang lokasyon ng mga aprubadong provider kapag available na.",
    "You can still choose an approved provider from the list below. Live map view will be available soon.":
      "Maaari ka pa ring pumili ng aprubadong provider mula sa listahan. Malapit nang maging available ang mapa."
  },
  ar: {
    "Map-ready provider list": "خريطة مقدمي الخدمة",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.":
      "ستظهر الخريطة هنا عندما تكون جاهزة. يمكنك الآن اختيار مقدم خدمة معتمد من القائمة.",
    "Approved provider locations will appear on the map once latitude and longitude are available.":
      "ستظهر مواقع مقدمي الخدمة المعتمدين على الخريطة عندما تتوفر. يمكنك استخدام القائمة الآن.",
    "Approved provider locations will appear here as soon as Banxito has map coordinates for them.":
      "ستظهر مواقع مقدمي الخدمة المعتمدين هنا عندما تتوفر.",
    "You can still choose an approved provider from the list below. Live map view will be available soon.":
      "يمكنك اختيار مقدم خدمة معتمد من القائمة أدناه. ستتوفر الخريطة قريبا."
  },
  hi: {
    "Map-ready provider list": "प्रदाता मानचित्र",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.":
      "लाइव मानचित्र तैयार होने पर यहां दिखाई देगा. आप अभी सूची से स्वीकृत प्रदाता चुन सकते हैं.",
    "Approved provider locations will appear on the map once latitude and longitude are available.":
      "स्वीकृत प्रदाताओं के स्थान उपलब्ध होने पर मानचित्र पर दिखाई देंगे. आप आज सूची का उपयोग कर सकते हैं.",
    "Approved provider locations will appear here as soon as Banxito has map coordinates for them.":
      "स्वीकृत प्रदाताओं के स्थान उपलब्ध होने पर यहां दिखाई देंगे.",
    "You can still choose an approved provider from the list below. Live map view will be available soon.":
      "आप अभी भी नीचे दी गई सूची से स्वीकृत प्रदाता चुन सकते हैं. लाइव मानचित्र जल्द उपलब्ध होगा."
  },
  vi: {
    "Map-ready provider list": "Ban do nha cung cap",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.":
      "Ban do truc tiep se hien thi o day khi san sang. Ban van co the chon nha cung cap da duyet trong danh sach.",
    "Approved provider locations will appear on the map once latitude and longitude are available.":
      "Vi tri nha cung cap da duyet se hien tren ban do khi co san. Ban co the dung danh sach hom nay.",
    "Approved provider locations will appear here as soon as Banxito has map coordinates for them.":
      "Vi tri nha cung cap da duyet se hien o day khi co san.",
    "You can still choose an approved provider from the list below. Live map view will be available soon.":
      "Ban van co the chon nha cung cap da duyet tu danh sach ben duoi. Ban do truc tiep se som co."
  },
  pa: {
    "Map-ready provider list": "ਪ੍ਰਦਾਤਾ ਨਕਸ਼ਾ",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.":
      "ਲਾਈਵ ਨਕਸ਼ਾ ਤਿਆਰ ਹੋਣ ਤੇ ਇੱਥੇ ਦਿਖੇਗਾ. ਤੁਸੀਂ ਹੁਣ ਸੂਚੀ ਵਿਚੋਂ ਮਨਜ਼ੂਰਸ਼ੁਦਾ ਪ੍ਰਦਾਤਾ ਚੁਣ ਸਕਦੇ ਹੋ.",
    "Approved provider locations will appear on the map once latitude and longitude are available.":
      "ਮਨਜ਼ੂਰਸ਼ੁਦਾ ਪ੍ਰਦਾਤਾਵਾਂ ਦੇ ਸਥਾਨ ਉਪਲਬਧ ਹੋਣ ਤੇ ਨਕਸ਼ੇ ਤੇ ਦਿਖਣਗੇ. ਤੁਸੀਂ ਅੱਜ ਸੂਚੀ ਵਰਤ ਸਕਦੇ ਹੋ.",
    "Approved provider locations will appear here as soon as Banxito has map coordinates for them.":
      "ਮਨਜ਼ੂਰਸ਼ੁਦਾ ਪ੍ਰਦਾਤਾਵਾਂ ਦੇ ਸਥਾਨ ਉਪਲਬਧ ਹੋਣ ਤੇ ਇੱਥੇ ਦਿਖਣਗੇ.",
    "You can still choose an approved provider from the list below. Live map view will be available soon.":
      "ਤੁਸੀਂ ਹੇਠਾਂ ਦਿੱਤੀ ਸੂਚੀ ਵਿਚੋਂ ਮਨਜ਼ੂਰਸ਼ੁਦਾ ਪ੍ਰਦਾਤਾ ਚੁਣ ਸਕਦੇ ਹੋ. ਲਾਈਵ ਨਕਸ਼ਾ ਜਲਦੀ ਉਪਲਬਧ ਹੋਵੇਗਾ."
  },
  sw: {
    "Map-ready provider list": "Ramani ya watoa huduma",
    "Banxito has provider location data ready. Add your Google Maps key to enable the live provider map.":
      "Ramani ya moja kwa moja itaonekana hapa ikiwa tayari. Bado unaweza kuchagua mtoa huduma aliyeidhinishwa kwenye orodha.",
    "Approved provider locations will appear on the map once latitude and longitude are available.":
      "Maeneo ya watoa huduma walioidhinishwa yataonekana kwenye ramani yakipatikana. Unaweza kutumia orodha leo.",
    "Approved provider locations will appear here as soon as Banxito has map coordinates for them.":
      "Maeneo ya watoa huduma walioidhinishwa yataonekana hapa yakipatikana.",
    "You can still choose an approved provider from the list below. Live map view will be available soon.":
      "Bado unaweza kuchagua mtoa huduma aliyeidhinishwa kutoka kwenye orodha. Ramani ya moja kwa moja itapatikana hivi karibuni."
  }
};

for (const [language, translations] of Object.entries(recipientProviderMapCopyTranslations) as [
  AppLanguage,
  Record<string, string>
][]) {
  recipientTranslations[language] = {
    ...recipientTranslations[language],
    ...translations
  };
}

const i18nReviewStatus = {
  ...stage2ReviewStatus,
  app: "recipient"
} as const;

type I18nContextValue = {
  language: AppLanguage;
  setLanguage: (language: AppLanguage) => void;
  t: (text: string) => string;
  format: (text: string, values: Record<string, string | number>) => string;
};

const I18nContext = React.createContext<I18nContextValue>({
  language: "en",
  setLanguage: () => undefined,
  t: (text) => text,
  format: (text, values) => formatTranslatedMessage(text, values)
});

function readLanguage(): AppLanguage {
  const saved = window.localStorage.getItem(storageKeys.language) as AppLanguage | null;
  if (saved && languageOptions.some((option) => option.value === saved)) {
    return saved;
  }

  const browserLanguage = window.navigator.language.toLowerCase();
  if (browserLanguage.startsWith("es")) return "es";
  if (browserLanguage.startsWith("fr")) return "fr";
  if (browserLanguage.startsWith("fil") || browserLanguage.startsWith("tl")) return "fil";
  if (browserLanguage.startsWith("ar")) return "ar";
  if (browserLanguage.startsWith("hi")) return "hi";
  if (browserLanguage.startsWith("vi")) return "vi";
  if (browserLanguage.startsWith("pa")) return "pa";
  if (browserLanguage.startsWith("sw")) return "sw";
  return "en";
}

function useI18n() {
  return React.useContext(I18nContext);
}

const pool = new CognitoUserPool({
  UserPoolId: config.userPoolId,
  ClientId: config.userPoolClientId
});

const countryOptions = [
  { code: "US", label: "United States" },
  { code: "MX", label: "Mexico" },
  { code: "IN", label: "India" },
  { code: "PH", label: "Philippines" },
  { code: "GH", label: "Ghana" },
  { code: "KE", label: "Kenya" }
];

const localeOptionsByCountry: Record<string, { value: string; label: string }[]> = {
  US: [{ value: "en-US", label: "English (United States)" }],
  MX: [
    { value: "es-MX", label: "Spanish (Mexico)" },
    { value: "en-MX", label: "English (Mexico)" }
  ],
  IN: [
    { value: "en-IN", label: "English (India)" },
    { value: "hi-IN", label: "Hindi (India)" }
  ],
  PH: [
    { value: "en-PH", label: "English (Philippines)" },
    { value: "fil-PH", label: "Filipino (Philippines)" }
  ],
  GH: [{ value: "en-GH", label: "English (Ghana)" }],
  KE: [
    { value: "en-KE", label: "English (Kenya)" },
    { value: "sw-KE", label: "Swahili (Kenya)" }
  ]
};

function getDefaultLocaleForCountry(countryCode: string) {
  return localeOptionsByCountry[countryCode]?.[0]?.value ?? "en-US";
}

function getCurrencyForCountry(countryCode: string) {
  const currencyByCountry: Record<string, string> = {
    US: "USD",
    MX: "MXN",
    IN: "INR",
    PH: "PHP",
    GH: "GHS",
    KE: "KES"
  };
  return currencyByCountry[countryCode] ?? "USD";
}

function getLanguageLabel(locale: string) {
  const languageCode = String(locale ?? "").split("-")[0]?.toLowerCase();
  const labels: Record<string, string> = {
    en: "English",
    es: "Spanish",
    hi: "Hindi",
    fil: "Filipino",
    sw: "Swahili"
  };
  return labels[languageCode] ?? locale;
}

function decodeJwtClaims(token: string) {
  try {
    const [, payload] = token.split(".");
    if (!payload) {
      return {};
    }
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    return JSON.parse(window.atob(padded));
  } catch {
    return {};
  }
}

function splitFullName(fullName: string) {
  const parts = String(fullName ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return {
    firstName: parts[0] ?? "",
    lastName: parts.slice(1).join(" ")
  };
}

function joinFullName(firstName: string, lastName: string) {
  return `${firstName ?? ""} ${lastName ?? ""}`.trim();
}

function normalizePhoneNumber(value: string) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return "";
  }

  const prefixed = trimmed.startsWith("+") ? trimmed : `+${trimmed}`;
  return prefixed.replace(/[^\d+]/g, "");
}

function normalizeAuthIdentifier(value: string) {
  const trimmed = String(value ?? "").trim();
  return trimmed.includes("@") ? trimmed.toLowerCase() : normalizePhoneNumber(trimmed);
}

function isUnconfirmedUserError(error: unknown) {
  const maybeError = error as { code?: string; name?: string; message?: string };
  return (
    maybeError.code === "UserNotConfirmedException" ||
    maybeError.name === "UserNotConfirmedException" ||
    String(maybeError.message ?? "").toLowerCase().includes("user is not confirmed")
  );
}

function getPasswordChecks(password: string) {
  return [
    { label: "At least 8 characters", ok: password.length >= 8 },
    { label: "1 uppercase letter", ok: /[A-Z]/.test(password) },
    { label: "1 lowercase letter", ok: /[a-z]/.test(password) },
    { label: "1 number", ok: /\d/.test(password) }
  ];
}

function buildSessionFromToken(idToken: string, fallbackEmail = "", fallbackFullName = "") {
  const claims = decodeJwtClaims(idToken) as Record<string, string | undefined>;
  return {
    idToken,
    email: claims.email ?? fallbackEmail,
    fullName: claims.name ?? claims["custom:full_name"] ?? fallbackFullName
  };
}

function persistSession(session: { idToken: string; email: string; fullName: string }) {
  window.localStorage.setItem(storageKeys.idToken, session.idToken);
  window.localStorage.setItem(storageKeys.email, session.email ?? "");
  window.localStorage.setItem(storageKeys.fullName, session.fullName ?? "");
}

function clearSession() {
  window.localStorage.removeItem(storageKeys.idToken);
  window.localStorage.removeItem(storageKeys.email);
  window.localStorage.removeItem(storageKeys.fullName);
  window.localStorage.removeItem(storageKeys.activeQrSession);
}

function readSession() {
  const idToken = window.localStorage.getItem(storageKeys.idToken);
  const email = window.localStorage.getItem(storageKeys.email) ?? "";
  const fullName = window.localStorage.getItem(storageKeys.fullName) ?? "";
  if (!idToken) {
    return null;
  }

  const claims = decodeJwtClaims(idToken) as Record<string, number | string | undefined>;
  const expiresAt = Number(claims.exp ?? 0);
  if (expiresAt && expiresAt * 1000 <= Date.now()) {
    return null;
  }

  return { idToken, email, fullName };
}

function restoreCognitoSession() {
  return new Promise<{ idToken: string; email: string; fullName: string } | null>((resolve) => {
    const currentUser = pool.getCurrentUser();
    if (!currentUser) {
      resolve(null);
      return;
    }

    currentUser.getSession((error: Error | null, sessionResult: any) => {
      if (error || !sessionResult) {
        currentUser.signOut();
        resolve(null);
        return;
      }

      const idToken = sessionResult.getIdToken().getJwtToken();
      const cachedEmail = window.localStorage.getItem(storageKeys.email) ?? currentUser.getUsername();
      const cachedFullName = window.localStorage.getItem(storageKeys.fullName) ?? "";

      resolve(buildSessionFromToken(idToken, cachedEmail, cachedFullName));
    });
  });
}

function signIn(identifier: string, password: string) {
  const normalizedIdentifier = normalizeAuthIdentifier(identifier);

  return new Promise<{ idToken: string; email: string; fullName: string }>((resolve, reject) => {
    const user = new CognitoUser({
      Username: normalizedIdentifier,
      Pool: pool
    });

    const authDetails = new AuthenticationDetails({
      Username: normalizedIdentifier,
      Password: password
    });

    user.authenticateUser(authDetails, {
      onSuccess: (result) => {
        const idToken = result.getIdToken().getJwtToken();
        const claims = decodeJwtClaims(idToken) as Record<string, string | undefined>;
        resolve({
          idToken,
          email: claims.email ?? normalizedIdentifier,
          fullName: claims.name ?? claims["custom:full_name"] ?? ""
        });
      },
      onFailure: reject,
      newPasswordRequired: () =>
        reject(new Error("Temporary-password challenge is not supported yet."))
    });
  });
}

function signUp(input: {
  email?: string;
  phone?: string;
  contactMethod: "email" | "phone";
  password: string;
  firstName: string;
  lastName: string;
}) {
  return new Promise<string>((resolve, reject) => {
    const fullName = joinFullName(input.firstName, input.lastName);
    const attributes = [
      new CognitoUserAttribute({ Name: "name", Value: fullName })
    ];

    const normalizedEmail = String(input.email ?? "").trim().toLowerCase();
    const normalizedPhone = normalizePhoneNumber(input.phone ?? "");

    if (input.contactMethod === "email" && normalizedEmail) {
      attributes.push(new CognitoUserAttribute({ Name: "email", Value: normalizedEmail }));
    }

    if (input.contactMethod === "phone" && normalizedPhone) {
      attributes.push(new CognitoUserAttribute({ Name: "phone_number", Value: normalizedPhone }));
    }

    const username = input.contactMethod === "phone" ? normalizedPhone : normalizedEmail;

    pool.signUp(username, input.password, attributes, [], (error, result) => {
      if (error) {
        reject(error);
        return;
      }
      resolve(result?.user.getUsername() ?? username);
    });
  });
}

function confirmSignUp(identifier: string, code: string) {
  const normalizedIdentifier = normalizeAuthIdentifier(identifier);

  return new Promise<string>((resolve, reject) => {
    const user = new CognitoUser({
      Username: normalizedIdentifier,
      Pool: pool
    });

    user.confirmRegistration(code, true, (error, result) => {
      if (error) {
        reject(error);
        return;
      }
      resolve(result);
    });
  });
}

function getDeliveryMessage(
  delivery: { deliveryMedium?: string; destination?: string; attributeName?: string } | null | undefined,
  fallbackMethod: "email" | "phone"
) {
  const medium = String(delivery?.deliveryMedium ?? "").toLowerCase();
  const destination = delivery?.destination ? ` to ${delivery.destination}` : "";

  if (medium === "sms") {
    return `We sent a fresh verification code by text message${destination}.`;
  }

  if (medium === "email") {
    return `We sent a fresh verification code by email${destination}.`;
  }

  return fallbackMethod === "phone"
    ? "We sent a fresh verification code by text message."
    : "We sent a fresh verification code by email.";
}

function resendConfirmationCode(identifier: string) {
  const normalizedIdentifier = normalizeAuthIdentifier(identifier);

  return new Promise<{ deliveryMedium?: string; destination?: string; attributeName?: string } | null>(
    (resolve, reject) => {
    const user = new CognitoUser({
      Username: normalizedIdentifier,
      Pool: pool
    });

    user.resendConfirmationCode((error, result) => {
      if (error) {
        reject(error);
        return;
      }
      resolve({
        deliveryMedium: result?.CodeDeliveryDetails?.DeliveryMedium,
        destination: result?.CodeDeliveryDetails?.Destination,
        attributeName: result?.CodeDeliveryDetails?.AttributeName
      });
    });
    }
  );
}

function requestPasswordReset(identifier: string) {
  const normalizedIdentifier = normalizeAuthIdentifier(identifier);

  return new Promise<{ deliveryMedium: string; destination: string }>((resolve, reject) => {
    const user = new CognitoUser({
      Username: normalizedIdentifier,
      Pool: pool
    });

    let settled = false;
    const finalize = (payload: { deliveryMedium: string; destination: string }) => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(payload);
    };

    user.forgotPassword({
      onSuccess: () =>
        finalize({
          deliveryMedium: normalizedIdentifier.includes("@") ? "email" : "sms",
          destination: normalizedIdentifier
        }),
      onFailure: reject,
      inputVerificationCode: (data) =>
        finalize({
          deliveryMedium:
            data?.CodeDeliveryDetails?.DeliveryMedium?.toLowerCase?.() ??
            data?.DeliveryMedium?.toLowerCase?.() ??
            (normalizedIdentifier.includes("@") ? "email" : "sms"),
          destination:
            data?.CodeDeliveryDetails?.Destination ??
            data?.Destination ??
            normalizedIdentifier
        })
    });
  });
}

function confirmPasswordReset(identifier: string, code: string, nextPassword: string) {
  const normalizedIdentifier = normalizeAuthIdentifier(identifier);

  return new Promise<void>((resolve, reject) => {
    const user = new CognitoUser({
      Username: normalizedIdentifier,
      Pool: pool
    });

    user.confirmPassword(code, nextPassword, {
      onSuccess: () => resolve(),
      onFailure: reject
    });
  });
}

async function apiRequest<T>(
  path: string,
  {
    method = "GET",
    token,
    body
  }: { method?: string; token?: string; body?: unknown } = {}
): Promise<T> {
  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    method,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body ? { "content-type": "application/json" } : {})
    },
    body: body ? JSON.stringify(body) : undefined
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.error ?? `Request failed with ${response.status}`);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  return payload as T;
}

function formatMoney(amountMinor: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency
  }).format(Number(amountMinor ?? 0) / 100);
}

function normalizeLabel(value: string) {
  return String(value ?? "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function translateBackendCareText(
  text: string,
  t: (text: string) => string,
  format: (text: string, values: Record<string, string | number>) => string
) {
  const direct = t(text);
  if (direct !== text) {
    return direct;
  }

  const balanceMatch = text.match(/^You have (.+) for (.+)\.$/);
  if (balanceMatch) {
    return format("You have {amount} in {category} service allowance.", {
      amount: balanceMatch[1],
      category: t(balanceMatch[2]).toLowerCase()
    });
  }

  const requestMatch = text.match(/^(.+) requested (.+) for (.+)\.$/);
  if (requestMatch) {
    return format("{provider} requested {amount} for {category}.", {
      provider: requestMatch[1],
      amount: requestMatch[2],
      category: t(requestMatch[3]).toLowerCase()
    });
  }

  return text;
}

function getFriendlyStatusLabel(status: string) {
  const labels: Record<string, string> = {
    pending_bind: "Waiting for setup",
    active: "Active",
    requested: "Requested",
    authorized: "Approved",
    completed: "Completed",
    submitted: "Submitted",
    pending: "Pending",
    scanned: "Scanned",
    expired: "Expired",
    invalidated: "Replaced"
  };

  return labels[status] ?? normalizeLabel(status);
}

type RecipientQrSession = {
  presentmentTokenId: string;
  recipientWalletId: string;
  token?: string;
  qrPayload: string;
  manualCode: string;
  status: string;
  balanceSnapshotMinor: number;
  expiresAt: string;
  currency: string;
  categoryCode: string;
  categoryName: string;
  recipientName: string;
  recipientMemberId: string;
};

function readStoredQrSession() {
  const raw = window.localStorage.getItem(storageKeys.activeQrSession);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as RecipientQrSession;
    if (!parsed?.expiresAt || new Date(parsed.expiresAt).getTime() <= Date.now()) {
      window.localStorage.removeItem(storageKeys.activeQrSession);
      return null;
    }
    return parsed;
  } catch {
    window.localStorage.removeItem(storageKeys.activeQrSession);
    return null;
  }
}

function persistQrSession(session: RecipientQrSession | null) {
  if (!session) {
    window.localStorage.removeItem(storageKeys.activeQrSession);
    return;
  }

  window.localStorage.setItem(storageKeys.activeQrSession, JSON.stringify(session));
}

function formatTimeRemaining(expiresAt: string) {
  const remainingMs = new Date(expiresAt).getTime() - Date.now();
  if (remainingMs <= 0) {
    return "Expired";
  }

  const totalSeconds = Math.floor(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")} left`;
}

function formatTranslatedMessage(template: string, values: Record<string, string | number>) {
  return Object.entries(values).reduce(
    (message, [key, value]) => message.split(`{${key}}`).join(String(value)),
    template
  );
}

type RecipientSummary = {
  recipient: {
    id: string;
    fullName: string;
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
    locale: string;
    countryCode: string;
    status: string;
    boundAt: string | null;
  };
  wallets: {
    recipientWalletId: string;
    categoryCode: string;
    categoryName: string;
    balanceMinor: number;
    reservedMinor: number;
    currency: string;
    status: string;
  }[];
  careEvents: {
    redemptionId: string;
    recipientWalletId: string;
    categoryCode: string;
    categoryName: string;
    providerName: string;
    serviceName: string | null;
    amountMinor: number;
    currency: string;
    redemptionStatus: string;
    requestedAt: string;
    authorizedAt: string | null;
    completedAt: string | null;
    receiptFileUrl: string | null;
  }[];
};

type RecipientServiceCategory = {
  recipientWalletId: string;
  categoryId: string;
  categoryCode: string;
  categoryName: string;
  balanceMinor: number;
  reservedMinor: number;
  availableMinor: number;
  currency: string;
};

type RecipientProvider = {
  providerLocationId: string;
  providerId: string;
  providerName: string;
  displayName: string;
  providerType: string;
  status: string;
  address: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    region: string | null;
    postalCode: string | null;
    countryCode: string | null;
  };
  latitude: number | null;
  longitude: number | null;
  phone: string | null;
  websiteUrl: string | null;
  mapsUrl: string | null;
  category: {
    categoryId: string;
    categoryCode: string;
    categoryName: string;
    recipientWalletId: string;
    availableMinor: number;
    currency: string;
  };
  distanceKm: number | null;
  isOpenNow: boolean;
};

function getProviderAddress(provider: RecipientProvider) {
  return [
    provider.address.line1,
    provider.address.line2,
    provider.address.city,
    provider.address.region,
    provider.address.postalCode,
    provider.address.countryCode
  ]
    .filter(Boolean)
    .join(", ");
}

function getProviderMapTarget(provider: RecipientProvider) {
  if (typeof provider.latitude === "number" && typeof provider.longitude === "number") {
    return `${provider.latitude},${provider.longitude}`;
  }

  return getProviderAddress(provider) || provider.displayName || provider.providerName;
}

type RecipientServiceVisitResponse = {
  serviceVisit: {
    id: string;
    status: string;
    createdAt: string;
  };
  presentment: RecipientQrSession;
  provider: {
    providerLocationId: string;
    providerName: string;
    displayName: string;
    categoryCode: string;
    categoryName: string;
  };
};

type CarePassportResponse = {
  recipient: {
    id: string;
    fullName: string;
    firstName: string;
    lastName: string;
    locale: string;
    countryCode: string;
    status: string;
  };
  carePassport: {
    headline: string;
    availableCare: {
      category: string;
      plainBalance: string;
      whereToUse: string;
    }[];
    frontDeskScript: string;
    qrInstructions: string;
    fallbackInstructions: string[];
    reminders: {
      title: string;
      body: string;
      priority: "low" | "medium" | "high";
    }[];
    riskFlags: {
      code: string;
      severity: "low" | "medium" | "high";
      message: string;
    }[];
  };
  ai: {
    mode: string;
    provider: string;
    promptId: string;
    promptVersion: string;
    riskLevel: string;
    humanReviewRequired: boolean;
    humanReviewRequiredFor: string[];
    disclosure: string;
  };
};

type RecipientRoute = "home" | "passport" | "services" | "wallets" | "activity" | "help" | "me" | "qr";

export default function App() {
  const [language, setLanguage] = useState<AppLanguage>(() => readLanguage());
  const [session, setSession] = useState(() => readSession());
  const [summary, setSummary] = useState<RecipientSummary | null>(null);
  const [carePassport, setCarePassport] = useState<CarePassportResponse | null>(null);
  const [route, setRoute] = useState<RecipientRoute>("home");
  const [status, setStatus] = useState({ type: "idle", message: "" });
  const [authNotice, setAuthNotice] = useState("");
  const [restoringSession, setRestoringSession] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingCarePassport, setLoadingCarePassport] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [issuingQr, setIssuingQr] = useState(false);
  const [serviceCategories, setServiceCategories] = useState<RecipientServiceCategory[]>([]);
  const [providers, setProviders] = useState<RecipientProvider[]>([]);
  const [selectedServiceCategory, setSelectedServiceCategory] = useState("");
  const [providerSearch, setProviderSearch] = useState("");
  const [openNowOnly, setOpenNowOnly] = useState(false);
  const [serviceViewMode, setServiceViewMode] = useState<"list" | "map">("list");
  const [serviceCoords, setServiceCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [startingVisitFor, setStartingVisitFor] = useState("");
  const [activeQrSession, setActiveQrSession] = useState<RecipientQrSession | null>(
    () => readStoredQrSession()
  );
  const [bindCode, setBindCode] = useState(
    () => window.localStorage.getItem(storageKeys.pendingBindCode) ?? ""
  );

  const wallets = summary?.wallets ?? [];
  const careEvents = summary?.careEvents ?? [];
  const qrEligibleAllowances = useMemo(
    () =>
      wallets.filter(
        (wallet) =>
          wallet.status === "active" &&
          Number(wallet.balanceMinor ?? 0) - Number(wallet.reservedMinor ?? 0) > 0
      ),
    [wallets]
  );

  const totalAvailableMinor = useMemo(
    () => wallets.reduce((sum, wallet) => sum + Number(wallet.balanceMinor ?? 0), 0),
    [wallets]
  );

  function isUnauthorizedError(error: unknown) {
    return (error as { status?: number } | undefined)?.status === 401;
  }

  function clearRecipientState() {
    clearSession();
    setSession(null);
    setSummary(null);
    setCarePassport(null);
    setServiceCategories([]);
    setProviders([]);
    setSelectedServiceCategory("");
    setProviderSearch("");
    setOpenNowOnly(false);
    setServiceCoords(null);
    setRoute("home");
    setActiveQrSession(null);
    setStatus({ type: "idle", message: "" });
  }

  function clearActiveQrSession() {
    persistQrSession(null);
    setActiveQrSession(null);
  }

  async function restorePersistentSession() {
    const restoredSession = await restoreCognitoSession();
    if (restoredSession?.idToken) {
      persistSession(restoredSession);
      setSession(restoredSession);
      setAuthNotice("");
      return restoredSession;
    }

    clearRecipientState();
    return null;
  }

  async function handleUnauthorizedSession() {
    const restoredSession = await restorePersistentSession();
    if (restoredSession?.idToken) {
      return restoredSession;
    }

    setAuthNotice("Your Banxito session expired. Please sign in again.");
    return null;
  }

  async function runWithSessionRetry<T>(
    operation: (activeSession: { idToken: string; email: string; fullName: string }) => Promise<T>
  ) {
    const activeSession = session?.idToken ? session : await restorePersistentSession();
    if (!activeSession?.idToken) {
      const error = new Error("Please sign in to continue.");
      (error as Error & { status?: number }).status = 401;
      throw error;
    }

    try {
      return await operation(activeSession);
    } catch (error) {
      if (!isUnauthorizedError(error)) {
        throw error;
      }

      const refreshedSession = await handleUnauthorizedSession();
      if (!refreshedSession?.idToken || refreshedSession.idToken === activeSession.idToken) {
        const authError = new Error("Your Banxito session expired. Please sign in again.");
        (authError as Error & { status?: number }).status = 401;
        throw authError;
      }

      return operation(refreshedSession);
    }
  }

  async function loadSummary() {
    setLoadingSummary(true);
    try {
      const response = await runWithSessionRetry((activeSession) =>
        apiRequest<RecipientSummary>("/wallets", {
          token: activeSession.idToken
        })
      );
      setSummary(response);
      setStatus((current) => (current.type === "error" ? { type: "idle", message: "" } : current));
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return;
      }

      if ((error as Error).message === "Recipient profile not found for authenticated user.") {
        setSummary(null);
      } else {
        setStatus({ type: "error", message: (error as Error).message });
      }
    } finally {
      setLoadingSummary(false);
    }
  }

  async function loadCarePassport() {
    setLoadingCarePassport(true);
    try {
      const response = await runWithSessionRetry((activeSession) =>
        apiRequest<CarePassportResponse>("/recipients/me/care-passport", {
          token: activeSession.idToken
        })
      );
      setCarePassport(response);
      setStatus((current) => (current.type === "error" ? { type: "idle", message: "" } : current));
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return;
      }
      setStatus({ type: "error", message: (error as Error).message });
    } finally {
      setLoadingCarePassport(false);
    }
  }

  async function loadServiceCategories() {
    try {
      const response = await runWithSessionRetry((activeSession) =>
        apiRequest<{ categories: RecipientServiceCategory[] }>("/recipients/me/service-categories", {
          token: activeSession.idToken
        })
      );
      setServiceCategories(response.categories);
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return;
      }
      setStatus({ type: "error", message: (error as Error).message });
    }
  }

  async function loadProviders() {
    setLoadingServices(true);
    try {
      const params = new URLSearchParams();
      if (selectedServiceCategory) params.set("category", selectedServiceCategory);
      if (providerSearch.trim()) params.set("search", providerSearch.trim());
      if (openNowOnly) params.set("openNow", "true");
      if (serviceCoords) {
        params.set("lat", String(serviceCoords.latitude));
        params.set("lng", String(serviceCoords.longitude));
        params.set("radiusKm", "50");
      }
      params.set("limit", "50");

      const response = await runWithSessionRetry((activeSession) =>
        apiRequest<{ providers: RecipientProvider[] }>(`/recipients/me/providers?${params.toString()}`, {
          token: activeSession.idToken
        })
      );
      setProviders(response.providers);
      setStatus((current) => (current.type === "error" ? { type: "idle", message: "" } : current));
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return;
      }
      setStatus({ type: "error", message: (error as Error).message });
    } finally {
      setLoadingServices(false);
    }
  }

  function handleUseLocationForServices() {
    if (!navigator.geolocation) {
      setStatus({ type: "error", message: "Location access is not available in this browser." });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setServiceCoords({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setStatus({
          type: "success",
          message: "Banxito is using your location only for this provider search."
        });
      },
      () => {
        setStatus({
          type: "error",
          message: "Banxito could not access your location. You can still search by category or provider name."
        });
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  }

  function handleClearServiceFilters() {
    setSelectedServiceCategory("");
    setProviderSearch("");
    setOpenNowOnly(false);
    setServiceCoords(null);
    setServiceViewMode("list");
    setStatus({
      type: "success",
      message: "Showing all approved providers for your available categories."
    });
  }

  async function handleStartServiceVisit(provider: RecipientProvider) {
    setStartingVisitFor(provider.providerLocationId);
    try {
      const response = await runWithSessionRetry((activeSession) =>
        apiRequest<RecipientServiceVisitResponse>("/recipients/me/service-visits", {
          method: "POST",
          token: activeSession.idToken,
          body: {
            providerLocationId: provider.providerLocationId,
            recipientWalletId: provider.category.recipientWalletId,
            categoryCode: provider.category.categoryCode
          }
        })
      );

      persistQrSession(response.presentment);
      setActiveQrSession(response.presentment);
      setRoute("qr");
      setStatus({
        type: "success",
        message: "Your visit QR is ready. Show it at the front desk."
      });
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return;
      }
      setStatus({ type: "error", message: (error as Error).message });
    } finally {
      setStartingVisitFor("");
    }
  }

  async function handleIssueQr(recipientWalletId: string) {
    setIssuingQr(true);
    try {
      const nextSession = await runWithSessionRetry((activeSession) =>
        apiRequest<RecipientQrSession>("/recipient-presentment-tokens", {
          method: "POST",
          token: activeSession.idToken,
          body: {
            recipientWalletId
          }
        })
      );

      persistQrSession(nextSession);
      setActiveQrSession(nextSession);
      setRoute("qr");
      setStatus({
        type: "success",
        message: "Your Banxito provider QR and manual code are ready."
      });
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return;
      }

      setStatus({ type: "error", message: (error as Error).message });
    } finally {
      setIssuingQr(false);
    }
  }

  async function handleBindRecipient(submittedCode?: string) {
    const normalizedCode = (submittedCode ?? bindCode).trim().toUpperCase();
    if (!normalizedCode) {
      setStatus({ type: "error", message: "Enter the setup code from your sponsor." });
      return;
    }

    const pendingProfile = JSON.parse(
      window.localStorage.getItem(storageKeys.pendingSignupProfile) ?? "{}"
    ) as {
      locale?: string;
      email?: string;
      phone?: string;
    };

    if (!session?.idToken) {
      setStatus({
        type: "error",
        message: "Create the recipient's own account before connecting this setup code."
      });
      return;
    }

    try {
      await runWithSessionRetry((activeSession) =>
        apiRequest("/recipients/bind", {
          method: "POST",
          token: activeSession.idToken,
          body: {
            invitationCode: normalizedCode,
            locale: pendingProfile?.locale ?? undefined,
            email: pendingProfile?.email ?? undefined,
            phone: pendingProfile?.phone ?? undefined
          }
        })
      );

      window.localStorage.removeItem(storageKeys.pendingBindCode);
      setBindCode("");
      setStatus({
        type: "success",
        message: "Your Banxito account is connected. Your sponsored service access is ready."
      });
      await loadSummary();
      setRoute("wallets");
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return;
      }
      setStatus({ type: "error", message: (error as Error).message });
    }
  }

  useEffect(() => {
    let isMounted = true;

    async function bootstrapSession() {
      try {
        const restored = await restorePersistentSession();
        if (restored?.idToken) {
          const pendingCode = window.localStorage.getItem(storageKeys.pendingBindCode);
          if (pendingCode) {
            setBindCode(pendingCode);
          }
        }
      } finally {
        if (isMounted) {
          setRestoringSession(false);
        }
      }
    }

    void bootstrapSession();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (restoringSession || !session?.idToken) {
      return;
    }
    void loadSummary();
  }, [restoringSession, session]);

  useEffect(() => {
    if (restoringSession || !session?.idToken || route !== "passport") {
      return;
    }
    void loadCarePassport();
  }, [restoringSession, session, route]);

  useEffect(() => {
    if (restoringSession || !session?.idToken || route !== "services") {
      return;
    }
    void loadServiceCategories();
  }, [restoringSession, session, route]);

  useEffect(() => {
    if (restoringSession || !session?.idToken || route !== "services") {
      return;
    }
    void loadProviders();
  }, [restoringSession, session, route, selectedServiceCategory, providerSearch, openNowOnly, serviceCoords]);

  useEffect(() => {
    if (!activeQrSession) {
      return;
    }

    if (new Date(activeQrSession.expiresAt).getTime() <= Date.now()) {
      clearActiveQrSession();
      setStatus({
        type: "error",
        message: "Your Banxito visit QR expired. Generate a fresh one when you are ready at the provider."
      });
      return;
    }

    const timer = window.setTimeout(() => {
      clearActiveQrSession();
      setStatus({
        type: "error",
        message: "Your Banxito visit QR expired. Generate a fresh one when you are ready at the provider."
      });
    }, new Date(activeQrSession.expiresAt).getTime() - Date.now());

    return () => window.clearTimeout(timer);
  }, [activeQrSession]);

  useEffect(() => {
    if (!summary || !activeQrSession) {
      return;
    }

    const walletStillAvailable = summary.wallets.some(
      (wallet) =>
        wallet.recipientWalletId === activeQrSession.recipientWalletId &&
        wallet.status === "active" &&
        Number(wallet.balanceMinor ?? 0) - Number(wallet.reservedMinor ?? 0) > 0
    );

    if (!walletStillAvailable) {
      clearActiveQrSession();
    }
  }, [summary, activeQrSession]);

  const pendingBindCode = window.localStorage.getItem(storageKeys.pendingBindCode) ?? "";

  useEffect(() => {
    window.localStorage.setItem(storageKeys.language, language);
    document.documentElement.lang = language;
    document.documentElement.dir = rtlLanguages.has(language) ? "rtl" : "ltr";
    document.documentElement.dataset.banxitoI18nBundle = i18nReviewStatus.activeBundle;
    document.documentElement.dataset.banxitoI18nReview = i18nReviewStatus.reviewLabel;
    document.documentElement.dataset.banxitoI18nProductionReady = String(
      i18nReviewStatus.productionReady
    );
  }, [language]);

  const i18nValue = useMemo<I18nContextValue>(
    () => ({
      language,
      setLanguage,
      t: (text: string) => translateRecipientString(language, text),
      format: (text: string, values: Record<string, string | number>) =>
        formatTranslatedMessage(translateRecipientString(language, text), values)
    }),
    [language]
  );

  function withI18n(children: React.ReactNode) {
    return <I18nContext.Provider value={i18nValue}>{children}</I18nContext.Provider>;
  }

  if (restoringSession) {
    return withI18n(
      <Shell>
        <AuthCard
          title="Restoring your Banxito access"
          description="Checking your saved session and loading your sponsored account."
        />
      </Shell>
    );
  }

  if (!session?.idToken) {
    return withI18n(
      <Shell>
        <AuthExperience
          notice={authNotice}
          onAuthenticated={(nextSession) => {
            persistSession(nextSession);
            setSession(nextSession);
            setStatus({ type: "success", message: "Signed in to Banxito Recipient." });
          }}
          onBindCodeCaptured={(code) => {
            window.localStorage.setItem(storageKeys.pendingBindCode, code);
            setBindCode(code);
          }}
        />
      </Shell>
    );
  }

  if (!summary?.recipient) {
    return withI18n(
      <Shell>
        <Header
          title={session.fullName || session.email}
          subtitle="Banxito Recipient"
          onSignOut={() => {
            pool.getCurrentUser()?.signOut();
            clearRecipientState();
          }}
        />
        <StatusBanner status={status} />
        <BindCard
          code={bindCode || pendingBindCode}
          onChange={(value) => {
            const normalized = value.toUpperCase();
            setBindCode(normalized);
            window.localStorage.setItem(storageKeys.pendingBindCode, normalized);
          }}
          onSubmit={() => void handleBindRecipient(bindCode || pendingBindCode)}
          onCreateAccount={() => {
            const normalized = (bindCode || pendingBindCode).trim().toUpperCase();
            if (normalized) {
              window.localStorage.setItem(storageKeys.pendingBindCode, normalized);
            }
            pool.getCurrentUser()?.signOut();
            clearRecipientState();
            setAuthNotice("Create the recipient's own account to use this setup code.");
          }}
        />
      </Shell>
    );
  }

  return withI18n(
    <Shell>
      <Header
        title={summary.recipient.firstName || summary.recipient.fullName}
        subtitle="Banxito Recipient"
        onSignOut={() => {
          pool.getCurrentUser()?.signOut();
          clearRecipientState();
        }}
      />

      <StatusBanner status={status} />

      {route === "home" ? (
        <HomeScreen
          summary={summary}
          totalAvailableMinor={totalAvailableMinor}
          loading={loadingSummary}
          qrEligibleAllowances={qrEligibleAllowances}
          activeQrSession={activeQrSession}
          onGoAllowances={() => setRoute("wallets")}
          onGoActivity={() => setRoute("activity")}
          onOpenQr={() => setRoute("qr")}
          onIssueQr={(recipientWalletId) => void handleIssueQr(recipientWalletId)}
          issuingQr={issuingQr}
        />
      ) : null}

      {route === "wallets" ? <WalletScreen wallets={wallets} /> : null}
      {route === "services" ? (
        <ServicesScreen
          categories={serviceCategories}
          providers={providers}
          loading={loadingServices}
          selectedCategory={selectedServiceCategory}
          onCategoryChange={setSelectedServiceCategory}
          search={providerSearch}
          onSearchChange={setProviderSearch}
          openNowOnly={openNowOnly}
          onOpenNowChange={setOpenNowOnly}
          viewMode={serviceViewMode}
          onViewModeChange={setServiceViewMode}
          hasLocation={Boolean(serviceCoords)}
          onUseLocation={handleUseLocationForServices}
          onClearFilters={handleClearServiceFilters}
          onStartVisit={(provider) => void handleStartServiceVisit(provider)}
          startingVisitFor={startingVisitFor}
        />
      ) : null}
      {route === "passport" ? (
        <CarePassportScreen
          carePassport={carePassport}
          loading={loadingCarePassport}
          qrEligibleAllowances={qrEligibleAllowances}
          onRefresh={() => void loadCarePassport()}
          onOpenQr={() => setRoute("qr")}
          onIssueQr={(recipientWalletId) => void handleIssueQr(recipientWalletId)}
          issuingQr={issuingQr}
        />
      ) : null}
      {route === "activity" ? <ActivityScreen careEvents={careEvents} /> : null}
      {route === "help" ? <HelpScreen /> : null}
      {route === "me" ? <ProfileScreen summary={summary} /> : null}
      {route === "qr" ? (
        <QrScreen
          session={activeQrSession}
          wallets={qrEligibleAllowances}
          onGenerate={(recipientWalletId) => void handleIssueQr(recipientWalletId)}
          onRefresh={() =>
            activeQrSession ? void handleIssueQr(activeQrSession.recipientWalletId) : undefined
          }
          issuingQr={issuingQr}
        />
      ) : null}

      <BottomNav route={route} onChange={setRoute} />
    </Shell>
  );
}

function AuthExperience({
  notice,
  onAuthenticated,
  onBindCodeCaptured
}: {
  notice: string;
  onAuthenticated: (session: { idToken: string; email: string; fullName: string }) => void;
  onBindCodeCaptured: (code: string) => void;
}) {
  const { t } = useI18n();
  const pendingSetupCode = window.localStorage.getItem(storageKeys.pendingBindCode) ?? "";
  const [mode, setMode] = useState<"signin" | "signup">(() =>
    pendingSetupCode ? "signup" : "signin"
  );
  const [authStep, setAuthStep] = useState<"auth" | "confirm" | "recoverRequest" | "recoverConfirm">("auth");
  const [contactMethod, setContactMethod] = useState<"email" | "phone">("email");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
  const [pendingConfirmationIdentifier, setPendingConfirmationIdentifier] = useState("");
  const [passwordResetDelivery, setPasswordResetDelivery] = useState<{
    deliveryMedium: string;
    destination: string;
  } | null>(null);
  const [smsConsentChecked, setSmsConsentChecked] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    signInId: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    sponsorCode: pendingSetupCode,
    countryCode: "US",
    locale: getDefaultLocaleForCountry("US"),
    code: "",
    recoveryId: "",
    resetCode: "",
    resetPassword: "",
    resetConfirmPassword: ""
  });
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const passwordChecks = getPasswordChecks(form.password);
  const passwordIsStrong = passwordChecks.every((check) => check.ok);
  const resetPasswordChecks = getPasswordChecks(form.resetPassword);
  const resetPasswordIsStrong = resetPasswordChecks.every((check) => check.ok);

  function resetToSignIn(nextStatus = "") {
    setMode("signin");
    setAuthStep("auth");
    setStatus(nextStatus);
    setPasswordResetDelivery(null);
    setForm((current) => ({
      ...current,
      password: "",
      confirmPassword: "",
      code: "",
      resetCode: "",
      resetPassword: "",
      resetConfirmPassword: ""
    }));
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setStatus("");

    try {
      if (mode === "signup") {
        const normalizedEmail = form.email.trim().toLowerCase();
        const normalizedPhone = normalizePhoneNumber(form.phone);

        if (contactMethod === "email" && !normalizedEmail) {
          throw new Error("Enter an email address to create your account.");
        }

        if (contactMethod === "phone" && !normalizedPhone) {
          throw new Error("Enter a mobile phone number to create your account.");
        }

        if (contactMethod === "phone" && !smsConsentChecked) {
          throw new Error("Please agree to receive Banxito verification texts before continuing.");
        }

        if (!passwordIsStrong) {
          throw new Error("Choose a stronger password before continuing.");
        }

        if (form.password !== form.confirmPassword) {
          throw new Error("Passwords do not match.");
        }

        const pendingProfile = {
          firstName: form.firstName,
          lastName: form.lastName,
          email: normalizedEmail,
          phone: "",
          locale: form.locale,
          countryCode: form.countryCode
        };
        window.localStorage.setItem(storageKeys.pendingSignupProfile, JSON.stringify(pendingProfile));
        if (form.sponsorCode.trim()) {
          const normalized = form.sponsorCode.trim().toUpperCase();
          window.localStorage.setItem(storageKeys.pendingBindCode, normalized);
          onBindCodeCaptured(normalized);
        }

        const createdUsername = await signUp({
          email: normalizedEmail,
          phone: "",
          contactMethod: "email",
          password: form.password,
          firstName: form.firstName,
          lastName: form.lastName
        });

        setPendingConfirmationIdentifier(createdUsername);

        setAuthStep("confirm");
        setStatus(
          contactMethod === "phone"
            ? "Check your text messages for the verification code, then confirm your account."
            : "Check your email for the verification code, then confirm your account."
        );
      } else {
        try {
          const session = await signIn(form.signInId, form.password);
          onAuthenticated(session);
        } catch (error) {
          if (!isUnconfirmedUserError(error)) {
            throw error;
          }

          const normalizedIdentifier = normalizeAuthIdentifier(form.signInId);
          const nextContactMethod = normalizedIdentifier.includes("@") ? "email" : "phone";
          setContactMethod(nextContactMethod);
          setPendingConfirmationIdentifier(normalizedIdentifier);
          setForm((current) => ({
            ...current,
            email: nextContactMethod === "email" ? normalizedIdentifier : current.email,
            phone: nextContactMethod === "phone" ? normalizedIdentifier : current.phone
          }));
          setAuthStep("confirm");
          setStatus(
            nextContactMethod === "phone"
              ? "This account still needs its text verification code. You can resend it below."
              : "This account still needs its email verification code. You can resend it below."
          );
        }
      }
    } catch (error) {
      setStatus((error as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setStatus("");
    try {
      const confirmationTarget =
        contactMethod === "phone" ? normalizePhoneNumber(form.phone) : form.email.trim().toLowerCase();
      await confirmSignUp(confirmationTarget, form.code);
      const session = await signIn(confirmationTarget, form.password);
      onAuthenticated(session);
    } catch (error) {
      setStatus((error as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResendCode() {
    setSubmitting(true);
    setStatus("");
    try {
      const confirmationTarget =
        pendingConfirmationIdentifier ||
        (contactMethod === "phone"
          ? normalizePhoneNumber(form.phone)
          : form.email.trim().toLowerCase());

      const delivery = await resendConfirmationCode(confirmationTarget);
      setStatus(getDeliveryMessage(delivery, contactMethod));
    } catch (error) {
      setStatus((error as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Get your Banxito account ready"
      description="Sign in to view your sponsored service allowances or create your recipient account using the setup code from your sponsor."
    >
      {notice ? <StatusBanner status={{ type: "error", message: notice }} /> : null}
      <div style={styles.segment}>
        <button
          type="button"
          style={mode === "signin" ? styles.segmentActive : styles.segmentButton}
        onClick={() => {
            resetToSignIn("");
          }}
        >
          {t("Sign in")}
        </button>
        <button
          type="button"
          style={mode === "signup" ? styles.segmentActive : styles.segmentButton}
          onClick={() => {
            setMode("signup");
            setAuthStep("auth");
            setContactMethod("email");
            setStatus("");
            setPasswordResetDelivery(null);
          }}
        >
          {t("Create account")}
        </button>
      </div>

      {authStep === "confirm" ? (
        <form style={styles.form} onSubmit={handleConfirm}>
          <div style={styles.confirmationCallout}>
            <strong style={styles.confirmationTitle}>
              {t(
                contactMethod === "phone"
                  ? "We’ll text a verification code to this mobile number."
                  : "We’ll email a verification code to this address."
              )}
            </strong>
            <p style={styles.helperMuted}>
              {contactMethod === "phone"
                ? normalizePhoneNumber(form.phone) || t("Enter a valid mobile number during signup.")
                : form.email || t("Enter a valid email address during signup.")}
            </p>
          </div>
          <Field
            label="Verification code"
            value={form.code}
            onChange={(value) => setForm((current) => ({ ...current, code: value }))}
            helper={
              contactMethod === "phone"
                ? "Open your text messages and enter the code Banxito sent to this mobile number."
                : "Open your inbox and enter the code Banxito emailed to you."
            }
          />
          <PrimaryButton title={submitting ? "Confirming..." : "Confirm account"} />
          <GhostButton
            title={submitting ? "Sending..." : "Resend code"}
            onClick={() => void handleResendCode()}
          />
        </form>
      ) : authStep === "recoverRequest" ? (
        <form style={styles.form} onSubmit={(event) => void handleResetRequest(event)}>
          <Field
            label="Email or mobile phone"
            type="text"
            autoComplete="username"
            value={form.recoveryId}
            onChange={(value) => setForm((current) => ({ ...current, recoveryId: value }))}
            helper="Banxito will send a password reset code to the email address or mobile number on this account."
          />
          <PrimaryButton title={submitting ? "Sending..." : "Send reset code"} />
          <GhostButton title="Back to sign in" onClick={() => resetToSignIn("")} />
        </form>
      ) : authStep === "recoverConfirm" ? (
        <form style={styles.form} onSubmit={(event) => void handleResetConfirm(event)}>
          <div style={styles.confirmationCallout}>
            <strong style={styles.confirmationTitle}>
              {passwordResetDelivery?.deliveryMedium === "sms"
                ? "We texted a password reset code to this mobile number."
                : "We emailed a password reset code to this address."}
            </strong>
            <p style={styles.helperMuted}>
              {passwordResetDelivery?.destination || form.recoveryId || "Use the account contact you entered above."}
            </p>
          </div>
          <Field
            label="Reset code"
            value={form.resetCode}
            onChange={(value) => setForm((current) => ({ ...current, resetCode: value }))}
            helper={
              passwordResetDelivery?.deliveryMedium === "sms"
                ? "Open your text messages and enter the code Banxito sent."
                : "Open your inbox and enter the code Banxito sent."
            }
          />
          <Field
            label="New password"
            type={showResetPassword ? "text" : "password"}
            autoComplete="new-password"
            value={form.resetPassword}
            onChange={(value) => setForm((current) => ({ ...current, resetPassword: value }))}
            trailingAction={{
              label: showResetPassword ? "Hide" : "Show",
              onClick: () => setShowResetPassword((current) => !current)
            }}
          />
          <PasswordChecklist checks={resetPasswordChecks} />
          <Field
            label="Confirm new password"
            type={showResetConfirmPassword ? "text" : "password"}
            autoComplete="new-password"
            value={form.resetConfirmPassword}
            onChange={(value) =>
              setForm((current) => ({ ...current, resetConfirmPassword: value }))
            }
            trailingAction={{
              label: showResetConfirmPassword ? "Hide" : "Show",
              onClick: () => setShowResetConfirmPassword((current) => !current)
            }}
          />
          <PrimaryButton title={submitting ? "Updating..." : "Update password"} />
          <GhostButton
            title={submitting ? "Sending..." : "Send code again"}
            onClick={() => {
              setAuthStep("recoverRequest");
              setStatus("");
            }}
          />
        </form>
      ) : (
        <form style={styles.form} onSubmit={handleSubmit}>
          {mode === "signup" ? (
            <>
              <TwoCol>
                <Field
                  label="First name"
                  value={form.firstName}
                  onChange={(value) => setForm((current) => ({ ...current, firstName: value }))}
                />
                <Field
                  label="Last name"
                  value={form.lastName}
                  onChange={(value) => setForm((current) => ({ ...current, lastName: value }))}
                />
              </TwoCol>
              <TwoCol>
                <SelectField
                  label="Country"
                  value={form.countryCode}
                  onChange={(value) =>
                    setForm((current) => ({
                      ...current,
                      countryCode: value,
                      locale: getDefaultLocaleForCountry(value)
                    }))
                  }
                  options={countryOptions.map((country) => ({
                    value: country.code,
                    label: country.label
                  }))}
                />
                <SelectField
                  label="Language"
                  value={form.locale}
                  onChange={(value) => setForm((current) => ({ ...current, locale: value }))}
                  options={(localeOptionsByCountry[form.countryCode] ?? []).map((locale) => ({
                    value: locale.value,
                    label: locale.label
                  }))}
                />
              </TwoCol>
              <Field
                label="Sponsor setup code"
                value={form.sponsorCode}
                onChange={(value) =>
                  setForm((current) => ({ ...current, sponsorCode: value.toUpperCase() }))
                }
              />
              <Field
                label="Email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(value) =>
                  setForm((current) => ({ ...current, email: value.toLowerCase(), phone: "" }))
                }
                helper="We will send the verification code here."
              />
            </>
          ) : null}

          {mode === "signin" ? (
            <Field
              label="Email or mobile phone"
              type="text"
              autoComplete="username"
              value={form.signInId}
              onChange={(value) => setForm((current) => ({ ...current, signInId: value }))}
              helper="Sign in with the email address or mobile number you used to set up Banxito."
            />
          ) : null}
          <Field
            label="Password"
            type={showPassword ? "text" : "password"}
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
            value={form.password}
            onChange={(value) => setForm((current) => ({ ...current, password: value }))}
            helper={mode === "signup" ? undefined : "Tap Show if you want to verify what you typed."}
            trailingAction={{
              label: showPassword ? "Hide" : "Show",
              onClick: () => setShowPassword((current) => !current)
            }}
          />
          {mode === "signup" ? (
            <PasswordChecklist checks={passwordChecks} />
          ) : null}
          {mode === "signup" ? (
            <Field
              label="Confirm password"
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={(value) =>
                setForm((current) => ({ ...current, confirmPassword: value }))
              }
              trailingAction={{
                label: showConfirmPassword ? "Hide" : "Show",
                onClick: () => setShowConfirmPassword((current) => !current)
              }}
            />
          ) : null}
          <PrimaryButton
            title={
              submitting ? "Working..." : mode === "signup" ? "Create account" : "Sign in"
            }
          />
          {mode === "signin" ? (
            <GhostButton
              title="Forgot password?"
              onClick={() => {
                setAuthStep("recoverRequest");
                setStatus("");
                setPasswordResetDelivery(null);
                setForm((current) => ({
                  ...current,
                  recoveryId: current.recoveryId || current.signInId
                }));
              }}
            />
          ) : null}
        </form>
      )}

      {status ? <p style={styles.helperText}>{status}</p> : null}
    </AuthCard>
  );

  async function handleResetRequest(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setStatus("");

    try {
      const recoveryId = normalizeAuthIdentifier(form.recoveryId || form.signInId);
      if (!recoveryId) {
        throw new Error("Enter the email address or mobile number for this Banxito account.");
      }

      const delivery = await requestPasswordReset(recoveryId);
      setPasswordResetDelivery(delivery);
      setForm((current) => ({ ...current, recoveryId }));
      setAuthStep("recoverConfirm");
      setStatus(
        delivery.destination
          ? `We sent a password reset code to ${delivery.destination}.`
          : "We sent a password reset code for this account."
      );
    } catch (error) {
      setStatus((error as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResetConfirm(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setStatus("");

    try {
      const recoveryId = normalizeAuthIdentifier(form.recoveryId || form.signInId);
      if (!recoveryId) {
        throw new Error("Enter the email address or mobile number for this Banxito account.");
      }
      if (!resetPasswordIsStrong) {
        throw new Error("Choose a stronger password before continuing.");
      }
      if (form.resetPassword !== form.resetConfirmPassword) {
        throw new Error("Passwords do not match.");
      }

      await confirmPasswordReset(recoveryId, form.resetCode, form.resetPassword);
      setForm((current) => ({
        ...current,
        signInId: recoveryId,
        password: "",
        resetCode: "",
        resetPassword: "",
        resetConfirmPassword: ""
      }));
      resetToSignIn("Password updated. Sign in with your new password.");
    } catch (error) {
      setStatus((error as Error).message);
    } finally {
      setSubmitting(false);
    }
  }
}

function HomeScreen({
  summary,
  totalAvailableMinor,
  loading,
  qrEligibleAllowances,
  activeQrSession,
  onGoAllowances,
  onGoActivity,
  onOpenQr,
  onIssueQr,
  issuingQr
}: {
  summary: RecipientSummary;
  totalAvailableMinor: number;
  loading: boolean;
  qrEligibleAllowances: RecipientSummary["wallets"];
  activeQrSession: RecipientQrSession | null;
  onGoAllowances: () => void;
  onGoActivity: () => void;
  onOpenQr: () => void;
  onIssueQr: (recipientWalletId: string) => void;
  issuingQr: boolean;
}) {
  const { t, format } = useI18n();
  const displayCurrency = summary.wallets[0]?.currency ?? getCurrencyForCountry(summary.recipient.countryCode);
  const accountDescription = `${summary.recipient.countryCode} · ${displayCurrency} · ${getLanguageLabel(
    summary.recipient.locale
  )}`;

  return (
    <div style={styles.stack}>
      <Panel hero>
        <Badge>Recipient home</Badge>
        <h1 style={styles.heroTitle}>{t("Your sponsored support is ready.")}</h1>
        <p style={styles.heroCopy}>
          {t(
            "View the service allowances your sponsor purchased for you, see which care categories are available, and follow recent care activity in one place."
          )}
        </p>
        <MetricGrid
          items={[
            { label: "Available now", value: formatMoney(totalAvailableMinor, displayCurrency) },
            { label: "Allowances", value: String(summary.wallets.length) },
            { label: "Recent activity", value: String(summary.careEvents.length) },
            { label: "Status", value: t(getFriendlyStatusLabel(summary.recipient.status)) }
          ]}
        />
        <div style={styles.actionRow}>
          <GhostButton title="See allowances" onClick={onGoAllowances} />
          <GhostButton title="View activity" onClick={onGoActivity} />
        </div>
      </Panel>

      <Panel>
        <SectionTitle
          eyebrow="Recipient account"
          title={summary.recipient.fullName}
          description={accountDescription}
        />
        <div style={styles.detailList}>
          <DetailRow label="Email" value={summary.recipient.email ?? "Not provided"} />
          <DetailRow
            label="Connected"
            value={
              summary.recipient.boundAt
                ? new Date(summary.recipient.boundAt).toLocaleString()
                : "Awaiting setup"
            }
          />
        </div>
      </Panel>

      <Panel>
        <SectionTitle
          eyebrow="Visit QR"
          title="Use a Banxito QR at the clinic"
          description="Choose an available service category and show the QR or manual code so the provider can submit a service claim."
        />
        {activeQrSession ? (
          <div style={styles.infoCard}>
            <strong>
              {activeQrSession.categoryName} {t("QR is active")}
            </strong>
            <p style={styles.helperMuted}>
              {formatMoney(activeQrSession.balanceSnapshotMinor, activeQrSession.currency)} {t("available snapshot")} · {formatTimeRemaining(activeQrSession.expiresAt)}
            </p>
            <div style={styles.actionRow}>
              <GhostButton title="Open QR" onClick={onOpenQr} />
            </div>
          </div>
        ) : qrEligibleAllowances.length > 0 ? (
          <div style={styles.stack}>
            {qrEligibleAllowances.map((wallet) => (
              <div key={wallet.recipientWalletId} style={styles.activityCard}>
                <div style={styles.rowBetween}>
                  <div>
                    <strong>{wallet.categoryName}</strong>
                    <p style={styles.helperMuted}>
                      {t("Available:")} {formatMoney(
                        Number(wallet.balanceMinor ?? 0) - Number(wallet.reservedMinor ?? 0),
                        wallet.currency
                      )}
                    </p>
                  </div>
                  <span style={styles.badge}>{t("Available")}</span>
                </div>
                <GhostButton
                  title={issuingQr ? "Preparing QR..." : "Generate QR and code"}
                  onClick={() => onIssueQr(wallet.recipientWalletId)}
                />
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No service allowance available for QR"
            message="Once your sponsor purchases a category allowance, you can generate a QR or manual code for approved providers."
          />
        )}
      </Panel>

      <Panel>
        <SectionTitle
          eyebrow="Quick view"
          title="Category allowances"
          description={loading ? "Refreshing allowances..." : "What you can use right now."}
        />
        <div style={styles.cardGrid}>
          {summary.wallets.length === 0 ? (
            <EmptyState
              title="No category allowances available"
              message="Your sponsor has not purchased a category allowance yet, or the allowance has not been assigned."
            />
          ) : (
            summary.wallets.map((wallet) => (
              <div key={wallet.recipientWalletId} style={styles.walletCard}>
                <span style={styles.walletLabel}>{wallet.categoryName}</span>
                <strong style={styles.walletAmount}>
                  {formatMoney(wallet.balanceMinor, wallet.currency)}
                </strong>
                <span style={styles.walletMeta}>
                  {t("Reserved:")} {formatMoney(wallet.reservedMinor, wallet.currency)}
                </span>
              </div>
            ))
          )}
        </div>
      </Panel>
    </div>
  );
}

function QrScreen({
  session,
  wallets,
  onGenerate,
  onRefresh,
  issuingQr
}: {
  session: RecipientQrSession | null;
  wallets: RecipientSummary["wallets"];
  onGenerate: (recipientWalletId: string) => void;
  onRefresh: () => void;
  issuingQr: boolean;
}) {
  const { t, format } = useI18n();

  return (
    <Panel>
      <SectionTitle
        eyebrow="Visit QR"
        title="Show your Banxito QR or code"
        description="Choose an available category. The provider scans the QR or enters the code, then Banxito approves the exact service and fee."
      />

      {!session ? (
        <div style={styles.stack}>
          <EmptyState
            title="Choose a category to generate a QR"
            message="The QR does not issue payment by itself. It lets the provider submit a service claim for the selected Banxito category."
          />
          <div style={styles.infoCard}>
            <strong>{t("How it works at the front desk")}</strong>
            <p style={styles.helperMuted}>
              {t("1. Show the QR or manual code from an available service category.")}
            </p>
            <p style={styles.helperMuted}>
              {t("2. The provider enters the service and fee.")}
            </p>
            <p style={styles.helperMuted}>
              {t("3. Banxito approves or denies before card information is issued.")}
            </p>
          </div>
          {wallets.length > 0 ? (
            wallets.map((wallet) => (
              <div key={wallet.recipientWalletId} style={styles.activityCard}>
                <div style={styles.rowBetween}>
                  <div>
                    <strong>{wallet.categoryName}</strong>
                    <p style={styles.helperMuted}>
                      {t("Available:")} {formatMoney(
                        Number(wallet.balanceMinor ?? 0) - Number(wallet.reservedMinor ?? 0),
                        wallet.currency
                      )}
                    </p>
                  </div>
                  <span style={styles.badge}>{t("Ready")}</span>
                </div>
                <GhostButton
                  title={issuingQr ? "Preparing QR..." : "Generate QR and code"}
                  onClick={() => onGenerate(wallet.recipientWalletId)}
                />
              </div>
            ))
          ) : (
            <div style={styles.infoCard}>
              <strong>{t("No service category available")}</strong>
              <p style={styles.helperMuted}>
                {t("Your sponsor must purchase a category allowance before you can generate a provider QR.")}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div style={styles.stack}>
          <div style={styles.qrCard}>
            <div style={styles.qrCanvasWrap}>
              <QRCode
                value={session.qrPayload}
                size={224}
                bgColor="#fbf4e8"
                fgColor="#071018"
              />
            </div>
            <div style={styles.detailList}>
              <DetailRow label="Recipient" value={session.recipientName} />
              <DetailRow label="Member ID" value={session.recipientMemberId} />
              <DetailRow label="Category" value={session.categoryName} />
              <DetailRow
                label="Available snapshot"
                value={formatMoney(session.balanceSnapshotMinor, session.currency)}
              />
              <DetailRow label="Expires" value={formatTimeRemaining(session.expiresAt)} />
            </div>
          </div>

          <div style={styles.infoCard}>
            <strong>{t("Manual fallback code")}</strong>
            <p style={styles.manualCode}>{session.manualCode}</p>
            <p style={styles.helperMuted}>
              {t("If the provider is using a desktop computer or the camera has trouble scanning, they can enter this code manually.")}
            </p>
          </div>

          <div style={styles.infoCard}>
            <strong>{t("What to do at the clinic")}</strong>
            <p style={styles.helperMuted}>
              {t("Ask the front desk to scan the QR or enter the code. They still need to enter the service and fee, and Banxito must approve it before one-time payment details are issued.")}
            </p>
          </div>

          <div style={styles.actionRow}>
            <GhostButton
              title={issuingQr ? "Refreshing..." : "Refresh QR"}
              onClick={onRefresh}
            />
          </div>
        </div>
      )}
    </Panel>
  );
}

function WalletScreen({ wallets }: { wallets: RecipientSummary["wallets"] }) {
  const { t } = useI18n();

  return (
    <Panel>
      <SectionTitle
        eyebrow="My credits"
        title="Your category service allowances"
        description="Each allowance is controlled by service category and approved provider rules."
      />
      <div style={styles.stack}>
        {wallets.length === 0 ? (
          <EmptyState
            title="No service allowances yet"
            message="Once your sponsor purchases and assigns support, your category allowances will appear here."
          />
        ) : (
          wallets.map((wallet) => (
            <div key={wallet.recipientWalletId} style={styles.walletRow}>
              <div>
                <strong>{wallet.categoryName}</strong>
                <p style={styles.helperMuted}>
                  {t("Status:")} {t(getFriendlyStatusLabel(wallet.status))}
                </p>
              </div>
              <div style={{ textAlign: "right" }}>
                <strong>{formatMoney(wallet.balanceMinor, wallet.currency)}</strong>
                <p style={styles.helperMuted}>
                  {t("Reserved:")} {formatMoney(wallet.reservedMinor, wallet.currency)}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </Panel>
  );
}

function CarePassportScreen({
  carePassport,
  loading,
  qrEligibleAllowances,
  onRefresh,
  onOpenQr,
  onIssueQr,
  issuingQr
}: {
  carePassport: CarePassportResponse | null;
  loading: boolean;
  qrEligibleAllowances: RecipientSummary["wallets"];
  onRefresh: () => void;
  onOpenQr: () => void;
  onIssueQr: (recipientWalletId: string) => void;
  issuingQr: boolean;
}) {
  const { t, format } = useI18n();
  const passport = carePassport?.carePassport;
  const recipientName = carePassport?.recipient.firstName ?? carePassport?.recipient.fullName?.split(" ")[0] ?? "Banxito";
  const passportHeadline = passport
    ? formatTranslatedMessage(t("{name}, your Banxito Care Passport is ready."), {
        name: recipientName
      })
    : t("Your Banxito Care Passport is loading.");
  const firstWallet = qrEligibleAllowances[0];

  return (
    <div style={styles.stack}>
      <Panel hero>
        <Badge>Care Passport</Badge>
        <h1 style={styles.heroTitle}>{passportHeadline}</h1>
        <p style={styles.heroCopy}>
          {t(
            "One simple place to see what care you can use, what to say at the front desk, and how to show your QR or code."
          )}
        </p>
        <div style={styles.actionRow}>
          {firstWallet ? (
            <PrimaryButton
              title={issuingQr ? "Preparing QR..." : "Show my QR or code"}
              onClick={() => onIssueQr(firstWallet.recipientWalletId)}
            />
          ) : (
            <GhostButton title="Open Visit QR" onClick={onOpenQr} />
          )}
          <GhostButton title={loading ? "Refreshing..." : "Refresh passport"} onClick={onRefresh} />
        </div>
      </Panel>

      <Panel>
        <SectionTitle
          eyebrow="Available care"
          title="What you can use"
          description="Plain-language service allowances by category. Banxito still checks provider approval and the exact service before fulfillment."
        />
        <div style={styles.stack}>
          {!passport ? (
            <EmptyState
              title={loading ? "Building your Care Passport" : "Care Passport not loaded yet"}
              message="Banxito is preparing a simple view of your available care categories."
            />
          ) : passport.availableCare.length === 0 ? (
            <EmptyState
              title="No active category allowance yet"
              message="When your sponsor purchases a care category allowance, it will appear here."
            />
          ) : (
            passport.availableCare.map((item) => (
              <div key={item.category} style={styles.activityCard}>
                <div style={styles.rowBetween}>
                  <strong>{t(item.category)}</strong>
                  <span style={styles.badge}>{t("Available")}</span>
                </div>
                <p style={styles.helperText}>
                  {formatTranslatedMessage(t("You have {amount} in {category} service allowance."), {
                    amount: item.plainBalance.match(/[A-Z]{2,3}\$?[\d,.]+|\$[\d,.]+/)?.[0] ?? item.plainBalance,
                    category: t(item.category).toLowerCase()
                  })}
                </p>
                <p style={styles.helperMuted}>
                  {formatTranslatedMessage(t("Use this with approved Banxito {category} providers."), {
                    category: t(item.category).toLowerCase()
                  })}
                </p>
              </div>
            ))
          )}
        </div>
      </Panel>

      <Panel>
        <SectionTitle
          eyebrow="At the front desk"
          title="What to say"
          description="Use this simple script so the clinic or provider knows exactly what to do."
        />
        <div style={styles.infoCard}>
          <strong>{t("Say this")}</strong>
          <p style={styles.frontDeskScript}>
            {t(
              "Tell the front desk: I'm using Banxito. Please scan my QR or enter my code before you charge me."
            )}
          </p>
        </div>
        <div style={styles.infoCard}>
          <strong>{t("QR instructions")}</strong>
          <p style={styles.helperMuted}>
            {t(
              "Open Visit QR, choose an available category, and show the QR or manual code after the provider confirms the service and amount."
            )}
          </p>
        </div>
      </Panel>

      <Panel>
        <SectionTitle
          eyebrow="Backup options"
          title="If scanning does not work"
          description="Banxito always gives the provider a low-tech option."
        />
        <div style={styles.stack}>
          {[
            "Ask the provider to type the manual code shown under your QR.",
            "If the code expired, generate a fresh Visit QR when you are ready."
          ].map((instruction) => (
            <div key={instruction} style={styles.infoCard}>
              <p style={styles.helperText}>{t(instruction)}</p>
            </div>
          ))}
        </div>
      </Panel>

      {passport?.reminders.length ? (
        <Panel>
          <SectionTitle
            eyebrow="Helpful reminders"
            title="What needs attention"
            description="Friendly notes based on your available support."
          />
          <div style={styles.stack}>
            {passport.reminders.map((reminder) => (
              <div key={`${reminder.title}-${reminder.body}`} style={styles.activityCard}>
                <div style={styles.rowBetween}>
                  <strong>{translateBackendCareText(reminder.title, t, format)}</strong>
                  <span style={styles.badge}>{t(normalizeLabel(reminder.priority))}</span>
                </div>
                <p style={styles.helperMuted}>{translateBackendCareText(reminder.body, t, format)}</p>
              </div>
            ))}
          </div>
        </Panel>
      ) : null}

      {carePassport?.ai ? (
        <div style={styles.aiDisclosure}>
          {t("AI-assisted guidance for clarity. Allowances and approvals come from Banxito records.")}
        </div>
      ) : null}
    </div>
  );
}

function ServicesScreen({
  categories,
  providers,
  loading,
  selectedCategory,
  onCategoryChange,
  search,
  onSearchChange,
  openNowOnly,
  onOpenNowChange,
  viewMode,
  onViewModeChange,
  hasLocation,
  onUseLocation,
  onClearFilters,
  onStartVisit,
  startingVisitFor
}: {
  categories: RecipientServiceCategory[];
  providers: RecipientProvider[];
  loading: boolean;
  selectedCategory: string;
  onCategoryChange: (categoryCode: string) => void;
  search: string;
  onSearchChange: (search: string) => void;
  openNowOnly: boolean;
  onOpenNowChange: (enabled: boolean) => void;
  viewMode: "list" | "map";
  onViewModeChange: (mode: "list" | "map") => void;
  hasLocation: boolean;
  onUseLocation: () => void;
  onClearFilters: () => void;
  onStartVisit: (provider: RecipientProvider) => void;
  startingVisitFor: string;
}) {
  const { t } = useI18n();
  const categoryOptions = [
    { value: "", label: "All available categories" },
    ...categories.map((category) => ({
      value: category.categoryCode,
      label: category.categoryName
    }))
  ];
  const mappableProviders = providers
    .map((provider) => ({
      provider,
      target: getProviderMapTarget(provider)
    }))
    .filter((item) => item.target);
  const mapTarget = mappableProviders.length > 0 ? mappableProviders[0].target : "";
  const mapEmbedUrl =
    viewMode === "map" && mapTarget
      ? `https://www.google.com/maps?q=${encodeURIComponent(mapTarget)}&output=embed`
      : "";

  return (
    <div style={styles.stack}>
      <Panel hero>
        <Badge>Find Services</Badge>
        <h1 style={styles.heroTitle}>{t("Find approved care near you")}</h1>
        <p style={styles.heroCopy}>
          {t(
            "Choose an available category and Banxito will show approved providers that can submit a service claim for it."
          )}
        </p>
        <div style={styles.filterGrid}>
          <SelectField
            label="Category"
            value={selectedCategory}
            onChange={onCategoryChange}
            options={categoryOptions}
          />
          <Field
            label="Search approved providers"
            value={search}
            onChange={onSearchChange}
            helper="Search by provider, city, or category"
          />
        </div>
        <div style={styles.actionRow}>
          <button
            type="button"
            style={openNowOnly ? styles.pillToggleActive : styles.pillToggle}
            onClick={() => onOpenNowChange(!openNowOnly)}
          >
            {t("Open now")}
          </button>
          <button
            type="button"
            style={hasLocation ? styles.pillToggleActive : styles.pillToggle}
            onClick={onUseLocation}
          >
            {t(hasLocation ? "Using your location" : "Use my location")}
          </button>
          <button
            type="button"
            style={viewMode === "map" ? styles.pillToggleActive : styles.pillToggle}
            onClick={() => onViewModeChange(viewMode === "map" ? "list" : "map")}
          >
            {t(viewMode === "map" ? "List view" : "Map view")}
          </button>
        </div>
        {hasLocation ? (
          <p style={styles.helperMuted}>
            {t(
              "Using location for this search. If you are not near the recipient's service area, show all providers instead."
            )}
          </p>
        ) : null}
      </Panel>

      <Panel>
        <SectionTitle
          eyebrow="Providers"
          title={viewMode === "map" ? "Provider map" : "Approved providers"}
          description={
            viewMode === "map"
              ? "Choose an approved provider from the map or list to start a Banxito visit QR."
              : "Call, get directions, or start a Banxito visit QR for an approved provider."
          }
        />

        {viewMode === "map" ? (
          <div style={styles.mapPlaceholder}>
            {mapEmbedUrl ? (
              <iframe
                src={mapEmbedUrl}
                title={t("Map of approved providers")}
                style={styles.mapFrame}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : (
              <>
                <strong>{t("Provider map is coming soon")}</strong>
                <p style={styles.helperMuted}>
                  {t(
                    "You can still choose an approved provider from the list below or search by provider, city, or category."
                  )}
                </p>
              </>
            )}
          </div>
        ) : null}

        <div style={styles.stack}>
          {loading ? (
            <EmptyState
              title="Loading approved providers"
              message="Checking providers that match your available categories."
            />
          ) : providers.length === 0 ? (
            <div style={styles.emptyActionCard}>
              <EmptyState
                title="No approved providers found"
                message="Try another category, search term, or show all approved providers."
              />
              <div style={styles.actionRow}>
                <button type="button" style={styles.pillToggle} onClick={onClearFilters}>
                  {t("Show all approved providers")}
                </button>
              </div>
              <p style={styles.helperMuted}>
                {t("Tip: location search works best when you are near the recipient's service area.")}
              </p>
            </div>
          ) : (
            providers.map((provider) => (
              <ProviderCard
                key={`${provider.providerLocationId}-${provider.category.categoryCode}`}
                provider={provider}
                onStartVisit={onStartVisit}
                starting={startingVisitFor === provider.providerLocationId}
              />
            ))
          )}
        </div>
      </Panel>
    </div>
  );
}

function ProviderCard({
  provider,
  onStartVisit,
  starting
}: {
  provider: RecipientProvider;
  onStartVisit: (provider: RecipientProvider) => void;
  starting: boolean;
}) {
  const { t } = useI18n();
  const address = getProviderAddress(provider);
  const directionsUrl =
    provider.mapsUrl ||
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      address || provider.displayName || provider.providerName
    )}`;

  return (
    <div style={styles.providerCard}>
      <div style={styles.rowBetween}>
        <div>
          <strong>{provider.displayName || provider.providerName}</strong>
          <p style={styles.helperMuted}>{address || t("Address coming soon")}</p>
        </div>
        <span style={styles.badge}>{t(provider.isOpenNow ? "Open now" : "Closed now")}</span>
      </div>

      <div style={styles.providerMetaGrid}>
        <DetailRow label="Available for this category" value={t(provider.category.categoryName)} />
        <DetailRow
          label="Allowance available"
          value={formatMoney(provider.category.availableMinor, provider.category.currency)}
        />
        <DetailRow
          label="Distance"
          value={provider.distanceKm === null ? t("Nearby") : `${provider.distanceKm.toFixed(1)} ${t("km away")}`}
        />
      </div>

      <div style={styles.confirmationCallout}>
        <strong style={styles.confirmationTitle}>{t("Provider is approved for")}</strong>
        <p style={styles.helperMuted}>
          {t(
            "This provider can submit a service claim for your selected category. Banxito still verifies the exact service and amount before any card details are issued."
          )}
        </p>
      </div>

      <div style={styles.providerActions}>
        {provider.phone ? (
          <a href={`tel:${provider.phone}`} style={styles.secondaryLinkButton}>
            {t("Call")}
          </a>
        ) : null}
        <a href={directionsUrl} target="_blank" rel="noreferrer" style={styles.secondaryLinkButton}>
          {t("Directions")}
        </a>
        <button
          type="button"
          style={styles.primaryButton}
          onClick={() => onStartVisit(provider)}
          disabled={starting}
        >
          {t(starting ? "Starting..." : "Start visit")}
        </button>
      </div>
    </div>
  );
}

function ActivityScreen({ careEvents }: { careEvents: RecipientSummary["careEvents"] }) {
  const { t } = useI18n();

  return (
    <Panel>
      <SectionTitle
        eyebrow="Activity"
        title="Care and service history"
        description="Recent provider activity and completed service events."
      />
      <div style={styles.stack}>
        {careEvents.length === 0 ? (
          <EmptyState
            title="No service activity yet"
            message="When a provider completes care or submits service proof, it will appear here."
          />
        ) : (
          careEvents.map((event) => (
            <div key={event.redemptionId} style={styles.activityCard}>
              <div style={styles.rowBetween}>
                <div>
                  <strong>{event.serviceName ?? event.categoryName}</strong>
                  <p style={styles.helperMuted}>{event.providerName}</p>
                </div>
                <span style={styles.badge}>{t(getFriendlyStatusLabel(event.redemptionStatus))}</span>
              </div>
              <div style={styles.activityMeta}>
                <span>{formatMoney(event.amountMinor, event.currency)}</span>
                <span>{new Date(event.requestedAt).toLocaleDateString()}</span>
              </div>
              {event.receiptFileUrl ? (
                <a href={event.receiptFileUrl} target="_blank" rel="noreferrer" style={styles.link}>
                  {t("Open receipt")}
                </a>
              ) : null}
            </div>
          ))
        )}
      </div>
    </Panel>
  );
}

function HelpScreen() {
  return (
    <Panel>
      <SectionTitle
        eyebrow="Help"
        title="How to use Banxito"
        description="A simple guide for recipients."
      />
      <div style={styles.stack}>
        <InfoCard
          title="1. Use your setup code"
          text="Your sponsor shares a setup code with you. Enter it once to connect your Banxito account."
        />
        <InfoCard
          title="2. Check your allowances"
          text="See what service categories are available to you, such as healthcare, school, or essentials."
        />
        <InfoCard
          title="3. Visit approved providers"
          text="Banxito support is meant to be used with approved providers and tracked service delivery."
        />
      </div>
    </Panel>
  );
}

function ProfileScreen({ summary }: { summary: RecipientSummary }) {
  const currency = getCurrencyForCountry(summary.recipient.countryCode);

  return (
    <Panel>
      <SectionTitle
        eyebrow="Me"
        title="Recipient profile"
        description="Basic account details for this Banxito recipient account."
      />
      <div style={styles.detailList}>
        <DetailRow label="Full name" value={summary.recipient.fullName} />
        <DetailRow label="Email" value={summary.recipient.email ?? "Not provided"} />
        <DetailRow label="Phone" value={summary.recipient.phone ?? "Not provided"} />
        <DetailRow label="Country" value={summary.recipient.countryCode} />
        <DetailRow label="Currency" value={currency} />
        <DetailRow label="Language" value={getLanguageLabel(summary.recipient.locale)} />
      </div>
    </Panel>
  );
}

function BindCard({
  code,
  onChange,
  onSubmit,
  onCreateAccount
}: {
  code: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCreateAccount?: () => void;
}) {
  const { t } = useI18n();

  return (
    <AuthCard
      title="Connect your account"
      description="Enter the setup code shared by your sponsor. Banxito will connect you to the right sponsored service access."
    >
      <div style={styles.form}>
        <Field label="Setup code" value={code} onChange={onChange} />
        <PrimaryButton title="Connect account" onClick={onSubmit} />
        {onCreateAccount ? (
          <GhostButton title="Create recipient account" onClick={onCreateAccount} />
        ) : null}
      </div>
      <p style={styles.helperText}>
        {t("Banxito accepts lowercase or uppercase input. Your code will be checked automatically.")}
      </p>
    </AuthCard>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={styles.appShell}>
      <div style={styles.bgGlowA} />
      <div style={styles.bgGlowB} />
      <div style={styles.maxWidth}>
        <LanguageSelect />
        {children}
      </div>
    </div>
  );
}

function LanguageSelect() {
  const { language, setLanguage, t } = useI18n();
  return (
    <div style={styles.languageBar}>
      <label style={styles.languageLabel}>
        {t("Language")}
        <select
          style={styles.languageSelect}
          value={language}
          onChange={(event) => setLanguage(event.target.value as AppLanguage)}
        >
          {languageOptions.map((option) => (
            <option key={option.value} value={option.value} style={styles.selectOption}>
              {languageDisplayLabels[option.value]}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function useIsNarrowScreen(maxWidth = 640) {
  const [isNarrow, setIsNarrow] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const query = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const update = () => setIsNarrow(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, [maxWidth]);

  return isNarrow;
}

function Header({
  title,
  subtitle,
  onSignOut
}: {
  title: string;
  subtitle: string;
  onSignOut: () => void;
}) {
  const { t } = useI18n();
  const isMobile = useIsNarrowScreen();
  return (
    <header style={{ ...styles.header, ...(isMobile ? styles.headerMobile : {}) }}>
      <div style={{ ...styles.headerBrand, ...(isMobile ? styles.headerBrandMobile : {}) }}>
        <img src={logoSrc} alt="Banxito" style={{ ...styles.logo, ...(isMobile ? styles.logoMobile : {}) }} />
        <div>
          <p style={styles.eyebrowText}>{t(subtitle)}</p>
          <h2 style={{ ...styles.headerTitle, ...(isMobile ? styles.headerTitleMobile : {}) }}>{title}</h2>
        </div>
      </div>
      <button style={styles.ghostButton} onClick={onSignOut}>
        {t("Sign out")}
      </button>
    </header>
  );
}

function AuthCard({
  title,
  description,
  children
}: {
  title: string;
  description: string;
  children?: React.ReactNode;
}) {
  const { t } = useI18n();
  const isMobile = useIsNarrowScreen();
  return (
    <div style={{ ...styles.authShell, ...(isMobile ? styles.authShellMobile : {}) }}>
      <div style={{ ...styles.authCard, ...(isMobile ? styles.authCardMobile : {}) }}>
        <img src={logoSrc} alt="Banxito" style={{ ...styles.heroLogo, ...(isMobile ? styles.heroLogoMobile : {}) }} />
        <p style={styles.eyebrowText}>{t("Banxito Recipient")}</p>
        <h1 style={{ ...styles.authTitle, ...(isMobile ? styles.authTitleMobile : {}) }}>{t(title)}</h1>
        <p style={{ ...styles.authCopy, ...(isMobile ? styles.authCopyMobile : {}) }}>{t(description)}</p>
        {children}
      </div>
    </div>
  );
}

function StatusBanner({
  status
}: {
  status: { type: string; message: string };
}) {
  const { t } = useI18n();

  if (!status.message) {
    return null;
  }

  return (
    <div
      style={{
        ...styles.statusBanner,
        ...(status.type === "error" ? styles.statusError : styles.statusSuccess)
      }}
    >
      {t(status.message)}
    </div>
  );
}

function Panel({
  children,
  hero = false
}: {
  children: React.ReactNode;
  hero?: boolean;
}) {
  return <section style={hero ? styles.heroPanel : styles.panel}>{children}</section>;
}

function Badge({ children }: { children: React.ReactNode }) {
  const { t } = useI18n();
  return <span style={styles.badgePill}>{typeof children === "string" ? t(children) : children}</span>;
}

function MetricGrid({
  items
}: {
  items: { label: string; value: string }[];
}) {
  const { t } = useI18n();
  return (
    <div style={styles.metricGrid}>
      {items.map((item) => (
        <div key={item.label} style={styles.metricCard}>
          <span style={styles.metricLabel}>{t(item.label)}</span>
          <strong style={styles.metricValue}>{item.value}</strong>
        </div>
      ))}
    </div>
  );
}

function SectionTitle({
  eyebrow,
  title,
  description
}: {
  eyebrow: string;
  title: string;
  description: string;
}) {
  const { t } = useI18n();
  return (
    <div style={{ marginBottom: 14 }}>
      <p style={styles.eyebrowText}>{t(eyebrow)}</p>
      <h3 style={styles.sectionTitle}>{t(title)}</h3>
      <p style={styles.sectionCopy}>{t(description)}</p>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  const { t } = useI18n();
  return (
    <div style={styles.detailRow}>
      <span style={styles.helperMuted}>{t(label)}</span>
      <strong>{value}</strong>
    </div>
  );
}

function EmptyState({ title, message }: { title: string; message: string }) {
  const { t } = useI18n();
  return (
    <div style={styles.emptyState}>
      <strong>{t(title)}</strong>
      <p style={styles.helperMuted}>{t(message)}</p>
    </div>
  );
}

function InfoCard({ title, text }: { title: string; text: string }) {
  const { t } = useI18n();
  return (
    <div style={styles.infoCard}>
      <strong>{t(title)}</strong>
      <p style={styles.helperMuted}>{t(text)}</p>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  helper,
  autoComplete,
  trailingAction
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  helper?: string;
  autoComplete?: string;
  trailingAction?: { label: string; onClick: () => void };
}) {
  const { t } = useI18n();
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{t(label)}</span>
      <div style={styles.inputWrap}>
        <input
          style={styles.input}
          type={type}
          autoComplete={autoComplete}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        {trailingAction ? (
          <button
            type="button"
            style={styles.inlineAction}
            onClick={trailingAction.onClick}
          >
            {t(trailingAction.label)}
          </button>
        ) : null}
      </div>
      {helper ? <span style={styles.fieldHelper}>{t(helper)}</span> : null}
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  const { t } = useI18n();
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{t(label)}</span>
      <select style={styles.input} value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {t(option.label)}
          </option>
        ))}
      </select>
    </label>
  );
}

function TwoCol({ children }: { children: React.ReactNode }) {
  return <div style={styles.twoCol}>{children}</div>;
}

function PasswordChecklist({
  checks
}: {
  checks: { label: string; ok: boolean }[];
}) {
  const { t } = useI18n();
  return (
    <div style={styles.passwordChecklist}>
      {checks.map((check) => (
        <div key={check.label} style={styles.passwordCheckRow}>
          <span style={check.ok ? styles.passwordCheckPass : styles.passwordCheckPending}>
            {check.ok ? "OK" : t("Add")}
          </span>
          <span style={styles.fieldHelper}>{t(check.label)}</span>
        </div>
      ))}
    </div>
  );
}

function PrimaryButton({ title, onClick }: { title: string; onClick?: () => void }) {
  const { t } = useI18n();
  return (
    <button style={styles.primaryButton} type={onClick ? "button" : "submit"} onClick={onClick}>
      {t(title)}
    </button>
  );
}

function GhostButton({ title, onClick }: { title: string; onClick: () => void }) {
  const { t } = useI18n();
  return (
    <button style={styles.ghostButton} type="button" onClick={onClick}>
      {t(title)}
    </button>
  );
}

function BottomNav({
  route,
  onChange
}: {
  route: RecipientRoute;
  onChange: (route: RecipientRoute) => void;
}) {
  const { t } = useI18n();
  const items: Array<[RecipientRoute, string]> = [
    ["home", "Home"],
    ["passport", "Passport"],
    ["services", "Services"],
    ["wallets", "Allowances"],
    ["qr", "Visit QR"],
    ["activity", "Activity"],
    ["help", "Help"],
    ["me", "Me"]
  ];

  return (
    <nav style={styles.bottomNav}>
      {items.map(([key, label]) => (
        <button
          key={key}
          style={route === key ? styles.navItemActive : styles.navItem}
          onClick={() => onChange(key)}
        >
          {t(label)}
        </button>
      ))}
    </nav>
  );
}

const styles: Record<string, React.CSSProperties> = {
  appShell: {
    minHeight: "100dvh",
    padding: "18px 18px calc(96px + env(safe-area-inset-bottom))",
    background:
      "radial-gradient(circle at 10% 4%, rgba(228,165,28,0.22), transparent 26%), radial-gradient(circle at 88% 8%, rgba(48,155,171,0.18), transparent 25%), radial-gradient(circle at bottom center, rgba(233,111,18,0.08), transparent 28%), linear-gradient(180deg, #071018 0%, #050b0f 100%)",
    color: "#fff6e4",
    fontFamily: '"Aptos","Segoe UI","Helvetica Neue",Arial,sans-serif',
    position: "relative",
    boxSizing: "border-box",
    overflowX: "hidden"
  },
  bgGlowA: {
    position: "fixed",
    top: 40,
    left: -60,
    width: 220,
    height: 220,
    borderRadius: 999,
    filter: "blur(88px)",
    opacity: 0.4,
    background: "rgba(242,180,59,0.28)",
    pointerEvents: "none"
  },
  bgGlowB: {
    position: "fixed",
    right: -40,
    bottom: 100,
    width: 180,
    height: 180,
    borderRadius: 999,
    filter: "blur(88px)",
    opacity: 0.34,
    background: "rgba(73,170,175,0.24)",
    pointerEvents: "none"
  },
  maxWidth: {
    width: "100%",
    maxWidth: 1080,
    margin: "0 auto",
    position: "relative",
    zIndex: 1,
    boxSizing: "border-box"
  },
  languageBar: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 12
  },
  languageLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "rgba(247,237,220,0.72)",
    fontSize: 13
  },
  languageSelect: {
    minHeight: 40,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.1)",
    background: "rgba(255,255,255,0.06)",
    color: "#f7eddc",
    padding: "0 14px",
    outline: "none"
  },
  selectOption: {
    color: "#071018",
    background: "#fbf4e8"
  },
  authShell: {
    minHeight: "80vh",
    display: "grid",
    placeItems: "center",
    width: "100%",
    boxSizing: "border-box"
  },
  authShellMobile: {
    minHeight: "auto",
    placeItems: "stretch",
    paddingTop: 4
  },
  authCard: {
    width: "100%",
    maxWidth: 560,
    borderRadius: 32,
    border: "1px solid rgba(229,170,45,0.28)",
    background: "linear-gradient(145deg, rgba(18,31,43,0.98), rgba(7,16,24,0.92))",
    boxShadow: "0 24px 80px rgba(0,0,0,0.35)",
    padding: "clamp(22px, 5vw, 38px)",
    display: "grid",
    gap: 14,
    boxSizing: "border-box",
    overflow: "hidden"
  },
  authCardMobile: {
    maxWidth: "none",
    borderRadius: 24,
    padding: "20px 18px",
    gap: 12,
    boxShadow: "0 18px 48px rgba(0,0,0,0.32)"
  },
  heroLogo: {
    width: "min(300px, 72vw)",
    marginBottom: 6
  },
  heroLogoMobile: {
    width: "min(220px, 64vw)",
    justifySelf: "center",
    marginBottom: 2
  },
  authTitle: {
    margin: 0,
    fontSize: "2rem"
  },
  authTitleMobile: {
    fontSize: "clamp(28px, 9vw, 36px)",
    lineHeight: 1.08
  },
  authCopy: {
    margin: 0,
    color: "rgba(247,237,220,0.76)"
  },
  authCopyMobile: {
    fontSize: 16,
    lineHeight: 1.45
  },
  segment: {
    display: "inline-flex",
    gap: 8,
    padding: 6,
    borderRadius: 999,
    background: "rgba(255,255,255,0.05)",
    marginTop: 4
  },
  segmentButton: {
    minHeight: 42,
    borderRadius: 999,
    padding: "0 16px",
    background: "transparent",
    border: "none",
    color: "rgba(247,237,220,0.66)",
    cursor: "pointer"
  },
  segmentActive: {
    minHeight: 42,
    borderRadius: 999,
    padding: "0 16px",
    background: "rgba(255,255,255,0.08)",
    border: "none",
    color: "#f7eddc",
    cursor: "pointer"
  },
  form: {
    display: "grid",
    gap: 14
  },
  twoCol: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))"
  },
  field: {
    display: "grid",
    gap: 6,
    minWidth: 0
  },
  fieldLabel: {
    color: "rgba(247,237,220,0.74)",
    fontSize: 14
  },
  inputWrap: {
    position: "relative",
    minWidth: 0
  },
  input: {
    width: "100%",
    minHeight: 50,
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.11)",
    padding: "12px 14px",
    background: "rgba(255,255,255,0.055)",
    color: "#fff6e4",
    outline: "none",
    boxSizing: "border-box"
  },
  inlineAction: {
    position: "absolute",
    top: "50%",
    right: 12,
    transform: "translateY(-50%)",
    minHeight: 32,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.08)",
    padding: "0 12px",
    background: "rgba(255,255,255,0.06)",
    color: "#f7eddc",
    cursor: "pointer"
  },
  fieldHelper: {
    color: "rgba(247,237,220,0.62)",
    fontSize: 13,
    lineHeight: 1.4
  },
  passwordChecklist: {
    display: "grid",
    gap: 8,
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.03)"
  },
  passwordCheckRow: {
    display: "flex",
    alignItems: "center",
    gap: 10
  },
  passwordCheckPass: {
    minWidth: 42,
    textAlign: "center" as const,
    padding: "4px 8px",
    borderRadius: 999,
    background: "rgba(30,159,110,0.16)",
    color: "#93e4bf",
    fontSize: 12,
    fontWeight: 700
  },
  passwordCheckPending: {
    minWidth: 42,
    textAlign: "center" as const,
    padding: "4px 8px",
    borderRadius: 999,
    background: "rgba(242,180,59,0.12)",
    color: "#f2b43b",
    fontSize: 12,
    fontWeight: 700
  },
  primaryButton: {
    minHeight: 50,
    borderRadius: 999,
    border: "none",
    padding: "0 16px",
    cursor: "pointer",
    background: "linear-gradient(90deg, #f5b842, #e96f12)",
    color: "#fff8f2",
    fontWeight: 800,
    boxShadow: "0 14px 32px rgba(233,111,18,0.28)"
  },
  ghostButton: {
    minHeight: 48,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.11)",
    padding: "0 16px",
    cursor: "pointer",
    background: "rgba(255,255,255,0.06)",
    color: "#fff6e4",
    fontWeight: 800
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    marginBottom: 18
  },
  headerMobile: {
    alignItems: "flex-start",
    gap: 12
  },
  headerBrand: {
    display: "flex",
    gap: 14,
    alignItems: "center"
  },
  headerBrandMobile: {
    gap: 10,
    minWidth: 0
  },
  logo: {
    width: 64,
    height: 64,
    objectFit: "contain"
  },
  logoMobile: {
    width: 46,
    height: 46
  },
  headerTitle: {
    margin: 0,
    fontSize: "2rem"
  },
  headerTitleMobile: {
    fontSize: "clamp(26px, 8vw, 34px)",
    lineHeight: 1.05
  },
  eyebrowText: {
    margin: 0,
    color: "#f2b43b",
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontSize: "0.74rem"
  },
  statusBanner: {
    padding: "14px 16px",
    borderRadius: 18,
    marginBottom: 14
  },
  statusSuccess: {
    background: "rgba(30,159,110,0.12)",
    color: "#93e4bf"
  },
  statusError: {
    background: "rgba(203,81,81,0.14)",
    color: "#ffb3a8"
  },
  stack: {
    display: "grid",
    gap: 16
  },
  panel: {
    borderRadius: 28,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "linear-gradient(160deg, rgba(11,19,29,0.94), rgba(15,25,37,0.88))",
    padding: 20,
    boxShadow: "0 24px 70px rgba(0,0,0,0.38)"
  },
  heroPanel: {
    borderRadius: 28,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "linear-gradient(160deg, rgba(14,24,36,0.96), rgba(20,32,46,0.9))",
    padding: 22,
    boxShadow: "0 24px 70px rgba(0,0,0,0.38)"
  },
  badgePill: {
    display: "inline-flex",
    padding: "8px 12px",
    borderRadius: 999,
    border: "1px solid rgba(242,180,59,0.24)",
    background: "rgba(242,180,59,0.08)",
    color: "#f2b43b",
    fontSize: 12,
    marginBottom: 14
  },
  heroTitle: {
    margin: "0 0 10px",
    fontSize: "clamp(26px, 6vw, 40px)",
    lineHeight: 1.05,
    letterSpacing: "-0.035em"
  },
  heroCopy: {
    margin: "0 0 18px",
    color: "rgba(247,237,220,0.76)"
  },
  metricGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    marginTop: 12
  },
  metricCard: {
    padding: 16,
    borderRadius: 22,
    background: "rgba(255,255,255,0.045)",
    border: "1px solid rgba(255,255,255,0.11)",
    display: "grid",
    gap: 6
  },
  metricLabel: {
    color: "rgba(247,237,220,0.7)",
    fontSize: 13
  },
  metricValue: {
    fontSize: "1.3rem"
  },
  actionRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 18
  },
  sectionTitle: {
    margin: "2px 0 6px",
    fontSize: "1.5rem"
  },
  sectionCopy: {
    margin: 0,
    color: "rgba(247,237,220,0.72)"
  },
  detailList: {
    display: "grid",
    gap: 14
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "center"
  },
  helperText: {
    margin: 0,
    color: "rgba(247,237,220,0.74)"
  },
  frontDeskScript: {
    margin: 0,
    color: "#fff6e4",
    fontSize: "1.15rem",
    lineHeight: 1.45,
    fontWeight: 800
  },
  aiDisclosure: {
    color: "rgba(247,237,220,0.58)",
    fontSize: 13,
    textAlign: "center" as const,
    padding: "4px 12px"
  },
  confirmationCallout: {
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(242,180,59,0.18)",
    background: "rgba(242,180,59,0.08)",
    display: "grid",
    gap: 6
  },
  confirmationTitle: {
    color: "#f7eddc"
  },
  helperMuted: {
    margin: 0,
    color: "rgba(247,237,220,0.66)",
    fontSize: 14
  },
  consentCard: {
    display: "grid",
    gap: 10,
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.03)"
  },
  checkboxRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10
  },
  checkbox: {
    width: 18,
    height: 18,
    marginTop: 2,
    accentColor: "#f2b43b",
    flexShrink: 0
  },
  cardGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))"
  },
  walletCard: {
    padding: 16,
    borderRadius: 22,
    background: "rgba(255,255,255,0.045)",
    border: "1px solid rgba(255,255,255,0.11)",
    display: "grid",
    gap: 8
  },
  walletLabel: {
    color: "rgba(247,237,220,0.76)"
  },
  walletAmount: {
    fontSize: "1.2rem"
  },
  walletMeta: {
    color: "rgba(247,237,220,0.62)",
    fontSize: 13
  },
  walletRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "center",
    padding: 16,
    borderRadius: 20,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)"
  },
  activityCard: {
    padding: 16,
    borderRadius: 22,
    background: "rgba(255,255,255,0.045)",
    border: "1px solid rgba(255,255,255,0.11)",
    display: "grid",
    gap: 10
  },
  rowBetween: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center"
  },
  badge: {
    display: "inline-flex",
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(242,180,59,0.12)",
    color: "#f2b43b",
    fontSize: 12
  },
  activityMeta: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    color: "rgba(247,237,220,0.7)",
    fontSize: 14
  },
  link: {
    color: "#ffd579",
    textDecoration: "none"
  },
  emptyState: {
    padding: 18,
    borderRadius: 20,
    background: "rgba(255,255,255,0.03)",
    border: "1px dashed rgba(255,255,255,0.12)",
    display: "grid",
    gap: 8
  },
  emptyActionCard: {
    padding: 18,
    borderRadius: 20,
    background: "rgba(255,255,255,0.03)",
    border: "1px dashed rgba(255,255,255,0.12)",
    display: "grid",
    gap: 12
  },
  infoCard: {
    padding: 16,
    borderRadius: 20,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    display: "grid",
    gap: 8
  },
  qrCard: {
    padding: 18,
    borderRadius: 24,
    background: "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.03))",
    border: "1px solid rgba(255,255,255,0.08)",
    display: "grid",
    gap: 18
  },
  qrCanvasWrap: {
    width: "fit-content",
    margin: "0 auto",
    padding: 16,
    borderRadius: 24,
    background: "#fbf4e8"
  },
  manualCode: {
    margin: 0,
    fontSize: "1.8rem",
    letterSpacing: "0.2em",
    fontWeight: 800,
    color: "#ffd579"
  },
  bottomNav: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    gap: 8,
    overflowX: "auto",
    padding: "12px 16px calc(12px + env(safe-area-inset-bottom))",
    background: "rgba(5,11,15,0.92)",
    backdropFilter: "blur(18px)",
    borderTop: "1px solid rgba(255,255,255,0.11)",
    boxShadow: "0 -18px 44px rgba(0,0,0,0.35)",
    zIndex: 40
  },
  navItem: {
    flex: "1 0 86px",
    minHeight: 44,
    borderRadius: 999,
    border: "none",
    background: "transparent",
    color: "rgba(247,237,220,0.68)",
    cursor: "pointer"
  },
  navItemActive: {
    flex: "1 0 86px",
    minHeight: 44,
    borderRadius: 999,
    border: "none",
    background: "linear-gradient(135deg, rgba(245,184,66,0.2), rgba(233,111,18,0.14))",
    color: "#fff6e4",
    cursor: "pointer",
    fontWeight: 800
  },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12
  },
  pillToggle: {
    minHeight: 42,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.11)",
    padding: "0 14px",
    background: "rgba(255,255,255,0.05)",
    color: "rgba(247,237,220,0.74)",
    cursor: "pointer",
    fontWeight: 700
  },
  pillToggleActive: {
    minHeight: 42,
    borderRadius: 999,
    border: "1px solid rgba(242,180,59,0.26)",
    padding: "0 14px",
    background: "rgba(242,180,59,0.12)",
    color: "#ffd579",
    cursor: "pointer",
    fontWeight: 800
  },
  mapPlaceholder: {
    padding: 18,
    borderRadius: 22,
    border: "1px dashed rgba(242,180,59,0.28)",
    background: "radial-gradient(circle at top left, rgba(242,180,59,0.13), rgba(255,255,255,0.035))",
    marginBottom: 14,
    display: "grid",
    gap: 8
  },
  mapImage: {
    width: "100%",
    minHeight: 260,
    borderRadius: 22,
    objectFit: "cover",
    border: "1px solid rgba(255,255,255,0.12)",
    display: "block"
  },
  mapFrame: {
    width: "100%",
    minHeight: 300,
    borderRadius: 22,
    border: "1px solid rgba(255,255,255,0.12)",
    display: "block"
  },
  providerCard: {
    padding: 18,
    borderRadius: 24,
    background: "linear-gradient(150deg, rgba(255,255,255,0.055), rgba(255,255,255,0.028))",
    border: "1px solid rgba(255,255,255,0.1)",
    display: "grid",
    gap: 14
  },
  providerMetaGrid: {
    display: "grid",
    gap: 10
  },
  providerActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center"
  },
  secondaryLinkButton: {
    minHeight: 48,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.11)",
    padding: "0 16px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.06)",
    color: "#fff6e4",
    textDecoration: "none",
    fontWeight: 800
  }
};
